import type { TranslationObject } from '@ngx-translate/core';

export const TRANSLATIONS: Record<'en' | 'es' | 'fr', TranslationObject> = {
  en: {
    COMMON: {
      CANCEL: 'Cancel',
      CLOSE: 'Close',
      UNEXPECTED_ERROR_TITLE: 'Something went wrong',
      UNEXPECTED_ERROR_MESSAGE: 'An unexpected error occurred. Please try again.',
    },
    ADMIN: {
      SHELL: {
        HEADER: {
          TOGGLE_MENU: 'Toggle menu',
          ADMIN_CONSOLE: 'Admin Console',
          ONLINE: 'Online',
          ADMINISTRATOR: 'Administrator',
          TALOS_ADMIN: 'TALOS Admin',
          ADMIN_USER: 'Admin User',
          LANGUAGE: 'Language',
          LOG_OUT: 'Log out',
        },
        SIDEBAR: {
          TALOS_HOME: 'TALOS home',
          TAKEOFFS: 'takeoffs',
          CLOSE_MENU: 'Close menu',
          OPERATIONS: 'Operations',
          SYSTEM: 'System',
          DASHBOARD: 'Dashboard',
          PROJECTS: 'Projects',
          BUILDERS: 'Builders',
          SUBSCRIPTIONS: 'Subscriptions',
          SUPPORT: 'Support',
          SETTINGS: 'Settings',
          COPYRIGHT: '© 2026 TALOS · All rights reserved',
          EXPAND: 'Expand sidebar',
          COLLAPSE: 'Collapse sidebar',
        },
      },
      DASHBOARD: {
        HERO: {
          KICKER: 'Operations Center',
          TITLE_LINE_1: 'Welcome back,',
          TITLE_LINE_2: 'Administrator.',
          DESCRIPTION: 'Analyze floor plans, review extracted quantities, and keep estimate pipelines moving.',
          UPLOAD: 'Create Project',
          ALL_PROJECTS: 'All Projects →',
        },
        PULSE: {
          KICKER: 'System Pulse',
          PLANS_PROCESSED: 'Plans Processed',
          REPORTS_GENERATED: 'Takeoffs Generated',
          ACTIVE_PROJECTS: 'Active Projects',
        },
        STATS: {
          TOTAL_PLANS: 'Total Plans Processed',
          REPORTS_GENERATED: 'Takeoffs Generated',
          LAST_ACTIVITY: 'Last Activity',
          LAST_ACTIVITY_VALUE: '2 hrs ago',
          SAVED_PROJECTS: 'Saved Projects',
          BADGE_PLUS_12: '+12%',
          BADGE_PLUS_8: '+8%',
          BADGE_TODAY: 'Today',
          BADGE_ACTIVE: 'Active',
        },
        RECENT: {
          KICKER: 'Recent Queue',
          TITLE: 'Recent Projects',
          DESCRIPTION: 'Latest entries on the platform',
          VIEW_ALL: 'View All →',
        },
      },
      PROJECTS: {
        HERO: {
          KICKER: 'Project Index',
          TITLE: 'Projects',
          LINE_1: 'Search, manage, and open',
          LINE_2: 'every takeoff package.',
        },
        ACTIONS: {
          NEW_PROJECT: 'New Project',
          REFRESH: 'Refresh',
          TRY_AGAIN: 'Try again',
        },
        SEARCH: {
          PLACEHOLDER: 'Search by project name or owner...',
        },
        META: {
          PROJECTS: '{{count}} projects',
          PAGE: 'Page {{page}}',
        },
        ERROR: {
          LOAD_TITLE: 'Failed to load projects',
          LOAD_MESSAGE: 'Failed to load projects. Please try again.',
        },
        TABLE: {
          PROJECT_NAME: 'Project Name',
          OWNER: 'Owner',
          STATUS: 'Status',
          LAST_MODIFIED: 'Last Modified',
          TYPE: 'Type',
          ACTIONS: 'Actions',
          EMPTY_TITLE: 'No projects found',
          EMPTY_MESSAGE: 'Try adjusting your search or add a new project',
          VIEW: 'View',
          VIEW_PROJECT: 'View project',
          EDIT: 'Edit',
          EDIT_PROJECT: 'Edit project',
          DELETE: 'Delete',
          DELETE_PROJECT: 'Delete project',
        },
        STATUS: {
          ACTIVE: 'Active',
          IN_PROGRESS: 'In Progress',
          COMPLETED: 'Completed',
        },
        TYPE: {
          RESIDENTIAL: 'Residential',
          COMMERCIAL: 'Commercial',
          INDUSTRIAL: 'Industrial',
          OTHER: 'Other',
        },
        CONFIRM_DELETE: {
          TITLE: 'Delete Project',
          MESSAGE: 'Are you sure you want to delete this project?',
          CONFIRM: 'Delete',
        },
        MODAL: {
          VIEW_TITLE: 'View Project',
          EDIT_TITLE: 'Edit Project',
          CREATE_TITLE: 'Create New Project',
          PROJECT_NAME: 'Project Name *',
          PROJECT_NAME_PLACEHOLDER: 'Enter project name',
          PROJECT_NAME_ERROR: 'Project name is required.',
          PROJECT_NAME_HINT: 'Example: Palm Heights Tower A',
          PROJECT_TYPE: 'Project Type',
          PROJECT_TYPE_PLACEHOLDER: 'Select project type',
          PROJECT_TYPE_ERROR: 'Please select a project type.',
          PROJECT_TYPE_NAME: 'Custom Type Name',
          PROJECT_TYPE_NAME_PLACEHOLDER: 'e.g. Mixed-Use, Retail',
          SAVE: 'Save Changes',
          CREATE: 'Create Project',
        },
        TOAST: {
          UPDATE_SUCCESS_TITLE: 'Project updated',
          CREATE_SUCCESS_TITLE: 'Project created',
          UPDATE_SUCCESS_MESSAGE: 'Project updated successfully.',
          CREATE_SUCCESS_MESSAGE: 'Project created successfully.',
          UPDATE_FAILED_TITLE: 'Update failed',
          CREATE_FAILED_TITLE: 'Create failed',
          UPDATE_FAILED_MESSAGE: 'We could not update the project. Please try again.',
          CREATE_FAILED_MESSAGE: 'We could not create the project. Please try again.',
        },
      },
      SETTINGS: {
        UNKNOWN_SCOPE: 'Settings',
        HERO: {
          KICKER: 'System Config',
          TITLE: 'Settings',
          LINE_1: 'Configure material pricing, scope parameters,',
          LINE_2: 'and default values across all categories.',
        },
        ACTIVE_SCOPE: {
          KICKER: 'Active Scope',
          DESCRIPTION: 'Changes apply to this category only.',
        },
        ACTIONS: {
          SAVE_CHANGES: 'Save Changes',
        },
        PARAMETERS: {
          KICKER: 'Scope Parameters',
          TITLE: 'Default Values',
          DESCRIPTION: 'Used when no project override is set.',
          WALL_HEIGHT_FT: 'Default Wall Height',
        },
        PANELS: {
          FIXTURE_TYPES: 'Fixture Types',
          FIXTURES: 'Fixtures',
          FIXTURE: 'Fixture',
          FIXTURE_COUNT_ONE: '{{count}} fixture configured',
          FIXTURE_COUNT_MANY: '{{count}} fixtures configured',
          MATERIAL_TYPES: 'Material Types',
          MATERIAL_TYPES_TITLE: 'Material Types',
          MATERIALS: 'Materials',
          MATERIAL: 'Material',
          MATERIAL_COUNT_ONE: '{{count}} material configured',
          MATERIAL_COUNT_MANY: '{{count}} materials configured',
          UNIT: 'Unit',
          PRICE_PER_UNIT: 'Price / Unit',
          ACTIVE: 'Active',
          ADD_MATERIAL: 'Add Material',
          EDIT: 'Edit',
          EDIT_PRICE: 'Edit price',
          EDIT_MATERIAL: 'Edit material',
          DELETE: 'Delete',
          DELETE_MATERIAL: 'Delete material',
          SAVE: 'Save',
          NO_FIXTURES: 'No fixtures configured',
          FIXTURES_SYSTEM_MANAGED: 'Fixture types are managed by the system.',
          NO_MATERIALS: 'No materials configured',
          MATERIALS_SYSTEM_MANAGED: 'Material types are managed by the system.',
          ADD_FIRST_MATERIAL: 'Add your first material to get started.',
          DRYWALL_PRICE_DESCRIPTION: 'Default backend prices used for drywall estimates',
          NO_DRYWALL_PRICES: 'No drywall prices found.',
        },
        MODAL: {
          KICKER: 'Material Config',
          ADD_MATERIAL: 'Add Material',
          EDIT_MATERIAL: 'Edit Material',
          SAVE_MATERIAL: 'Save Material',
          SAVE_PRICE: 'Save Price',
          SAVE_FIXTURE: 'Save Fixture',
          EDIT_DRYWALL_PRICE: 'Edit Drywall Price',
          EDIT_FIXTURE_PRICE: 'Edit Fixture Price',
          EDIT_PLUMBING_MATERIAL_PRICE: 'Edit Plumbing Material Price',
          EDIT_FIXTURE: 'Edit Fixture',
          ADD_FIXTURE: 'Add Fixture',
          MATERIAL_NAME: 'Material Name *',
          MATERIAL_NAME_PLACEHOLDER: 'e.g. Hardwood, Tile',
          MATERIAL_NAME_ERROR: 'Material name is required.',
          UNIT: 'Unit *',
          UNIT_ERROR: 'Please select a unit.',
          PRICE_PER_UNIT: 'Price per Unit *',
          PRICE_ERROR: 'Please enter a valid price (0 or more).',
        },
        DOORS_WINDOWS: {
          MATERIALS_DESCRIPTION: 'Shared material varieties for doors and windows.',
          MATERIAL_NAME: 'Material Name',
          DOORS: 'Doors',
          DOORS_EMPTY: 'doors',
          DOORS_DESCRIPTION: 'Door types and default install rates.',
          WINDOWS: 'Windows',
          WINDOWS_EMPTY: 'windows',
          WINDOWS_DESCRIPTION: 'Window types and default install rates.',
          ADD_TYPE: 'Add Type',
          NEW_TYPE: 'New type',
          UNIT_AT_PRICE: '{{unit}} at ${{price}}',
          DELETE_TYPE: 'Delete type',
          TYPE_NAME: 'Type Name',
          EDIT_TYPE: 'Edit type',
          OPERATION: 'Operation',
          EDIT_OPERATION: 'Edit operation',
          BEST_USE: 'Best Use',
          EDIT_BEST_USE: 'Edit best use',
          NOT_SET: 'Not set',
          HANDING_OPTIONS: 'Handing Options',
          NO_ACTIVE_MATERIALS: 'No active materials available.',
          NO_TYPES: 'No {{type}} configured',
          ADD_FIRST_TYPE: 'Add the first type to get started.',
        },
        PAINT: {
          BRAND_LIBRARY: 'Paint Brand Library',
          BRAND_LIBRARY_DESCRIPTION: 'Define brands, their available colors, sheen, and price',
          REMOVE_BRAND: 'Remove brand',
          REMOVE_COLOR: 'Remove color',
          ADD_COLOR_PLACEHOLDER: 'Add color...',
          ADD: 'Add',
          ADD_BRAND: 'Add Brand',
          BRAND_NAME: 'Brand name',
          DEFAULT_SHEEN: 'Default sheen',
          PRICE_5_GAL: 'Price / 5 gal',
          MIN_PRICE: 'Minimum price is $5.00',
          SUPPLIES: 'Paint Supplies',
          SUPPLIES_DESCRIPTION: "Unit prices used in the report's Paint Supplies section",
          PAINTERS_TAPE: "Painter's Tape (per roll)",
          DROP_CLOTH: 'Plastic Drop Cloth (each)',
          ROLLER_KIT: 'Roller Kit (per kit)',
          SURFACE_ASSIGNMENTS: 'Surface Assignments',
          SURFACE_ASSIGNMENTS_DESCRIPTION: 'Assign brands to each surface, then choose color and sheen per assignment',
          NO_BRANDS_ASSIGNED: 'No brands assigned yet.',
          BRAND: 'Brand',
          COLOR: 'Color',
          SHEEN: 'Sheen',
          REMOVE_ASSIGNMENT: 'Remove assignment',
          CHOOSE_BRAND: 'Choose brand...',
          NO_COLORS: 'No colors configured',
          NO_SHEEN: 'No sheen configured',
        },
        TOAST: {
          LOAD_FAILED_TITLE: 'Unable to load settings',
          LOAD_DRYWALL_FAILED_TITLE: 'Unable to load drywall prices',
          LOAD_DOORS_WINDOWS_FAILED_TITLE: 'Unable to load doors & windows settings',
          LOAD_PLUMBING_FAILED_TITLE: 'Unable to load plumbing settings',
          LOAD_PAINT_FAILED_TITLE: 'Unable to load interior paint settings',
          TRY_AGAIN_MESSAGE: 'Please try again in a moment.',
          SAVED_TITLE: 'Settings saved',
          SAVE_FAILED_TITLE: 'Unable to save settings',
          SAVE_FAILED_MESSAGE: 'Please review your changes and try again.',
          WALL_HEIGHT_MESSAGE: 'Wall height must be at least 6 ft.',
          PRICING_SAVED_MESSAGE: 'Pricing configuration has been updated.',
          PLUMBING_SAVED_MESSAGE: 'Plumbing settings have been updated.',
          PAINT_SAVED_MESSAGE: 'Interior paint settings have been updated.',
          DRYWALL_SAVED_MESSAGE: 'Drywall settings have been updated.',
          DOORS_WINDOWS_SAVED_MESSAGE: 'Doors & Windows settings have been updated.',
        },
      },
      SUBSCRIPTIONS: {
        HERO: {
          KICKER: 'Billing',
          TITLE: 'Subscription Plans',
          LINE_1: 'View and manage all subscription tiers',
          LINE_2: 'available on the platform.',
        },
        ACTIONS: {
          ADD_PLAN: 'Add Plan',
          EDIT_PLAN: 'Edit plan',
        },
        STATS: {
          TOTAL: 'Total',
          PLANS: 'Plans',
          ACTIVE: 'Active',
          RUNNING: 'Running',
          INACTIVE: 'Inactive',
          PAUSED: 'Paused',
        },
        BILLING: {
          MONTHLY: 'Monthly',
          YEARLY: 'Yearly',
        },
        STATUS: {
          ACTIVE: 'Active',
          INACTIVE: 'Inactive',
        },
        PLANS: {
          MONTHLY_LIMITED: 'Monthly Limited Plan',
          MONTHLY_PREMIUM: 'Monthly Premium Plan',
          YEARLY_LIMITED: 'Yearly Limited Plan',
          YEARLY_PREMIUM: 'Yearly Premium Plan',
        },
        PRICE: {
          PER_YEAR: '/year',
          PER_MONTH: '≈ {{amount}} per month',
        },
        FEATURES: {
          ALL_CATEGORIES: 'All categories included',
          ONE_CATEGORY: '{{count}} category included',
          MANY_CATEGORIES: '{{count}} categories included',
          SUPPORT: '{{support}} support',
        },
        SUPPORT: {
          PRIORITY: 'Priority',
        },
        MODAL: {
          KICKER: 'Plan Config',
          TITLE: 'Edit Subscription Plan',
          CLOSE: 'Close',
          PLAN_NAME: 'Plan Name *',
          PLAN_NAME_PLACEHOLDER: 'Enter plan name',
          PLAN_NAME_ERROR: 'Plan name is required.',
          DESCRIPTION: 'Description *',
          DESCRIPTION_PLACEHOLDER: 'Short and clear description for this plan.',
          DESCRIPTION_ERROR: 'Description is required.',
          DESCRIPTION_HINT: 'Short and clear description for this plan.',
          ANNUAL_FEE: 'Annual Fee *',
          ANNUAL_FEE_ERROR: 'Please enter a valid annual fee.',
          CANCEL: 'Cancel',
          SAVE: 'Save Changes',
        },
      },
      BUILDERS: {
        HERO: {
          KICKER: 'PLATFORM',
          TITLE: 'All Builders',
          TOTAL_BADGE: '{{count}} total',
          LINE_1: 'Manage all registered builders',
          LINE_2: 'and their subscription access.',
        },
        STATS: {
          TOTAL_KICKER: 'TOTAL',
          REGISTERED: 'Registered',
          PAID_KICKER: 'PAID',
          PRO_ENTERPRISE: 'Pro & Enterprise',
          FREE_KICKER: 'FREE',
          TRIAL: 'Trial Access',
        },
        TABLE: {
          KICKER: 'BUILDER REGISTRY',
          HEADING: 'All Builders',
          SHOWING: 'Showing {{shown}} of {{total}} builders',
          PAGE: '· Page {{page}}',
          SEARCH_PH: 'Search name or email...',
        },
        EMPTY: {
          TITLE: 'No builders found',
          SUBTITLE: 'Try adjusting your search query.',
        },
        COL: {
          BUILDER: 'Builder',
          EMAIL: 'Email',
          SUBSCRIPTION: 'Subscription',
          JOINED: 'Joined',
        },
        ACTION: {
          VIEW: 'View',
          DELETE: 'Delete',
        },
        MODAL: {
          TITLE: 'Remove Builder',
          MESSAGE: 'Are you sure you want to remove this builder from the platform?',
          CONFIRM: 'Remove',
          CANCEL: 'Cancel',
        },
      },
      SUPPORT: {
        HERO: {
          KICKER: 'HELP CENTER',
          TITLE: 'How can we help?',
          LINE_1: 'Browse our FAQ, read our policies, or reach out directly.',
          LINE_2: 'Our team is here to make your TALOS experience seamless.',
        },
        CONTACT: {
          EMAIL_TITLE: 'Email Support',
          EMAIL_SUBTITLE: 'support@talos.ai',
          EMAIL_ACTION: 'Send a message',
          PHONE_TITLE: 'Phone Support',
          PHONE_SUBTITLE: '+1 (800) 555-0199',
          PHONE_ACTION: 'Call us',
          TERMS_TITLE: 'Terms & Conditions',
          TERMS_SUBTITLE: 'Platform usage policies',
          TERMS_ACTION: 'Read terms',
          PRIVACY_TITLE: 'Privacy Policy',
          PRIVACY_SUBTITLE: 'Data handling & rights',
          PRIVACY_ACTION: 'Read policy',
        },
        FAQ: {
          KICKER: 'FAQ',
          TITLE: 'Frequently Asked Questions',
          COUNT: '{{count}} questions',
          Q1: 'How do I add a new builder to the platform?',
          A1: 'Navigate to Builder Management from the sidebar. Use the invite or add builder option to register a new builder account. They will receive an email with login credentials.',
          Q2: 'How do subscription plans get assigned to builders?',
          A2: 'Subscription plans are selected by builders during their onboarding flow. As an admin, you can manage available plans from the Subscription Management page.',
          Q3: 'What happens when I delete a project?',
          A3: 'Deleting a project is permanent. All associated floor plans, reports, and AI-generated documents will be removed. Always confirm before proceeding.',
          Q4: 'Can I edit a subscription plan price after it has been published?',
          A4: 'Yes — use the edit (pencil) button on any plan card in the Subscription Management page. Changes apply to new subscriptions only; existing subscribers are not affected.',
          Q5: 'How do I contact technical support?',
          A5: 'You can reach the technical team via email at support@talos.ai or call our helpline during business hours. Response times are typically within 24 hours.',
        },
        FOOTER: 'Support hours: Mon – Fri, 9 AM – 6 PM IST · Avg. response time: < 24 hours',
      },
    },
    REPORTS: {
      NAV: {
        REPORTS: 'Reports',
        AI_ESTIMATE: 'AI Estimate',
        EXPORT_WORD: 'Export Word',
        EXPORT_PDF: 'Export PDF',
        EXPORT_EXCEL: 'Export Excel',
      },
      CATEGORIES: {
        FLOORING: 'Flooring',
        DRYWALL: 'Drywall',
        INTERIOR_PAINT: 'Interior Paint',
        FIXTURES: 'Fixtures',
        FIXTURES_PLUMBING: 'Fixtures (Plumbing)',
        DOORS_WINDOWS: 'Doors & Windows',
      },
      TITLES: {
        FLOORING_REPORT: 'Flooring Report',
        DRYWALL_REPORT: 'Drywall Report',
        INTERIOR_PAINT_REPORT: 'Interior Paint Report',
        FIXTURES_REPORT: 'Fixtures (Plumbing) Report',
        DOORS_WINDOWS_REPORT: 'Doors & Windows Report',
      },
      LABELS: {
        AI_GENERATED: 'AI-generated estimate',
        TOTAL_COST: 'Total Cost',
        GRAND_TOTAL: 'Grand Total',
        PROJECT_ESTIMATE: 'Project Estimate',
        GENERATED: 'Generated',
        ESTIMATED_MATERIALS: 'Estimated materials',
        FLOORS: 'Floors',
        FLOOR: 'Floor',
        LEVELS_ANALYZED: 'Level(s) analyzed',
        TOTAL_AREA: 'Total Area',
        COMBINED_FLOOR_AREA: 'Combined floor area',
        ROOMS: 'Rooms',
        ACROSS_ALL_FLOORS: 'Across all floors',
        COST_BY_FLOOR: 'Cost by Floor',
        MATERIALS_DISTRIBUTION: 'Materials distribution across levels',
        FLOOR_DISTRIBUTION: 'Floor Distribution',
        COST_SHARE_PER_FLOOR: 'Cost share per floor',
        ROOM_MATERIALS: 'Room Materials',
        MATERIALS_BREAKDOWN: 'Materials Breakdown',
        ITEM: 'Item',
        NAME: 'Name',
        LABEL: 'Label',
        ROOM: 'Room',
        QTY: 'Qty',
        QUANTITY: 'Quantity',
        UNIT: 'Unit',
        UNIT_PRICE: 'Unit Price',
        TOTAL: 'Total',
        COST: 'Cost',
        VALUE: 'Value',
        DIMENSIONS: 'Dimensions',
        TYPE: 'Type',
        HANDING: 'Handing',
        MATERIAL: 'Material',
        FLOOR_SUBTOTAL: 'Floor Subtotal',
        PROJECT_TOTAL: 'Project total',
        WALL_AREA: 'Wall Area',
        CEILING_AREA: 'Ceiling Area',
        CORNERS: 'Corners',
        INTERIOR_WALL_SURFACE: 'Interior wall surface',
        TOTAL_CEILING_SURFACE: 'Total ceiling surface',
        TOTAL_CORNER_POINTS: 'Total corner points',
        MATERIAL_COST_BREAKDOWN: 'Material Cost Breakdown',
        SURFACE_AREA_SPLIT: 'Surface Area Split',
        BOARD_TYPE: 'Board Type',
        ALL_MATERIALS: 'All Materials',
        COMPLETE_MATERIALS_SCHEDULE: 'Complete materials schedule',
        TOTAL_MATERIALS: 'Total Materials',
        COST_SPLIT: 'Cost Split',
        PAINT_AREAS: 'Paint Areas',
        PRIMER_APPLICATION: 'Primer Application',
        PAINT_APPLICATION: 'Paint Application',
        PAINT_SUPPLIES: 'Paint Supplies',
        PRIMER: 'Primer',
        PAINT: 'Paint',
        SUPPLIES: 'Supplies',
        SINGLE_COAT: 'Single coat',
        SINGLE_COAT_APPLICATION: 'Single coat application',
        TWO_COATS: 'Two coats',
        TWO_COAT_APPLICATION: 'Two-coat application',
        PRIMER_PAINT_SUPPLIES: 'Primer + Paint + Supplies',
        BRUSHES_TAPE_TOOLS: 'Brushes, tape & tools',
        APPLICATION: 'Application',
        SQ_FT: 'Sq Ft',
        GALLONS: 'Gallons',
        GAL: 'Gal',
        BRAND: 'Brand',
        COLOR: 'Color',
        SHEEN: 'Sheen',
        TOTAL_PRIMER_COST: 'Total Primer Cost',
        OVERALL_PAINT_COST: 'Overall Paint Cost',
        TOTAL_PAINT_SUPPLIES: 'Total Paint Supplies',
        PEX_PIPING: 'PEX & Piping',
        PEX_TUBING: 'PEX Tubing',
        PEX_CONNECTORS: 'PEX Connectors',
        DRAINAGE: 'Drainage',
        OTHER: 'Other',
        ALL_PLUMBING_COMBINED: 'All plumbing combined',
        PER_ROOM_FIXTURE_COST: 'Per-room fixture cost',
        TUBING_CONNECTORS: 'Tubing & connectors',
        DRAINAGE_EXAMPLES: 'P-traps, flanges, wyes',
        COST_BREAKDOWN: 'Cost Breakdown',
        SHARE_BY_PLUMBING_CATEGORY: 'Share by plumbing category',
        SHARE_BY_CATEGORY: 'Share by category',
        TOP_FIXTURES_BY_COST: 'Top Fixtures by Cost',
        PER_FIXTURE_COST_DISTRIBUTION: 'Per-fixture cost distribution',
        FIXTURES_SUBTOTAL: 'Fixtures Subtotal',
        DOORS: 'Doors',
        WINDOWS: 'Windows',
        INSTALLATION: 'Installation',
        MATERIALS_INSTALLATION: 'Materials + Installation',
        LABOR_MATERIALS: 'Labor & materials',
        DOOR_ITEMS_BY_COST: 'Door Items by Cost',
        COST_PER_DETECTED_DOOR: 'Cost per detected door',
        DOORS_SUBTOTAL: 'Doors Subtotal',
        WINDOWS_SUBTOTAL: 'Windows Subtotal',
        INSTALLATION_MATERIALS: 'Installation Materials',
        DOOR_INSTALLATION: 'Door Installation',
        WINDOW_INSTALLATION: 'Window Installation',
        INSTALLATION_SUBTOTAL: 'Installation Subtotal',
        TOTAL_ESTIMATED_PROJECT_COST: 'Total Estimated Project Cost',
        POWERED_BY_TALOS: 'Powered by TALOS',
        ALL_RIGHTS_RESERVED: 'All rights reserved.',
        ITEM_COUNT: '{{count}} item(s)',
        ITEMS_DETECTED: '{{count}} item(s) detected',
        FLOOR_COUNT: '{{count}} floor(s)',
        ROOM_COUNT: '{{count}} room(s)',
        AREAS_COUNT: '{{count}} areas',
        GAL_TOTAL: '{{count}} gal total',
        OF_TOTAL: '{{percent}}% of total',
        OF_TOTAL_SURFACE: '{{percent}}% of total surface',
      },
      EMPTY: {
        NO_ROOMS: 'No rooms assigned',
        NO_MATERIALS: 'No materials assigned',
        NO_FIXTURE_ROWS: 'No fixture rows',
        NO_FIXTURE_ROWS_AVAILABLE: 'No fixture rows available',
        NO_DOOR_ROWS_AVAILABLE: 'No door rows available',
        NO_REPORT_DATA: 'No report data available.',
        GO_BACK: 'Go back',
      },
      DISCLAIMER: 'This estimate is AI-generated based on provided floor plan data and regional material pricing. Actual costs may vary based on local market conditions, contractor rates, and final project specifications. Figures are intended as a planning guide only and should be verified by a licensed contractor.',
      SHORT_DISCLAIMER: 'All measurements and cost estimates are approximate, generated using AI tools. Please verify all figures before execution.',
    },
    PROJECT_STEPPER: {
      WORKFLOW: 'Project Workflow',
      STEPS: {
        DETECTION_SCOPE: 'Detection Scope',
        UPLOAD_PLAN: 'Upload Plan',
        ASSIGN_FLOORS: 'Assign Floors',
        RESULTS: 'Results',
      },
      HEADER: {
        STEP: 'Step',
        STEP_OF: 'STEP {{step}} OF {{total}}',
      },
      ARIA: {
        DIALOG: 'Step {{step}} of {{total}}: {{label}} - {{project}}',
        STEP_LIVE: 'Step {{step}} of {{total}}: {{label}}',
        WORKFLOW_STEPS: 'Workflow steps',
        COMPLETE: 'complete',
        CURRENT: 'current step',
        UPCOMING: 'upcoming',
      },
      LOADING: {
        PROJECT_TITLE: 'Loading project',
        PROJECT_MESSAGE: 'Checking for existing results...',
        FLOOR_VIEWER_TITLE: 'Loading floor plan viewer',
        FLOOR_VIEWER_MESSAGE: 'Preparing the PDF assignment workspace.',
        RESULTS_TITLE: 'Loading results',
        RESULTS_MESSAGE: 'Preparing the detection results view for this project.',
      },
      NAV: {
        PREVIOUS: 'Previous step',
        RESTART_TOUR: 'Show tour again',
        CONTINUE: 'Continue',
        CONTINUE_UPLOAD: 'Continue to Upload',
        CONTINUE_ASSIGN: 'Continue to Assign Floors',
        CONTINUE_RESULTS: 'Continue to Results',
        FINISH: 'Finish',
        SAVING_PRICING: 'Saving pricing...',
        SELECT_CATEGORIES_TOOLTIP: 'Select 1-{{max}} categories to continue',
        UPLOAD_TOOLTIP: 'Upload a floor plan to continue',
        ASSIGN_AND_SCALE_TOOLTIP: 'Assign at least one page and select a scale to continue',
        ASSIGN_TOOLTIP: 'Assign at least one page to continue',
        SCALE_TOOLTIP: 'Select a scale to continue',
      },
      CONFIRM: {
        CLOSE_DIRTY: 'You have unsaved floor plan edits. Close and discard them?',
        BACK_DIRTY: 'You have unsaved edits. Go back and discard them?',
        SCALE_TITLE: 'Scale not assigned',
        SCALE_MESSAGE: 'You changed the scale but did not click Assign. The new scale will not be saved.',
        CONTINUE_ANYWAY: 'Continue anyway',
        GO_BACK: 'Go back',
      },
      TOAST: {
        SAVE_FAILED_TITLE: 'Save failed',
        SAVE_SCOPE_FAILED_MESSAGE: 'Could not save the detection scope.',
        PRICING_SAVE_FAILED_TITLE: 'Pricing save failed',
        PRICING_SAVE_FAILED_MESSAGE: 'Could not save material pricing. Please try again.',
      },
      ERRORS: {
        MAX_CATEGORIES: 'Select up to {{max}} detection categories.',
        LOAD_SCOPE: 'Failed to load detection scope.',
        SAVE_SCOPE: 'Failed to save detection scope.',
        NO_PROJECT: 'No project selected.',
        NO_PROJECT_RESTART: 'No project selected. Please restart the flow.',
        TOO_MANY_CATEGORIES: 'Too many detection categories selected.',
      },
      CATEGORIES: {
        FLOORING: { LABEL: 'Flooring', DESCRIPTION: 'Detect flooring areas and room polygons for quantity takeoff.' },
        DRYWALL: { LABEL: 'Drywall', DESCRIPTION: 'Measure walls and room perimeters for drywall estimates.' },
        FIXTURES: { LABEL: 'Plumbing Fixtures', DESCRIPTION: 'Identify sinks, toilets, tubs, and related plumbing fixtures.' },
        DOORS_WINDOWS: { LABEL: 'Doors & Windows', DESCRIPTION: 'Detect door and window openings across assigned floor plans.' },
        INTERIOR_PAINT: { LABEL: 'Interior Paint', DESCRIPTION: 'Estimate interior paint surfaces by room and wall area.' },
        HVAC: { LABEL: 'HVAC', DESCRIPTION: 'Measure duct runs and HVAC elements from plan drawings.' },
        COLUMNS: { LABEL: 'Columns', DESCRIPTION: 'Detect structural columns and their measured dimensions.' },
        STEEL_BEAMS: { LABEL: 'Steel Beams', DESCRIPTION: 'Detect steel beams and grouped beam measurements.' },
      },
      STEP1: {
        TITLE: 'Select Detection Scope.',
        DESCRIPTION: 'Choose up to two architectural elements for the AI to detect across your floor plans.',
        LOADING_TITLE: 'Loading detection scope',
        LOADING_MESSAGE: 'Fetching the available detection categories for this project.',
        SELECT_UP_TO: 'Select up to {{max}} categories.',
      },
      STEP2: {
        TITLE: 'Upload Floor Plan.',
        DESCRIPTION: 'Upload your floor plan PDF - the AI will analyze each page you assign.',
        CHECKING_TITLE: 'Checking existing uploads',
        CHECKING_MESSAGE: 'Looking for floor plans already attached to this project.',
        UPLOADED_DOCUMENTS: 'Uploaded documents',
        FILE_COUNT_ONE: '{{count}} file',
        FILE_COUNT_MANY: '{{count}} files',
        UPLOADED_AT: 'Uploaded {{date}}',
        DELETE_DOCUMENT: 'Delete document',
        UPLOAD_NEW_VERSION: 'Upload a new version',
        DROPZONE_TITLE: 'Drag and drop your floor plan here',
        BROWSE_HINT: 'or click to browse files',
        BROWSE_FILES: 'Browse Files',
        PDF_ONLY: 'PDF only',
        UPLOADING: 'Uploading...',
        UPLOADED_SUCCESS: 'Uploaded successfully - you can continue',
        CHANGE_FILE: 'Change file',
        ERRORS: {
          UNSUPPORTED_TYPE: 'Unsupported file type. Please upload a PDF file.',
          INVALID_PDF: 'This file doesn\'t look like a valid PDF. Please check the file and try again.',
          FILE_TOO_LARGE: 'File is too large. Please upload a PDF under {{maxSizeMb}} MB.',
          UPLOAD_FAILED: 'Upload failed. Please try again.',
        },
        TOAST: {
          DELETE_SUCCESS_TITLE: 'Document deleted',
          DELETE_SUCCESS_MESSAGE: 'The floor plan has been removed.',
          DELETE_FAILED_TITLE: 'Delete failed',
          DELETE_FAILED_MESSAGE: 'Could not delete the document. Please try again.',
          UPLOAD_SUCCESS_TITLE: 'Upload successful',
          UPLOAD_SUCCESS_MESSAGE: 'Floor plan uploaded successfully.',
          UPLOAD_FAILED_TITLE: 'Upload failed',
          UPLOAD_FAILED_MESSAGE: 'Could not upload the file. Please try again.',
        },
      },
      STEP3: {
        EDIT_PAGE_TITLE: 'Click to edit page number',
        FIT: 'Fit',
        FIT_TITLE: 'Fit page to screen',
        CRITICAL_TITLE: 'Critical Instruction',
        CRITICAL_MESSAGE_PREFIX: 'Select only pages that contain',
        ONE_FLOOR_PLAN: 'ONE floor plan',
        CRITICAL_MESSAGE_SUFFIX: 'Avoid pages with multiple layouts, sections, or combined drawings.',
        DISMISS_NOTICE: 'Dismiss instruction notice',
        NO_PDF: 'No PDF loaded. Please upload a floor plan in the previous step.',
        CAL_FIRST_POINT: 'Click the first point on a known dimension.',
        CAL_SECOND_POINT: 'Click the second point to define the scale.',
        SCALE_UPDATED: 'Scale updated successfully.',
        CALIBRATE_SCALE: 'Calibrate Scale',
        PAGE_LABEL: 'Page {{page}}',
        DRAWN_LINE: 'Drawn Line',
        PDF_PT: 'PDF pt',
        EQUALS_REAL_WORLD: 'equals in real world',
        FEET_LABEL: "Feet (ft') *",
        INCHES_LABEL: 'Inches (in")',
        SCALE: 'Scale',
        RECALIBRATE: 'Recalibrate',
        APPLY_SCALE: 'Apply Scale',
        ENTER_REAL_LENGTH: 'Enter real length',
        NOT_ASSIGNED: 'Not Assigned',
        ASSIGNMENT_PANEL: 'ASSIGNMENT PANEL',
        FLOOR_ASSIGNMENT: 'Floor Assignment',
        ASSIGN_EACH_PAGE: 'Assign each page to a floor level',
        ASSIGNED: 'Assigned',
        UNASSIGNED: 'Unassigned',
        ASSIGN_FLOOR: 'Assign Floor',
        STANDARD: 'Standard',
        CALIBRATE: 'Calibrate',
        LOADING_SCALES: 'Loading scales...',
        SELECT_SCALE: 'Select scale...',
        NO_SCALES: 'No scales available',
        REDO_CALIBRATION: 'Redo calibration',
        CLEAR_CALIBRATED_SCALE: 'Clear calibrated scale',
        SCALE_REQUIRED: 'Select or calibrate a scale before assigning.',
        FLOOR: 'Floor',
        SELECT_FLOOR: 'Select floor...',
        ALL_FLOORS_ASSIGNED: 'All floors are already assigned',
        ASSIGN: 'Assign',
        ASSIGN_NEXT: 'Assign & Next',
        ASSIGNED_FLOORS: 'ASSIGNED FLOORS',
        NO_FLOORS_ASSIGNED: 'No floors assigned yet.',
        SELECT_AND_ASSIGN: 'Select a floor and click Assign.',
        PAGE_SHORT: 'pg',
        CHANGE_FLOOR: 'Change floor...',
        VIEWING_NOW: 'Viewing now',
        EDIT_FLOOR: 'Edit floor',
        REMOVE: 'Remove',
        TOAST: {
          INVALID_SCALE_TITLE: 'Invalid scale',
          INVALID_SCALE_MESSAGE: 'Enter the real-world length for the measured line.',
          SUBMISSION_FAILED_TITLE: 'Submission failed',
          SUBMISSION_FAILED_MESSAGE: 'Could not submit this page. Please try again.',
          DELETE_FAILED_TITLE: 'Delete failed',
          DELETE_FAILED_MESSAGE: 'Could not remove this assignment. Please try again.',
          UPDATE_FAILED_TITLE: 'Update failed',
          UPDATE_FAILED_MESSAGE: 'Could not update this assignment. Please try again.',
        },
      },
      STEP4: {
        ARIA: {
          OVERLAY_LABEL: 'Interactive floor plan for {{category}} with {{count}} detected items. Press Tab to move between items, Enter or Space to select.',
        },
        FIT: 'Fit',
        UPDATING_ZOOM: 'Updating zoom',
        LOADING_FLOOR_PLAN: 'Loading floor plan',
        RENDERING_VIEW: 'Rendering {{zoom}} view',
        PLEASE_WAIT: 'Please wait',
        REGENERATING_COORDINATES: 'Regenerating coordinates',
        REGENERATE_DETECTION: 'Regenerate detection',
        REGENERATING: 'Regenerating...',
        REGENERATE: 'Regenerate',
        MATERIALS_PRICING: 'Materials & Pricing',
        PRICING: 'Pricing',
        VIEW_HTML_REPORT: 'View HTML report',
        VIEW: 'View',
        AI_DETECTION_RUNNING: 'AI DETECTION RUNNING',
        ANALYZING_TITLE: 'Analyzing Floor Plans',
        ANALYZING_MESSAGE: 'Results will appear as each detection category completes. You can monitor progress below.',
        DONE: 'Done',
        FAILED: 'Failed',
        DETECTING: 'Detecting {{category}}...',
        PERCENT_COMPLETE: '{{percent}}% complete',
        DETECTION_FAILED: 'Detection failed',
        NO_ELEMENTS: 'No elements detected',
        NO_ELEMENTS_MESSAGE: 'The AI did not find any {{category}} elements.',
        NO_FLOOR_PLAN: 'No floor plan available.',
        DOOR: 'door',
        WINDOW: 'window',
        DRAG_DRAW_DW: 'Drag to draw a {{type}} rectangle',
        ZOOM_OUT: 'Zoom out',
        ZOOM_IN: 'Zoom in',
        FIT_TO_SCREEN: 'Fit to screen',
        DRAG_RECTANGLE: 'Drag to draw a rectangle',
        DRAG_ELLIPSE: 'Drag to draw an ellipse',
        CLICK_FIRST_VERTEX: 'Click to place first vertex',
        POINTS_CLOSE_HINT: 'points - double-click or click the first point to close',
        SELECTED: 'Selected: {{name}}',
        EDIT_POLYGON_POINTS: 'Edit polygon points',
        EDIT: 'Edit',
        DELETE_THIS_ITEM: 'Delete this item',
        DELETE: 'Delete',
        DESELECT: 'Deselect',
        WIDTH: 'Width',
        THICKNESS: 'Thickness',
        NO_SPEC_DATA: 'No specification data available.',
        SELECT_ITEM: 'Select an item',
        SELECT_ITEM_MESSAGE: 'Click a door or window on the floor plan to view its details.',
        BEAM: 'Beam',
        COLUMN: 'Column',
        GROUPING: 'Grouping',
        ERRORS: {
          REGENERATE_FAILED: 'Failed to regenerate coordinates. Please try again.',
          COMING_SOON: '{{category}} is coming soon.',
          PROCESS_FAILED: 'Failed to process floor plans. Please try again.',
          PROCESSING_ERROR: 'An error occurred during AI processing.',
        },
        POINTS_NEED_MORE: '{{count}} {{plural}} — need {{more}} more',
        DW_PANEL: {
          COMPLETED: 'Completed',
          ITEMS_DETECTED: '{{count}} items detected',
          SUMMARY: 'Summary',
          DOORS: 'Doors',
          WINDOWS: 'Windows',
          FLOORS: 'Floors',
          EDITING: 'Editing:',
          DRAWING_NEW_DOOR: 'Drawing New Door',
          DRAWING_NEW_WINDOW: 'Drawing New Window',
          NAME_YOUR_DOOR: 'Name Your Door',
          NAME_YOUR_WINDOW: 'Name Your Window',
          DRAG_DRAW_RECT: 'Drag on the floor plan to draw a rectangle.',
          DOOR_NAME: 'Door Name',
          WINDOW_NAME: 'Window Name',
          CANCEL: 'Cancel',
          SAVE_DOOR: 'Save Door',
          SAVE_WINDOW: 'Save Window',
          ADD_DOOR: 'Add Door',
          ADD_WINDOW: 'Add Window',
          DETECTION: 'Detection',
          CHANGE_COLOR_HINT: 'Click the swatch to change the detection overlay color',
          COLOR_TIP: 'Tip: click here to choose a color',
        },
        EDIT_TOOLBAR: {
          TITLE: 'Edit Points',
          UNSAVED: 'Unsaved changes',
          UNDO: 'Undo',
          UNDO_TITLE: 'Undo (Ctrl+Z)',
          REDO: 'Redo',
          REDO_TITLE: 'Redo (Ctrl+Y)',
          CANCEL: 'Cancel',
          CANCEL_TITLE: 'Cancel (Esc)',
          DELETE: 'Delete',
          DELETE_ROOM: 'Delete this room',
          SAVE: 'Save',
          SAVING: 'Saving…',
          SQ_FT: 'sq ft',
          PTS: 'pts',
          SELF_CROSSING: 'Self-crossing',
          SHORTCUTS_TITLE: 'Keyboard shortcuts',
          EDIT_SHORTCUTS: 'Edit Shortcuts',
          DRAG_VERTEX: 'Drag vertex',
          MOVE_CORNER: 'Move corner',
          CLICK_ON_EDGE: 'Click ○ on edge',
          ADD_VERTEX: 'Add vertex',
          RIGHT_CLICK_VERTEX: 'Right-click vertex',
          DELETE_VERTEX: 'Delete vertex',
          UNDO_KEY: 'Undo',
          REDO_KEY: 'Redo',
          CANCEL_KEY: 'Cancel (confirms)',
          DRAG_HINT: 'Drag · Click ○ to add · Right-click to delete',
        },
        ROOM_DETAIL: {
          FLOORING_DETECTION: 'Flooring Detection',
          DRYWALL_DETECTION: 'Drywall Detection',
          PAINT_DETECTION: 'Paint Detection',
          PLUMBING_DETECTION: 'Plumbing Detection',
          HVAC_DETECTION: 'HVAC Detection',
          COLUMN_DETECTION: 'Column Detection',
          BEAM_DETECTION: 'Beam Detection',
          DETECTION_DETAILS: 'Detection Details',
          COMPLETED: 'Completed',
          PROCESSING: 'Processing…',
          FAILED: 'Failed',
          SUMMARY: 'Summary',
          ROOMS: 'Rooms',
          FIXTURES: 'Fixtures',
          DUCTS: 'Ducts',
          COLUMNS: 'Columns',
          BEAMS: 'Beams',
          FLOORS: 'Floors',
          TOTAL_LENGTH: 'Total Length',
          TOTAL_FLOOR_AREA: 'Total Floor Area',
          TOTAL_CEILING_AREA: 'Total Ceiling Area',
          TOTAL_FIXTURES: 'Total Fixtures',
          TOTAL_AREA: 'Total Area',
          TOTAL_PERIMETER: 'Total Perimeter',
          TOTAL_WALL_PERIMETER: 'Total Wall Perimeter',
          TOTAL_WALL_LENGTH: 'Total Wall Length',
          FIXTURE_COUNT: 'Fixture Count',
          TOTAL_WALL_AREA: 'Total Interior Wall Area',
          TOTAL_CORNERS: 'Total Corner Count',
          ADD_ROOM: 'Add New Room',
          ADD_FIXTURE: 'Add New Fixture',
          ADD_DUCT: 'Add New Duct',
          ADD_COLUMN: 'Add New Column',
          ADD_BEAM: 'Add New Beam',
          DRAWING_NEW_FIXTURE: 'Drawing New Fixture',
          NAME_YOUR_FIXTURE: 'Name Your Fixture',
          PIPE_READY: 'Pipe Ready — Save to confirm',
          ADD_NEW_PIPE: 'Add New Pipe',
          NAME_YOUR_ROOM: 'Name Your Room',
          DRAWING_NEW_ROOM: 'Drawing New Room',
          CANCEL_ESC: 'Cancel (Esc)',
          PIPE_THICKNESS: 'Pipe Thickness',
          THIN: 'Thin',
          MEDIUM: 'Medium',
          THICK: 'Thick',
          WIDE: 'Wide',
          PIPE_START: 'Click the start of the pipe on the floor plan.',
          PIPE_END: 'Click the end of the pipe to finish.',
          POLYGON: 'Polygon',
          RECT: 'Rect',
          ELLIPSE: 'Ellipse',
          DRAW_POLYGON_TITLE: 'Draw polygon — click to place points',
          DRAW_RECT_TITLE: 'Draw rectangle — drag to draw',
          DRAW_ELLIPSE_TITLE: 'Draw ellipse — drag to draw',
          POLYGON_HINT1: 'Click the floor plan to place vertices.',
          POLYGON_HINT2: 'Double-click or click the first point ◎ to close.',
          VERTICES_PLACED: 'Vertices placed',
          DRAG_RECT: 'Drag on the floor plan to draw a rectangle.',
          DRAG_ELLIPSE: 'Drag on the floor plan to draw an ellipse.',
          ASSOCIATED_ROOM: 'Associated Room',
          NOT_DETECTED: 'Not detected yet',
          PIPE_NAME: 'Pipe Name',
          FIXTURE_TYPE_LABEL: 'Fixture Type',
          ROOM_NAME_LABEL: 'Room Name',
          NO_FIXTURES: 'No fixtures configured',
          SELECT_FIXTURE: 'Select fixture',
          ROOM_NAME_PH: 'e.g. Living Room',
          OUTSIDE_ROOMS: 'Outside detected rooms',
          VERTICES: 'Vertices',
          CANCEL: 'Cancel',
          SAVE_DUCT: 'Save Duct',
          SAVE_FIXTURE: 'Save Fixture',
          SAVE_ROOM: 'Save Room',
          EDITING: 'Editing:',
          LIVE: 'Live',
          SELECTED: 'Selected: {{name}}',
          EDIT_POINTS_TITLE: 'Edit polygon points',
          EDIT: 'Edit',
          DELETE_FIXTURE: 'Delete this fixture',
          DELETE_ROOM_BTN: 'Delete this room',
          DELETE: 'Delete',
          DESELECT: 'Deselect',
          DRAG_FIXTURE_HINT: 'Drag the fixture polygon on the plan to update this room.',
          FIXTURE_TYPE: 'Fixture Type',
          ASSOC_ROOM: 'Associated Room',
          UNASSIGNED: 'Unassigned',
          FLOOR_AREA: 'Floor Area',
          PERIMETER: 'Perimeter',
          FLOORING_TYPE: 'Flooring Type',
          CEILING_AREA: 'Ceiling Area',
          WALL_PERIMETER: 'Wall Perimeter',
          ROOM_HEIGHT: 'Room Height',
          CORNER_POINTS: 'Corner Points',
          WALL_LENGTH: 'Wall Length',
          SAVE_HEIGHT_TITLE: 'Save room height',
          EDIT_HEIGHT_TITLE: 'Edit room height',
          SAVE: 'Save',
          CANCEL_HEIGHT: 'Cancel',
          LENGTH: 'Length',
          WIDTH: 'Width',
          TYPE: 'Type',
          BEAM_TYPE: 'Beam Type',
          THICKNESS: 'Thickness',
          AREA: 'Area',
          CORNERS: 'Corners',
          COL_GROUP: 'Column Group',
          BEAM_GROUP: 'Beam Group',
          TOTAL_LEN: 'Total Length',
          COLS_IN_GROUP: 'Columns in group',
          BEAMS_IN_GROUP: 'Beams in group',
          NO_ROOM_SELECTED: 'No room selected',
          NO_FIXTURE_SELECTED: 'No fixture selected',
          NO_COLUMN_SELECTED: 'No column selected',
          NO_BEAM_SELECTED: 'No beam selected',
          CLICK_ROOM_HINT: 'Click a room on the floor plan to view details.',
          CLICK_FIXTURE_HINT: 'Click a fixture on the floor plan to view details.',
          CLICK_COLUMN_HINT: 'Left-click a column to view details.',
          CLICK_COLUMN_RIGHT: 'Right-click to select the whole group.',
          CLICK_BEAM_HINT: 'Left-click a beam to view details.',
          CLICK_BEAM_RIGHT: 'Right-click to select the whole group.',
          INVALID_HEIGHT_TITLE: 'Invalid height',
          INVALID_HEIGHT_MESSAGE: 'Room height must be between 4 and 15 ft.',
        },
        SUMMARY: {
          FIXTURE_S: 'fixture detected',
          FIXTURE_P: 'fixtures detected',
          AREA_S: 'area detected',
          AREA_P: 'areas detected',
          NO_FIXTURES: 'No fixtures detected for this floor.',
          NO_AREAS: 'No areas detected for this floor.',
          FIXTURE_TYPE: 'Fixture Type',
          COUNT: 'Count',
          AREA_NAME: 'Area Name',
          AREA_SQ_FT: 'Area (sq ft)',
          PERIMETER_FT: 'Perimeter (ft)',
          CORNERS: 'Corners',
        },
      },
    },
    AUTH: {
      ADMIN_LOGIN: {
        CONSOLE: 'Admin Console',
        HERO_LINE_1: 'Operate the',
        HERO_LINE_2: 'Workspace.',
        HERO_DESCRIPTION: 'One console to manage builders, subscriptions, project records, scope configurations, and support.',
        FEATURE_BUILDERS: 'Builders',
        FEATURE_ACCOUNTS: 'Accounts',
        FEATURE_PLANS: 'Plans',
        FEATURE_SUBSCRIPTIONS: 'Subscriptions',
        FEATURE_SCOPES: 'Scopes',
        FEATURE_SETTINGS: 'Settings',
        BULLET_BUILDERS: 'Review and manage all builder accounts',
        BULLET_SUBSCRIPTIONS: 'Maintain subscriptions and project records',
        BULLET_SETTINGS: 'Configure scope materials, pricing, and support resources',
        MONITORED: 'Admin access is monitored and logged · TALOS Takeoffs',
        ADMIN: 'Admin',
        PORTAL: 'Admin Portal',
        TITLE: 'Sign in.',
        SUBTITLE: 'Authorized personnel only',
        EMAIL: 'Email address',
        EMAIL_ERROR: 'Please enter a valid email address.',
        PASSWORD: 'Password',
        PASSWORD_PLACEHOLDER: 'Enter your password',
        PASSWORD_ERROR: 'Password is required.',
        SIGN_IN_BUTTON: 'Sign in to Admin Portal',
        SIGNING_IN: 'Signing in...',
        SECURE_NOTE: '256-bit encrypted · Secure TALOS access',
      },
      BUILDER: {
        COMMON: {
          TAKEOFFS: 'TAKEOFFS',
          EMAIL: 'Email Address',
          EMAIL_ERROR: 'Please enter a valid email address.',
          PASSWORD: 'Password',
          PASSWORD_PLACEHOLDER: 'Enter your password',
          PASSWORD_REQUIRED: 'Password is required.',
          SECURE_ACCESS: '256-bit encrypted · Secure TALOS access',
          BUILT_AROUND: 'Built around the TALOS takeoff workflow.',
          BACK_TO_LOGIN: 'Back to Login',
          NEED_HELP: 'Need Help?',
          CONTACT_SUPPORT: 'Contact Support',
          OTP_PLACEHOLDER: 'Enter 6-digit code',
        },
        LOGIN: {
          PANEL_EYEBROW: 'Builder Workspace',
          PANEL_TITLE_1: 'From Plans',
          PANEL_TITLE_2: 'to Pricing',
          PANEL_TITLE_3: 'Reports.',
          PANEL_DESC: 'Upload plan PDFs, choose trade scopes, review detected quantities, and export pricing reports from one workspace.',
          FEATURE_PLANS: 'Plans',
          FEATURE_UPLOADS: 'Uploads',
          FEATURE_SCOPES: 'Scopes',
          FEATURE_QUANTITIES: 'Quantities',
          FEATURE_REPORTS: 'Reports',
          FEATURE_EXPORTS: 'Exports',
          BULLET_PROJECTS: 'Create projects and upload floor plan sheets',
          BULLET_REVIEW: 'Review AI-detected rooms, scopes, and quantities',
          BULLET_PRICING: 'Adjust pricing inputs before exporting reports',
          BADGE: 'Builder Portal',
          TITLE: 'Welcome back.',
          SUBTITLE: 'Sign in to your project workspace',
          REMEMBER_ME: 'Remember me',
          FORGOT_PASSWORD: 'Forgot password?',
          SIGN_IN_BUTTON: 'Sign in to your account',
          SIGNING_IN: 'Signing in...',
          NO_ACCOUNT: "Don't have an account?",
          SIGN_UP_FREE: 'Sign up for free',
          SIGN_IN_SHORT: 'Sign in',
        },
        SIGNUP: {
          LOGO_TAG: 'BUILDER_SIGNUP',
          PANEL_EYEBROW: 'INITIALIZE WORKSPACE',
          PANEL_TITLE_1: 'Faster estimates',
          PANEL_TITLE_2: 'start here.',
          PANEL_DESC: 'Tell us about your trade and workflow. TALOS configures your estimating workspace around your answers.',
          FEATURE_PROFILE: 'Builder Profile',
          FEATURE_PROFILE_HINT: 'Trade, company, and contact details',
          FEATURE_WORKFLOW: 'Workflow Setup',
          FEATURE_WORKFLOW_HINT: 'Project types and estimating volume',
          FEATURE_SECURE: 'Secure Access',
          FEATURE_SECURE_HINT: '256-bit encrypted credentials',
          FOOT_LABEL: 'SYSTEM_READY - TALOS AI CONSTRUCTION',
          BADGE: 'Builder Signup',
          TITLE: 'Create your workspace.',
          SUBTITLE: 'Complete the sections below to activate your TALOS builder account.',
          SECTIONS: {
            ACCOUNT_TITLE: 'Account Details',
            ACCOUNT_HINT: 'Name, contact, and company info',
            SECURITY_TITLE: 'Security',
            SECURITY_HINT: 'Create a strong password for your account',
            WORK_TITLE: 'Your Work',
            WORK_HINT: 'Tell us about your projects and volume',
            CHALLENGES_TITLE: 'Your Challenges',
            CHALLENGES_HINT: 'Help us optimize your workflow',
          },
          FIELDS: {
            FULL_NAME: 'Full Name *',
            FULL_NAME_PLACEHOLDER: 'Enter your full name',
            WORK_EMAIL: 'Work Email *',
            PHONE: 'Phone Number *',
            COMPANY: 'Company Name *',
            COMPANY_PLACEHOLDER: 'Enter your company name',
            ADDRESS: 'Address *',
            ADDRESS_PLACEHOLDER: 'Enter your address',
            TRADE: 'Trade / Specialty *',
            TRADE_PLACEHOLDER: 'Select your trade',
            PASSWORD: 'Password *',
            PASSWORD_PLACEHOLDER: 'Enter a password',
            CONFIRM_PASSWORD: 'Confirm Password *',
            CONFIRM_PLACEHOLDER: 'Re-enter your password',
            WORK_TYPE: 'What type of work do you do?',
            PROJECT_TYPE: 'What type of projects do you usually work on? *',
            ESTIMATES: 'How many projects do you estimate each month?',
            CHALLENGES: 'What slows you down the most?',
            OTHER: "Anything else you'd like to share?",
            OTHER_PLACEHOLDER: 'Tell us about any other challenges you face...',
            OPTIONAL: 'Optional',
          },
          ERRORS: {
            FULL_NAME: 'Full name is required.',
            PHONE: 'Please enter a valid 10-digit US phone number.',
            COMPANY: 'Company name is required.',
            ADDRESS: 'Address is required.',
            TRADE: 'Trade is required.',
            PASSWORD: "Password doesn't meet all requirements.",
            CONFIRM: 'Passwords do not match.',
            WORK_TYPE: 'Select at least one type of work.',
            PROJECT_TYPE: 'Please select a project type.',
            ESTIMATES: 'Please select an estimate range.',
            CHALLENGES: 'Select at least one challenge.',
            GENERIC: 'Something went wrong. Please try again.',
          },
          PASSWORD_RULES: {
            MIN: 'At least 8 characters',
            UPPER: 'One uppercase letter',
            LOWER: 'One lowercase letter',
            NUMBER: 'One number',
            SPECIAL: 'One special character',
          },
          OPTIONS: {
            TRADES: {
              GENERAL_CONTRACTOR: 'General Contractor',
              SUBCONTRACTOR: 'Subcontractor',
              ESTIMATOR: 'Estimator',
              ARCHITECT: 'Architect',
              ENGINEER: 'Engineer',
              DEVELOPER: 'Developer',
              HOME_OWNER: 'Home Owner',
            },
            WORK_TYPES: {
              FLOORING: 'Flooring',
              DRYWALL: 'Drywall',
              FRAMING: 'Framing',
              PAINTING: 'Painting',
              ELECTRICAL: 'Electrical',
              PLUMBING: 'Plumbing',
              GENERAL_CONTRACTOR: 'General Contractor',
              OTHER: 'Other',
            },
            PROJECT_TYPES: {
              RESIDENTIAL: 'Residential',
              COMMERCIAL: 'Commercial',
              BOTH: 'Both',
            },
            CHALLENGES: {
              TAKEOFFS_LONG: 'Takeoffs take too long',
              MANUAL_WORK: 'Too much manual work',
              MISSING_BIDS: 'Missing bids due to time',
              ERRORS: 'Errors in estimates',
              SCALE: 'Hard to scale estimating',
            },
          },
          CREATE_ACCOUNT: 'Create my account',
          HAVE_ACCOUNT: 'Already have an account?',
        },
        FORGOT: {
          PANEL_EYEBROW: 'Account Recovery',
          PANEL_TITLE_1: 'Verify your',
          PANEL_TITLE_2: 'email before',
          PANEL_TITLE_3: 'reset.',
          PANEL_DESC: 'We send a one-time code to your registered email so the reset request stays tied to your account.',
          BULLET_EMAIL: 'Request a code with your work email',
          BULLET_OTP: 'Use the OTP from your inbox to continue',
          BULLET_RESET: 'Reset access without exposing your old password',
          PANEL_FOOTER: 'Secure TALOS account recovery.',
          BADGE: 'Recovery',
          TITLE: 'Forgot password?',
          SUBTITLE: "Enter your email and we'll send a one-time code.",
          EMAIL_PLACEHOLDER: 'Enter your work email',
          SEND_OTP: 'Send OTP',
          SENDING: 'Sending...',
          CHECK_INBOX: 'Check your inbox.',
          SENT_PREFIX: 'We sent a 6-digit OTP to',
          ENTER_OTP: 'Enter OTP & Reset Password',
          ERROR_SEND: 'Could not send OTP. Please try again.',
        },
        RESET: {
          PANEL_EYEBROW: 'Credential Reset',
          PANEL_TITLE_1: 'Confirm OTP,',
          PANEL_TITLE_2: 'set a safer',
          PANEL_TITLE_3: 'password.',
          PANEL_DESC: 'The reset flow verifies your one-time code, checks the new credential, and updates access in one secure pass.',
          BULLET_VALIDATE: 'Validate the OTP sent to your email',
          BULLET_STRONGER: 'Create a stronger password before submitting',
          BULLET_RETURN: 'Return to login after the credential is updated',
          PANEL_FOOTER: 'Secure TALOS credential update.',
          BADGE: 'Password Reset',
          TITLE: 'Reset password.',
          SUBTITLE: 'Enter the OTP sent to your email and choose a new password.',
          OTP_SENT_TO: 'OTP sent to',
          OTP_LABEL: 'One-Time Password (OTP) *',
          OTP_ERROR: 'Please enter the 6-digit OTP from your email.',
          NEW_PASSWORD: 'New Password *',
          NEW_PASSWORD_PLACEHOLDER: 'Enter new password',
          PASSWORD_ERROR: 'Password must meet all requirements below.',
          REQUIREMENTS: 'Password requirements',
          RULE_UPPER: 'At least 1 uppercase letter',
          RULE_NUMBER: 'At least 1 number',
          RULE_SPECIAL: 'At least 1 special character',
          CONFIRM_PASSWORD: 'Confirm New Password *',
          CONFIRM_PLACEHOLDER: 'Repeat new password',
          RESET_BUTTON: 'Reset Password',
          RESETTING: 'Resetting...',
          ERROR_INVALID: 'Invalid or expired OTP. Please try again.',
        },
        VERIFY: {
          TITLE: 'Verify your email',
          SUBTITLE: 'We sent a 6-digit code to your email address. Enter it below to confirm your account.',
          CODE_LABEL: 'Verification Code *',
          CODE_ERROR: 'Please enter the 6-digit code.',
          VERIFY_BUTTON: 'Verify Email',
          SENDING: 'Sending...',
          RESEND_IN: 'Resend code in {{seconds}}s',
          RESEND: "Didn't receive a code? Resend",
          SECURE_NOTE: 'Your data is protected with 256-bit encryption',
          SUCCESS_TITLE: 'Email verified!',
          SUCCESS_MESSAGE: 'Your account has been confirmed. You can now sign in.',
          GO_LOGIN: 'Go to Login',
          TOAST_RESENT_TITLE: 'Code resent',
          TOAST_RESENT_MESSAGE: 'A new verification code has been sent to your email.',
          TOAST_FAILED_TITLE: 'Resend failed',
          TOAST_FAILED_MESSAGE: 'Failed to resend code. Please try again.',
          ERROR_INVALID: 'Invalid or expired code. Please try again.',
        },
      },
    },
    LANDING: {
      LOGO_SUB: 'construction ai',
      HERO: {
        EYEBROW: 'AI Takeoff Platform',
        HEADLINE_LINE_1: 'Plans in.',
        HEADLINE_LINE_2: 'Reports out.',
        HEADLINE_MUTED: 'No manual scaling.',
        DESCRIPTION: 'Upload floor plan PDFs, choose trade scopes, review AI-detected quantities, and export structured pricing reports - all in one workstation.',
        FEATURES: {
          PLANS_TITLE: 'Plans',
          PLANS_SUB: 'Uploads',
          SCOPES_TITLE: 'Scopes',
          SCOPES_SUB: 'Detected',
          REPORTS_TITLE: 'Reports',
          REPORTS_SUB: 'Exported',
        },
        BULLETS: {
          DETECTS: 'AI detects rooms, scopes, and quantities from plan PDFs',
          RATES: 'Apply material and labor rates across 5 trade categories',
          EXPORT: 'Export itemized pricing reports for every project',
        },
        CAPTION: 'Built around the TALOS takeoff workflow.',
      },
      CARD: {
        LOGO_SUB: 'TAKEOFFS + PRICING',
        BADGE: 'AI Construction Platform',
        TITLE_LINE_1: 'The AI Takeoff',
        TITLE_LINE_2: 'Workstation.',
        SUBTITLE: 'From floor plan upload to priced, exportable report - in minutes, not hours.',
        START_FIRST: 'Start Your First Takeoff ->',
        SIGN_IN: 'Sign In to Workspace',
        SECURE_ACCESS: '256-bit encrypted · Secure TALOS access',
        STATS: {
          ANALYSIS_TIME: 'ANALYSIS TIME',
          TRADE_CATEGORIES: 'TRADE CATEGORIES',
          DETECTION_ACCURACY: 'DETECTION ACCURACY',
          FULL_WORKFLOW: 'FULL WORKFLOW',
        },
      },
      HOW: {
        EYEBROW: 'How It Works',
        TITLE_LINE_1: 'Four steps. PDF to',
        TITLE_MUTED: 'priced report.',
        DESCRIPTION: 'A structured AI workflow that replaces the manual estimation process entirely - from raw PDF to structured pricing output.',
        STEPS: {
          UPLOAD: {
            TITLE: 'Upload your floor plan PDFs',
            DESC: 'Create a project and attach your plan sheets. TALOS accepts multi-page PDFs and processes each sheet independently.',
          },
          DETECT: {
            TITLE: 'AI detects rooms and quantities',
            DESC: 'The TALOS engine scans each sheet, identifies room boundaries, and calculates scope quantities across your selected trade categories.',
          },
          REVIEW: {
            TITLE: 'Review and apply pricing',
            DESC: 'Inspect detected quantities room by room. Apply material and labor rates to each scope from the pricing configuration panel.',
          },
          EXPORT: {
            TITLE: 'Export structured pricing reports',
            DESC: 'Generate a formatted PDF with itemized scope totals, labor breakdowns, and a project summary - ready for client delivery.',
          },
        },
      },
      MODULES: {
        EYEBROW: 'Platform Modules',
        TITLE_LINE_1: 'Two modules.',
        TITLE_MUTED: 'One platform.',
        DESCRIPTION: 'Takeoffs and pricing live together. No context switching between tools.',
        TAKEOFF: {
          MOCK_TITLE: 'AI Takeoff Review',
          DESCRIPTION: 'AI-powered material estimation for enterprise contracting divisions. Upload floor plans, detect trade scopes, review quantities, and lock the estimate - in minutes.',
          CTA: 'Access Takeoff Workstation ->',
        },
        PRICING: {
          MOCK_TITLE: 'Pricing Report',
          EXPORT_READY: 'EXPORT READY',
          DESCRIPTION: 'Convert AI-detected quantities into configurable pricing reports. Apply rates per scope, review totals, and export structured PDF reports for client delivery.',
          CTA: 'Access Pricing Module ->',
        },
      },
      CTA: {
        EYEBROW: 'Get Started',
        TITLE_LINE_1: 'Built for estimators',
        TITLE_LINE_2: 'who value',
        TITLE_MUTED: 'precision.',
        DESCRIPTION: 'No manual scaling. No spreadsheet chaos. Full AI takeoff and pricing infrastructure - available now.',
        CREATE_ACCOUNT: 'Create Your Account ->',
      },
      FOOTER: {
        BRAND_SUB: 'CONSTRUCTION AI',
        COPY: '© 2026 TALOS - All rights reserved',
        SECURITY: 'Security',
        PRIVACY: 'Privacy',
        TERMS: 'Terms',
      },
    },
    FOOTER: {
      BRAND_DESC: 'AI-powered plan takeoffs and pricing reports for construction estimating teams.',
      PRODUCT: {
        TITLE: 'Product',
        TAKEOFFS: 'Takeoffs',
        PRICING: 'Pricing',
        PROCESS: 'Process',
        SCOPE_SETTINGS: 'Scope Settings',
      },
      COMPANY: {
        TITLE: 'Company',
        MISSION: 'Mission',
        WORKFLOWS: 'Workflows',
        BUILDERS: 'Builders',
        SUPPORT: 'Support',
      },
      ACCOUNT: {
        TITLE: 'Account',
        LOGIN: 'Login',
        START_TAKEOFF: 'Start Takeoff',
        DOCUMENTATION: 'Documentation',
        PRIVACY: 'Privacy',
      },
      COPYRIGHT: '© 2026 TALOS / AI-CONSTRUCTION — All rights reserved',
      LEGAL: {
        SECURITY: 'Security',
        PRIVACY: 'Privacy',
        TERMS: 'Terms',
      },
    },
    BUILDER: {
      DASHBOARD: {
        HERO: {
          KICKER: 'OPERATIONS CENTER',
          WELCOME: 'Welcome back,',
          LINE_1: 'Upload your floor plans and let TALOS',
          LINE_2: 'run your AI-powered takeoff analysis.',
          START: 'Start New Project',
        },
        STATS: {
          TOTAL_KICKER: 'TOTAL',
          PROJECTS: 'Projects',
          DONE_KICKER: 'DONE',
          COMPLETED: 'Completed',
          ACTIVE_KICKER: 'ACTIVE',
          IN_PROGRESS: 'In Progress',
        },
        TABLE: {
          KICKER: 'RECENT QUEUE',
          HEADING: 'My Projects',
          SUBTITLE: 'Your latest takeoff packages',
          NEW_PROJECT: 'New Project',
          COL_PROJECT: 'Project',
          COL_STATUS: 'Status',
          COL_PLANS: 'Plans',
          COL_UPDATED: 'Updated',
          UPLOADED: 'uploaded',
        },
        STATUS: {
          COMPLETED: 'Completed',
          IN_PROGRESS: 'In Progress',
          ACTIVE: 'Active',
        },
      },
      PROFILE: {
        EDIT_BTN: 'Edit Profile',
        MEMBER_SINCE: 'Member since',
        CONTACT: {
          TITLE: 'Contact Information',
          EMAIL: 'Email',
          PHONE: 'Phone',
          COMPANY: 'Company',
          ADDRESS: 'Address',
        },
        WORK: {
          TITLE: 'Work Details',
          WORK_TYPE: 'Work Type',
          PROJECT_TYPE: 'Project Type',
          PPM: 'Projects / Month',
          CHALLENGES: 'Challenges',
          OTHER_CHALLENGES: 'Other Challenges',
        },
        SECURITY: {
          TITLE: 'Security',
          PASSWORD: 'Password',
          LAST_UPDATED: 'Last updated: unknown',
          CHANGE_BTN: 'Change Password',
        },
        DANGER: {
          TITLE: 'Danger Zone',
          DESC: 'Permanently delete your account and all associated data. This cannot be undone.',
          DELETE_BTN: 'Delete Account',
        },
        EDIT: {
          TITLE: 'Edit Profile',
          SUBTITLE: 'Changes are saved immediately after you click Save.',
          CANCEL: 'Cancel',
          SAVE: 'Save Changes',
          BASIC_INFO: 'Basic Information',
          FULL_NAME: 'Full Name *',
          FULL_NAME_PH: 'John Doe',
          FULL_NAME_ERR: 'Full name is required.',
          PHONE: 'Phone Number *',
          PHONE_PH: '10-digit phone',
          PHONE_ERR: 'Enter a valid 10-digit phone number.',
          COMPANY: 'Company Name *',
          COMPANY_PH: 'Acme Construction',
          COMPANY_ERR: 'Company name is required.',
          ADDRESS: 'Address *',
          ADDRESS_PH: '123 Main St',
          ADDRESS_ERR: 'Address is required.',
          TRADE: 'Trade *',
          TRADE_PH: 'Select your trade',
          TRADE_ERR: 'Trade is required.',
          PROJ_TYPE: 'Project Type *',
          PROJ_TYPE_PH: 'Select project type',
          PROJ_TYPE_ERR: 'Project type is required.',
          PPM: 'Projects / Month *',
          PPM_PH: 'Select range',
          PPM_ERR: 'This field is required.',
          OTHER: 'Other Challenges',
          OTHER_PH: "Anything else you'd like to improve...",
        },
        DELETE_MODAL: {
          TITLE: 'Delete your account?',
          DESC: 'This will permanently delete your account, projects, and all data.',
          CANNOT_UNDO: 'This cannot be undone.',
          CONFIRM: 'Yes, permanently delete',
          CANCEL: 'Cancel',
        },
        CP_MODAL: {
          TITLE: 'Change Password',
          STEP: 'Step {{step}} of 2',
          STEP1_DESC: "We'll send a 6-digit OTP to your registered email address:",
          SEND_OTP: 'Send OTP',
          OTP_SENT: 'OTP sent! Check your email and enter it below.',
          OTP_CODE: 'OTP Code *',
          OTP_PH: 'Enter 6-digit code',
          OTP_ERR: 'Please enter the 6-digit OTP.',
          NEW_PWD: 'New Password *',
          NEW_PWD_PH: 'At least 8 characters',
          NEW_PWD_ERR: 'Password must be at least 8 characters.',
          CONFIRM_PWD: 'Confirm Password *',
          CONFIRM_PH: 'Repeat new password',
          CONFIRM_ERR: 'Passwords do not match.',
          UPDATE: 'Update Password',
          SENDING: 'Sending...',
          RESEND_WAIT: 'Resend OTP in {{seconds}}s',
          RESEND: 'Resend OTP',
        },
        TOAST: {
          PROFILE_UPDATED: 'Profile updated',
          PROFILE_UPDATED_MSG: 'Your changes have been saved.',
          UPDATE_FAILED: 'Update failed',
          UPDATE_FAILED_MSG: 'Failed to update profile. Please try again.',
          PWD_CHANGED: 'Password changed',
          PWD_CHANGED_MSG: 'Your password has been updated successfully.',
          OTP_RESENT: 'OTP resent',
          OTP_RESENT_MSG: 'A new code has been sent to your email.',
        },
      },
    },
  },
  es: {
    COMMON: {
      CANCEL: 'Cancelar',
      CLOSE: 'Cerrar',
      UNEXPECTED_ERROR_TITLE: 'Algo salió mal',
      UNEXPECTED_ERROR_MESSAGE: 'Ocurrió un error inesperado. Inténtalo de nuevo.',
    },
    ADMIN: {
      SHELL: {
        HEADER: {
          TOGGLE_MENU: 'Alternar menú',
          ADMIN_CONSOLE: 'Consola Admin',
          ONLINE: 'En línea',
          ADMINISTRATOR: 'Administrador',
          TALOS_ADMIN: 'Admin TALOS',
          ADMIN_USER: 'Usuario Admin',
          LANGUAGE: 'Idioma',
          LOG_OUT: 'Cerrar sesión',
        },
        SIDEBAR: {
          TALOS_HOME: 'Inicio TALOS',
          TAKEOFFS: 'takeoffs',
          CLOSE_MENU: 'Cerrar menú',
          OPERATIONS: 'Operaciones',
          SYSTEM: 'Sistema',
          DASHBOARD: 'Panel',
          PROJECTS: 'Proyectos',
          BUILDERS: 'Builders',
          SUBSCRIPTIONS: 'Suscripciones',
          SUPPORT: 'Soporte',
          SETTINGS: 'Configuración',
          COPYRIGHT: '© 2026 TALOS · Todos los derechos reservados',
          EXPAND: 'Expandir barra lateral',
          COLLAPSE: 'Contraer barra lateral',
        },
      },
      DASHBOARD: {
        HERO: {
          KICKER: 'Centro de Operaciones',
          TITLE_LINE_1: 'Bienvenido de nuevo,',
          TITLE_LINE_2: 'Administrador.',
          DESCRIPTION: 'Analiza planos, revisa cantidades extraídas y mantén avanzando los flujos de estimación.',
          UPLOAD: 'Crear Proyecto',
          ALL_PROJECTS: 'Todos los Proyectos →',
        },
        PULSE: {
          KICKER: 'Pulso del Sistema',
          PLANS_PROCESSED: 'Planos Procesados',
          REPORTS_GENERATED: 'Levantamientos Generados',
          ACTIVE_PROJECTS: 'Proyectos Activos',
        },
        STATS: {
          TOTAL_PLANS: 'Total de Planos Procesados',
          REPORTS_GENERATED: 'Levantamientos Generados',
          LAST_ACTIVITY: 'Última Actividad',
          LAST_ACTIVITY_VALUE: 'hace 2 h',
          SAVED_PROJECTS: 'Proyectos Guardados',
          BADGE_PLUS_12: '+12%',
          BADGE_PLUS_8: '+8%',
          BADGE_TODAY: 'Hoy',
          BADGE_ACTIVE: 'Activo',
        },
        RECENT: {
          KICKER: 'Cola Reciente',
          TITLE: 'Proyectos Recientes',
          DESCRIPTION: 'Últimas entradas en la plataforma',
          VIEW_ALL: 'Ver Todo →',
        },
      },
      PROJECTS: {
        HERO: {
          KICKER: 'Índice de Proyectos',
          TITLE: 'Proyectos',
          LINE_1: 'Busca, administra y abre',
          LINE_2: 'cada paquete de takeoff.',
        },
        ACTIONS: {
          NEW_PROJECT: 'Nuevo Proyecto',
          REFRESH: 'Actualizar',
          TRY_AGAIN: 'Intentar de Nuevo',
        },
        SEARCH: {
          PLACEHOLDER: 'Buscar por nombre de proyecto o propietario...',
        },
        META: {
          PROJECTS: '{{count}} proyectos',
          PAGE: 'Página {{page}}',
        },
        ERROR: {
          LOAD_TITLE: 'No se pudieron cargar los proyectos',
          LOAD_MESSAGE: 'No se pudieron cargar los proyectos. Inténtalo de nuevo.',
        },
        TABLE: {
          PROJECT_NAME: 'Nombre del Proyecto',
          OWNER: 'Propietario',
          STATUS: 'Estado',
          LAST_MODIFIED: 'Última Modificación',
          TYPE: 'Tipo',
          ACTIONS: 'Acciones',
          EMPTY_TITLE: 'No se encontraron proyectos',
          EMPTY_MESSAGE: 'Ajusta la búsqueda o agrega un nuevo proyecto',
          VIEW: 'Ver',
          VIEW_PROJECT: 'Ver proyecto',
          EDIT: 'Editar',
          EDIT_PROJECT: 'Editar proyecto',
          DELETE: 'Eliminar',
          DELETE_PROJECT: 'Eliminar proyecto',
        },
        STATUS: {
          ACTIVE: 'Activo',
          IN_PROGRESS: 'En Progreso',
          COMPLETED: 'Completado',
        },
        TYPE: {
          RESIDENTIAL: 'Residencial',
          COMMERCIAL: 'Comercial',
          INDUSTRIAL: 'Industrial',
          OTHER: 'Otro',
        },
        CONFIRM_DELETE: {
          TITLE: 'Eliminar Proyecto',
          MESSAGE: '¿Seguro que quieres eliminar este proyecto?',
          CONFIRM: 'Eliminar',
        },
        MODAL: {
          VIEW_TITLE: 'Ver Proyecto',
          EDIT_TITLE: 'Editar Proyecto',
          CREATE_TITLE: 'Crear Nuevo Proyecto',
          PROJECT_NAME: 'Nombre del Proyecto *',
          PROJECT_NAME_PLACEHOLDER: 'Ingresa el nombre del proyecto',
          PROJECT_NAME_ERROR: 'El nombre del proyecto es obligatorio.',
          PROJECT_NAME_HINT: 'Ejemplo: Palm Heights Torre A',
          PROJECT_TYPE: 'Tipo de Proyecto',
          PROJECT_TYPE_PLACEHOLDER: 'Selecciona el tipo de proyecto',
          PROJECT_TYPE_ERROR: 'Selecciona un tipo de proyecto.',
          PROJECT_TYPE_NAME: 'Nombre de Tipo Personalizado',
          PROJECT_TYPE_NAME_PLACEHOLDER: 'Ej. Uso Mixto, Comercio',
          SAVE: 'Guardar Cambios',
          CREATE: 'Crear Proyecto',
        },
        TOAST: {
          UPDATE_SUCCESS_TITLE: 'Proyecto actualizado',
          CREATE_SUCCESS_TITLE: 'Proyecto creado',
          UPDATE_SUCCESS_MESSAGE: 'Proyecto actualizado correctamente.',
          CREATE_SUCCESS_MESSAGE: 'Proyecto creado correctamente.',
          UPDATE_FAILED_TITLE: 'Error al actualizar',
          CREATE_FAILED_TITLE: 'Error al crear',
          UPDATE_FAILED_MESSAGE: 'No pudimos actualizar el proyecto. Inténtalo de nuevo.',
          CREATE_FAILED_MESSAGE: 'No pudimos crear el proyecto. Inténtalo de nuevo.',
        },
      },
      SETTINGS: {
        UNKNOWN_SCOPE: 'Configuración',
        HERO: {
          KICKER: 'Config. del Sistema',
          TITLE: 'Configuración',
          LINE_1: 'Configura precios de materiales, parámetros de alcance',
          LINE_2: 'y valores predeterminados para todas las categorías.',
        },
        ACTIVE_SCOPE: {
          KICKER: 'Alcance Activo',
          DESCRIPTION: 'Los cambios aplican solo a esta categoría.',
        },
        ACTIONS: {
          SAVE_CHANGES: 'Guardar Cambios',
        },
        PARAMETERS: {
          KICKER: 'Parámetros de Alcance',
          TITLE: 'Valores Predeterminados',
          DESCRIPTION: 'Se usan cuando no hay una anulación del proyecto.',
          WALL_HEIGHT_FT: 'Altura de Muro Predeterminada',
        },
        PANELS: {
          FIXTURE_TYPES: 'Tipos de Accesorios',
          FIXTURES: 'Accesorios',
          FIXTURE: 'Accesorio',
          FIXTURE_COUNT_ONE: '{{count}} accesorio configurado',
          FIXTURE_COUNT_MANY: '{{count}} accesorios configurados',
          MATERIAL_TYPES: 'Tipos de Material',
          MATERIAL_TYPES_TITLE: 'Tipos de Material',
          MATERIALS: 'Materiales',
          MATERIAL: 'Material',
          MATERIAL_COUNT_ONE: '{{count}} material configurado',
          MATERIAL_COUNT_MANY: '{{count}} materiales configurados',
          UNIT: 'Unidad',
          PRICE_PER_UNIT: 'Precio / Unidad',
          ACTIVE: 'Activo',
          ADD_MATERIAL: 'Agregar Material',
          EDIT: 'Editar',
          EDIT_PRICE: 'Editar precio',
          EDIT_MATERIAL: 'Editar material',
          DELETE: 'Eliminar',
          DELETE_MATERIAL: 'Eliminar material',
          SAVE: 'Guardar',
          NO_FIXTURES: 'No hay accesorios configurados',
          FIXTURES_SYSTEM_MANAGED: 'Los tipos de accesorios son administrados por el sistema.',
          NO_MATERIALS: 'No hay materiales configurados',
          MATERIALS_SYSTEM_MANAGED: 'Los tipos de material son administrados por el sistema.',
          ADD_FIRST_MATERIAL: 'Agrega tu primer material para comenzar.',
          DRYWALL_PRICE_DESCRIPTION: 'Precios predeterminados del backend usados para estimaciones de drywall',
          NO_DRYWALL_PRICES: 'No se encontraron precios de drywall.',
        },
        MODAL: {
          KICKER: 'Config. de Material',
          ADD_MATERIAL: 'Agregar Material',
          EDIT_MATERIAL: 'Editar Material',
          SAVE_MATERIAL: 'Guardar Material',
          SAVE_PRICE: 'Guardar Precio',
          SAVE_FIXTURE: 'Guardar Accesorio',
          EDIT_DRYWALL_PRICE: 'Editar Precio de Drywall',
          EDIT_FIXTURE_PRICE: 'Editar Precio de Accesorio',
          EDIT_PLUMBING_MATERIAL_PRICE: 'Editar Precio de Material de Plomería',
          EDIT_FIXTURE: 'Editar Accesorio',
          ADD_FIXTURE: 'Agregar Accesorio',
          MATERIAL_NAME: 'Nombre del Material *',
          MATERIAL_NAME_PLACEHOLDER: 'ej. Madera, Azulejo',
          MATERIAL_NAME_ERROR: 'El nombre del material es obligatorio.',
          UNIT: 'Unidad *',
          UNIT_ERROR: 'Selecciona una unidad.',
          PRICE_PER_UNIT: 'Precio por Unidad *',
          PRICE_ERROR: 'Ingresa un precio válido (0 o más).',
        },
        DOORS_WINDOWS: {
          MATERIALS_DESCRIPTION: 'Variedades de material compartidas para puertas y ventanas.',
          MATERIAL_NAME: 'Nombre del Material',
          DOORS: 'Puertas',
          DOORS_EMPTY: 'puertas',
          DOORS_DESCRIPTION: 'Tipos de puerta y tarifas de instalación predeterminadas.',
          WINDOWS: 'Ventanas',
          WINDOWS_EMPTY: 'ventanas',
          WINDOWS_DESCRIPTION: 'Tipos de ventana y tarifas de instalación predeterminadas.',
          ADD_TYPE: 'Agregar Tipo',
          NEW_TYPE: 'Nuevo tipo',
          UNIT_AT_PRICE: '{{unit}} a ${{price}}',
          DELETE_TYPE: 'Eliminar tipo',
          TYPE_NAME: 'Nombre del Tipo',
          EDIT_TYPE: 'Editar tipo',
          OPERATION: 'Operación',
          EDIT_OPERATION: 'Editar operación',
          BEST_USE: 'Uso Ideal',
          EDIT_BEST_USE: 'Editar uso ideal',
          NOT_SET: 'No definido',
          HANDING_OPTIONS: 'Opciones de Mano',
          NO_ACTIVE_MATERIALS: 'No hay materiales activos disponibles.',
          NO_TYPES: 'No hay {{type}} configuradas',
          ADD_FIRST_TYPE: 'Agrega el primer tipo para comenzar.',
        },
        PAINT: {
          BRAND_LIBRARY: 'Biblioteca de Marcas de Pintura',
          BRAND_LIBRARY_DESCRIPTION: 'Define marcas, colores disponibles, brillo y precio',
          REMOVE_BRAND: 'Eliminar marca',
          REMOVE_COLOR: 'Eliminar color',
          ADD_COLOR_PLACEHOLDER: 'Agregar color...',
          ADD: 'Agregar',
          ADD_BRAND: 'Agregar Marca',
          BRAND_NAME: 'Nombre de marca',
          DEFAULT_SHEEN: 'Brillo predeterminado',
          PRICE_5_GAL: 'Precio / 5 gal',
          MIN_PRICE: 'El precio mínimo es $5.00',
          SUPPLIES: 'Suministros de Pintura',
          SUPPLIES_DESCRIPTION: 'Precios unitarios usados en la sección Suministros de Pintura del reporte',
          PAINTERS_TAPE: 'Cinta de pintor (por rollo)',
          DROP_CLOTH: 'Lona plástica (cada una)',
          ROLLER_KIT: 'Kit de rodillo (por kit)',
          SURFACE_ASSIGNMENTS: 'Asignaciones de Superficie',
          SURFACE_ASSIGNMENTS_DESCRIPTION: 'Asigna marcas a cada superficie y luego elige color y brillo por asignación',
          NO_BRANDS_ASSIGNED: 'Aún no hay marcas asignadas.',
          BRAND: 'Marca',
          COLOR: 'Color',
          SHEEN: 'Brillo',
          REMOVE_ASSIGNMENT: 'Eliminar asignación',
          CHOOSE_BRAND: 'Elegir marca...',
          NO_COLORS: 'No hay colores configurados',
          NO_SHEEN: 'No hay brillo configurado',
        },
        TOAST: {
          LOAD_FAILED_TITLE: 'No se pudo cargar la configuración',
          LOAD_DRYWALL_FAILED_TITLE: 'No se pudieron cargar los precios de drywall',
          LOAD_DOORS_WINDOWS_FAILED_TITLE: 'No se pudo cargar la configuración de puertas y ventanas',
          LOAD_PLUMBING_FAILED_TITLE: 'No se pudo cargar la configuración de plomería',
          LOAD_PAINT_FAILED_TITLE: 'No se pudo cargar la configuración de pintura interior',
          TRY_AGAIN_MESSAGE: 'Inténtalo de nuevo en un momento.',
          SAVED_TITLE: 'Configuración guardada',
          SAVE_FAILED_TITLE: 'No se pudo guardar la configuración',
          SAVE_FAILED_MESSAGE: 'Revisa los cambios e inténtalo de nuevo.',
          WALL_HEIGHT_MESSAGE: 'La altura del muro debe ser al menos 6 ft.',
          PRICING_SAVED_MESSAGE: 'La configuración de precios fue actualizada.',
          PLUMBING_SAVED_MESSAGE: 'La configuración de plomería fue actualizada.',
          PAINT_SAVED_MESSAGE: 'La configuración de pintura interior fue actualizada.',
          DRYWALL_SAVED_MESSAGE: 'La configuración de drywall fue actualizada.',
          DOORS_WINDOWS_SAVED_MESSAGE: 'La configuración de Puertas y Ventanas fue actualizada.',
        },
      },
      SUBSCRIPTIONS: {
        HERO: {
          KICKER: 'Facturación',
          TITLE: 'Planes de Suscripción',
          LINE_1: 'Consulta y administra todos los niveles de suscripción',
          LINE_2: 'disponibles en la plataforma.',
        },
        ACTIONS: {
          ADD_PLAN: 'Agregar Plan',
          EDIT_PLAN: 'Editar plan',
        },
        STATS: {
          TOTAL: 'Total',
          PLANS: 'Planes',
          ACTIVE: 'Activos',
          RUNNING: 'En Curso',
          INACTIVE: 'Inactivos',
          PAUSED: 'Pausados',
        },
        BILLING: {
          MONTHLY: 'Mensual',
          YEARLY: 'Anual',
        },
        STATUS: {
          ACTIVE: 'Activo',
          INACTIVE: 'Inactivo',
        },
        PLANS: {
          MONTHLY_LIMITED: 'Plan Mensual Limitado',
          MONTHLY_PREMIUM: 'Plan Mensual Premium',
          YEARLY_LIMITED: 'Plan Anual Limitado',
          YEARLY_PREMIUM: 'Plan Anual Premium',
        },
        PRICE: {
          PER_YEAR: '/año',
          PER_MONTH: '≈ {{amount}} al mes',
        },
        FEATURES: {
          ALL_CATEGORIES: 'Todas las categorías incluidas',
          ONE_CATEGORY: '{{count}} categoría incluida',
          MANY_CATEGORIES: '{{count}} categorías incluidas',
          SUPPORT: 'Soporte {{support}}',
        },
        SUPPORT: {
          PRIORITY: 'prioritario',
        },
        MODAL: {
          KICKER: 'Config. del Plan',
          TITLE: 'Editar Plan de Suscripción',
          CLOSE: 'Cerrar',
          PLAN_NAME: 'Nombre del Plan *',
          PLAN_NAME_PLACEHOLDER: 'Ingresa el nombre del plan',
          PLAN_NAME_ERROR: 'El nombre del plan es obligatorio.',
          DESCRIPTION: 'Descripción *',
          DESCRIPTION_PLACEHOLDER: 'Descripción breve y clara para este plan.',
          DESCRIPTION_ERROR: 'La descripción es obligatoria.',
          DESCRIPTION_HINT: 'Descripción breve y clara para este plan.',
          ANNUAL_FEE: 'Tarifa Anual *',
          ANNUAL_FEE_ERROR: 'Ingresa una tarifa anual válida.',
          CANCEL: 'Cancelar',
          SAVE: 'Guardar Cambios',
        },
      },
      BUILDERS: {
        HERO: {
          KICKER: 'PLATAFORMA',
          TITLE: 'Todos los Constructores',
          TOTAL_BADGE: '{{count}} total',
          LINE_1: 'Gestiona todos los constructores registrados',
          LINE_2: 'y su acceso a suscripciones.',
        },
        STATS: {
          TOTAL_KICKER: 'TOTAL',
          REGISTERED: 'Registrados',
          PAID_KICKER: 'PAGADO',
          PRO_ENTERPRISE: 'Pro y Empresarial',
          FREE_KICKER: 'GRATIS',
          TRIAL: 'Acceso de Prueba',
        },
        TABLE: {
          KICKER: 'REGISTRO DE CONSTRUCTORES',
          HEADING: 'Todos los Constructores',
          SHOWING: 'Mostrando {{shown}} de {{total}} constructores',
          PAGE: '· Página {{page}}',
          SEARCH_PH: 'Buscar por nombre o email...',
        },
        EMPTY: {
          TITLE: 'No se encontraron constructores',
          SUBTITLE: 'Intenta ajustar tu búsqueda.',
        },
        COL: {
          BUILDER: 'Constructor',
          EMAIL: 'Correo',
          SUBSCRIPTION: 'Suscripción',
          JOINED: 'Se unió',
        },
        ACTION: {
          VIEW: 'Ver',
          DELETE: 'Eliminar',
        },
        MODAL: {
          TITLE: 'Eliminar Constructor',
          MESSAGE: '¿Estás seguro de que deseas eliminar este constructor de la plataforma?',
          CONFIRM: 'Eliminar',
          CANCEL: 'Cancelar',
        },
      },
      SUPPORT: {
        HERO: {
          KICKER: 'CENTRO DE AYUDA',
          TITLE: '¿En qué podemos ayudarte?',
          LINE_1: 'Consulta nuestras preguntas frecuentes, lee nuestras políticas o contáctanos.',
          LINE_2: 'Nuestro equipo está aquí para hacer tu experiencia con TALOS fluida.',
        },
        CONTACT: {
          EMAIL_TITLE: 'Soporte por Email',
          EMAIL_SUBTITLE: 'support@talos.ai',
          EMAIL_ACTION: 'Enviar mensaje',
          PHONE_TITLE: 'Soporte Telefónico',
          PHONE_SUBTITLE: '+1 (800) 555-0199',
          PHONE_ACTION: 'Llamarnos',
          TERMS_TITLE: 'Términos y Condiciones',
          TERMS_SUBTITLE: 'Políticas de uso de la plataforma',
          TERMS_ACTION: 'Leer términos',
          PRIVACY_TITLE: 'Política de Privacidad',
          PRIVACY_SUBTITLE: 'Manejo de datos y derechos',
          PRIVACY_ACTION: 'Leer política',
        },
        FAQ: {
          KICKER: 'PREGUNTAS FRECUENTES',
          TITLE: 'Preguntas Frecuentes',
          COUNT: '{{count}} preguntas',
          Q1: '¿Cómo agrego un nuevo constructor a la plataforma?',
          A1: 'Ve a Gestión de Constructores desde la barra lateral. Usa la opción de invitar o agregar constructor para registrar una nueva cuenta. Recibirá un correo con sus credenciales.',
          Q2: '¿Cómo se asignan los planes de suscripción a los constructores?',
          A2: 'Los planes de suscripción son seleccionados por los constructores durante su incorporación. Como administrador, puedes gestionar los planes disponibles desde la página de Gestión de Suscripciones.',
          Q3: '¿Qué ocurre cuando elimino un proyecto?',
          A3: 'Eliminar un proyecto es permanente. Todos los planos, reportes y documentos generados por IA serán eliminados. Confirma siempre antes de proceder.',
          Q4: '¿Puedo editar el precio de un plan de suscripción después de publicarlo?',
          A4: 'Sí — usa el botón de edición (lápiz) en cualquier tarjeta de plan en la página de Gestión de Suscripciones. Los cambios aplican solo a nuevas suscripciones; los suscriptores existentes no se ven afectados.',
          Q5: '¿Cómo contacto al soporte técnico?',
          A5: 'Puedes comunicarte con el equipo técnico por correo a support@talos.ai o llamar a nuestra línea de ayuda en horario de oficina. Los tiempos de respuesta son normalmente menores a 24 horas.',
        },
        FOOTER: 'Horario de soporte: Lun – Vie, 9 AM – 6 PM IST · Tiempo de respuesta promedio: < 24 horas',
      },
    },
    REPORTS: {
      NAV: {
        REPORTS: 'Reportes',
        AI_ESTIMATE: 'Estimación IA',
        EXPORT_WORD: 'Exportar Word',
        EXPORT_PDF: 'Exportar PDF',
        EXPORT_EXCEL: 'Exportar Excel',
      },
      CATEGORIES: {
        FLOORING: 'Pisos',
        DRYWALL: 'Drywall',
        INTERIOR_PAINT: 'Pintura Interior',
        FIXTURES: 'Fixtures',
        FIXTURES_PLUMBING: 'Fixtures (Plomería)',
        DOORS_WINDOWS: 'Puertas y Ventanas',
      },
      TITLES: {
        FLOORING_REPORT: 'Reporte de Pisos',
        DRYWALL_REPORT: 'Reporte de Drywall',
        INTERIOR_PAINT_REPORT: 'Reporte de Pintura Interior',
        FIXTURES_REPORT: 'Reporte de Fixtures (Plomería)',
        DOORS_WINDOWS_REPORT: 'Reporte de Puertas y Ventanas',
      },
      LABELS: {
        AI_GENERATED: 'Estimación generada por IA',
        TOTAL_COST: 'Costo Total',
        GRAND_TOTAL: 'Total General',
        PROJECT_ESTIMATE: 'Estimación del Proyecto',
        GENERATED: 'Generado',
        ESTIMATED_MATERIALS: 'Materiales estimados',
        FLOORS: 'Pisos',
        FLOOR: 'Piso',
        LEVELS_ANALYZED: 'Nivel(es) analizados',
        TOTAL_AREA: 'Área Total',
        COMBINED_FLOOR_AREA: 'Área combinada de pisos',
        ROOMS: 'Habitaciones',
        ACROSS_ALL_FLOORS: 'En todos los pisos',
        COST_BY_FLOOR: 'Costo por Piso',
        MATERIALS_DISTRIBUTION: 'Distribución de materiales por nivel',
        FLOOR_DISTRIBUTION: 'Distribución de Pisos',
        COST_SHARE_PER_FLOOR: 'Participación de costo por piso',
        ROOM_MATERIALS: 'Materiales por Habitación',
        MATERIALS_BREAKDOWN: 'Desglose de Materiales',
        ITEM: 'Ítem',
        NAME: 'Nombre',
        LABEL: 'Etiqueta',
        ROOM: 'Habitación',
        QTY: 'Cant.',
        QUANTITY: 'Cantidad',
        UNIT: 'Unidad',
        UNIT_PRICE: 'Precio Unitario',
        TOTAL: 'Total',
        COST: 'Costo',
        VALUE: 'Valor',
        DIMENSIONS: 'Dimensiones',
        TYPE: 'Tipo',
        HANDING: 'Apertura',
        MATERIAL: 'Material',
        FLOOR_SUBTOTAL: 'Subtotal del Piso',
        PROJECT_TOTAL: 'Total del proyecto',
        WALL_AREA: 'Área de Muros',
        CEILING_AREA: 'Área de Techo',
        CORNERS: 'Esquinas',
        INTERIOR_WALL_SURFACE: 'Superficie interior de muros',
        TOTAL_CEILING_SURFACE: 'Superficie total de techo',
        TOTAL_CORNER_POINTS: 'Puntos de esquina totales',
        MATERIAL_COST_BREAKDOWN: 'Desglose de Costo de Materiales',
        SURFACE_AREA_SPLIT: 'División de Superficies',
        BOARD_TYPE: 'Tipo de Placa',
        ALL_MATERIALS: 'Todos los Materiales',
        COMPLETE_MATERIALS_SCHEDULE: 'Lista completa de materiales',
        TOTAL_MATERIALS: 'Materiales Totales',
        COST_SPLIT: 'División de Costos',
        PAINT_AREAS: 'Áreas de Pintura',
        PRIMER_APPLICATION: 'Aplicación de Primer',
        PAINT_APPLICATION: 'Aplicación de Pintura',
        PAINT_SUPPLIES: 'Suministros de Pintura',
        PRIMER: 'Primer',
        PAINT: 'Pintura',
        SUPPLIES: 'Suministros',
        SINGLE_COAT: 'Una capa',
        SINGLE_COAT_APPLICATION: 'Aplicación de una capa',
        TWO_COATS: 'Dos capas',
        TWO_COAT_APPLICATION: 'Aplicación de dos capas',
        PRIMER_PAINT_SUPPLIES: 'Primer + Pintura + Suministros',
        BRUSHES_TAPE_TOOLS: 'Brochas, cinta y herramientas',
        APPLICATION: 'Aplicación',
        SQ_FT: 'Pies²',
        GALLONS: 'Galones',
        GAL: 'Gal',
        BRAND: 'Marca',
        COLOR: 'Color',
        SHEEN: 'Acabado',
        TOTAL_PRIMER_COST: 'Costo Total de Primer',
        OVERALL_PAINT_COST: 'Costo Total de Pintura',
        TOTAL_PAINT_SUPPLIES: 'Total de Suministros de Pintura',
        PEX_PIPING: 'PEX y Tubería',
        PEX_TUBING: 'Tubería PEX',
        PEX_CONNECTORS: 'Conectores PEX',
        DRAINAGE: 'Drenaje',
        OTHER: 'Otros',
        ALL_PLUMBING_COMBINED: 'Toda la plomería combinada',
        PER_ROOM_FIXTURE_COST: 'Costo de fixtures por habitación',
        TUBING_CONNECTORS: 'Tubería y conectores',
        DRAINAGE_EXAMPLES: 'P-traps, bridas, wyes',
        COST_BREAKDOWN: 'Desglose de Costos',
        SHARE_BY_PLUMBING_CATEGORY: 'Participación por categoría de plomería',
        SHARE_BY_CATEGORY: 'Participación por categoría',
        TOP_FIXTURES_BY_COST: 'Fixtures principales por costo',
        PER_FIXTURE_COST_DISTRIBUTION: 'Distribución de costo por fixture',
        FIXTURES_SUBTOTAL: 'Subtotal de Fixtures',
        DOORS: 'Puertas',
        WINDOWS: 'Ventanas',
        INSTALLATION: 'Instalación',
        MATERIALS_INSTALLATION: 'Materiales + Instalación',
        LABOR_MATERIALS: 'Mano de obra y materiales',
        DOOR_ITEMS_BY_COST: 'Puertas por Costo',
        COST_PER_DETECTED_DOOR: 'Costo por puerta detectada',
        DOORS_SUBTOTAL: 'Subtotal de Puertas',
        WINDOWS_SUBTOTAL: 'Subtotal de Ventanas',
        INSTALLATION_MATERIALS: 'Materiales de Instalación',
        DOOR_INSTALLATION: 'Instalación de Puertas',
        WINDOW_INSTALLATION: 'Instalación de Ventanas',
        INSTALLATION_SUBTOTAL: 'Subtotal de Instalación',
        TOTAL_ESTIMATED_PROJECT_COST: 'Costo Total Estimado del Proyecto',
        POWERED_BY_TALOS: 'Desarrollado por TALOS',
        ALL_RIGHTS_RESERVED: 'Todos los derechos reservados.',
        ITEM_COUNT: '{{count}} ítem(s)',
        ITEMS_DETECTED: '{{count}} ítem(s) detectados',
        FLOOR_COUNT: '{{count}} piso(s)',
        ROOM_COUNT: '{{count}} habitación(es)',
        AREAS_COUNT: '{{count}} áreas',
        GAL_TOTAL: '{{count}} gal total',
        OF_TOTAL: '{{percent}}% del total',
        OF_TOTAL_SURFACE: '{{percent}}% de la superficie total',
      },
      EMPTY: {
        NO_ROOMS: 'No hay habitaciones asignadas',
        NO_MATERIALS: 'No hay materiales asignados',
        NO_FIXTURE_ROWS: 'No hay filas de fixtures',
        NO_FIXTURE_ROWS_AVAILABLE: 'No hay filas de fixtures disponibles',
        NO_DOOR_ROWS_AVAILABLE: 'No hay filas de puertas disponibles',
        NO_REPORT_DATA: 'No hay datos de reporte disponibles.',
        GO_BACK: 'Volver',
      },
      DISCLAIMER: 'Esta estimación fue generada por IA con base en los datos del plano y precios regionales de materiales. Los costos reales pueden variar según el mercado local, tarifas de contratistas y especificaciones finales del proyecto. Las cifras son solo una guía de planificación y deben ser verificadas por un contratista autorizado.',
      SHORT_DISCLAIMER: 'Todas las medidas y estimaciones de costo son aproximadas, generadas con herramientas de IA. Verifique todas las cifras antes de ejecutar.',
    },
    PROJECT_STEPPER: {
      WORKFLOW: 'Flujo del Proyecto',
      STEPS: {
        DETECTION_SCOPE: 'Alcance de Detección',
        UPLOAD_PLAN: 'Subir Plano',
        ASSIGN_FLOORS: 'Asignar Pisos',
        RESULTS: 'Resultados',
      },
      HEADER: {
        STEP: 'Paso',
        STEP_OF: 'PASO {{step}} DE {{total}}',
      },
      ARIA: {
        DIALOG: 'Paso {{step}} de {{total}}: {{label}} - {{project}}',
        STEP_LIVE: 'Paso {{step}} de {{total}}: {{label}}',
        WORKFLOW_STEPS: 'Pasos del flujo',
        COMPLETE: 'completo',
        CURRENT: 'paso actual',
        UPCOMING: 'próximo',
      },
      LOADING: {
        PROJECT_TITLE: 'Cargando proyecto',
        PROJECT_MESSAGE: 'Buscando resultados existentes...',
        FLOOR_VIEWER_TITLE: 'Cargando visor del plano',
        FLOOR_VIEWER_MESSAGE: 'Preparando el espacio de asignación del PDF.',
        RESULTS_TITLE: 'Cargando resultados',
        RESULTS_MESSAGE: 'Preparando la vista de resultados de detección para este proyecto.',
      },
      NAV: {
        PREVIOUS: 'Paso anterior',
        RESTART_TOUR: 'Mostrar el tour de nuevo',
        CONTINUE: 'Continuar',
        CONTINUE_UPLOAD: 'Continuar a Subida',
        CONTINUE_ASSIGN: 'Continuar a Asignar Pisos',
        CONTINUE_RESULTS: 'Continuar a Resultados',
        FINISH: 'Finalizar',
        SAVING_PRICING: 'Guardando precios...',
        SELECT_CATEGORIES_TOOLTIP: 'Selecciona 1-{{max}} categorías para continuar',
        UPLOAD_TOOLTIP: 'Sube un plano para continuar',
        ASSIGN_AND_SCALE_TOOLTIP: 'Asigna al menos una página y selecciona una escala para continuar',
        ASSIGN_TOOLTIP: 'Asigna al menos una página para continuar',
        SCALE_TOOLTIP: 'Selecciona una escala para continuar',
      },
      CONFIRM: {
        CLOSE_DIRTY: 'Tienes ediciones de plano sin guardar. ¿Cerrar y descartarlas?',
        BACK_DIRTY: 'Tienes ediciones sin guardar. ¿Volver y descartarlas?',
        SCALE_TITLE: 'Escala no asignada',
        SCALE_MESSAGE: 'Cambiaste la escala pero no hiciste clic en Asignar. La nueva escala no se guardará.',
        CONTINUE_ANYWAY: 'Continuar de todos modos',
        GO_BACK: 'Volver',
      },
      TOAST: {
        SAVE_FAILED_TITLE: 'Error al guardar',
        SAVE_SCOPE_FAILED_MESSAGE: 'No se pudo guardar el alcance de detección.',
        PRICING_SAVE_FAILED_TITLE: 'Error al guardar precios',
        PRICING_SAVE_FAILED_MESSAGE: 'No se pudieron guardar los precios de materiales. Inténtalo de nuevo.',
      },
      ERRORS: {
        MAX_CATEGORIES: 'Selecciona hasta {{max}} categorías de detección.',
        LOAD_SCOPE: 'No se pudo cargar el alcance de detección.',
        SAVE_SCOPE: 'No se pudo guardar el alcance de detección.',
        NO_PROJECT: 'No hay proyecto seleccionado.',
        NO_PROJECT_RESTART: 'No hay proyecto seleccionado. Reinicia el flujo.',
        TOO_MANY_CATEGORIES: 'Hay demasiadas categorías seleccionadas.',
      },
      CATEGORIES: {
        FLOORING: { LABEL: 'Pisos', DESCRIPTION: 'Detecta áreas de piso y polígonos de habitaciones para el cómputo.' },
        DRYWALL: { LABEL: 'Drywall', DESCRIPTION: 'Mide muros y perímetros de habitaciones para estimaciones de drywall.' },
        FIXTURES: { LABEL: 'Accesorios de Plomería', DESCRIPTION: 'Identifica lavabos, inodoros, bañeras y accesorios relacionados.' },
        DOORS_WINDOWS: { LABEL: 'Puertas y Ventanas', DESCRIPTION: 'Detecta aberturas de puertas y ventanas en los planos asignados.' },
        INTERIOR_PAINT: { LABEL: 'Pintura Interior', DESCRIPTION: 'Estima superficies de pintura interior por habitación y área de muro.' },
        HVAC: { LABEL: 'HVAC', DESCRIPTION: 'Mide ductos y elementos HVAC desde los planos.' },
        COLUMNS: { LABEL: 'Columnas', DESCRIPTION: 'Detecta columnas estructurales y sus dimensiones medidas.' },
        STEEL_BEAMS: { LABEL: 'Vigas de Acero', DESCRIPTION: 'Detecta vigas de acero y mediciones agrupadas.' },
      },
      STEP1: {
        TITLE: 'Seleccionar Alcance de Detección.',
        DESCRIPTION: 'Elige hasta dos elementos arquitectónicos para que la IA los detecte en tus planos.',
        LOADING_TITLE: 'Cargando alcance de detección',
        LOADING_MESSAGE: 'Obteniendo las categorías disponibles para este proyecto.',
        SELECT_UP_TO: 'Selecciona hasta {{max}} categorías.',
      },
      STEP2: {
        TITLE: 'Subir Plano.',
        DESCRIPTION: 'Sube el PDF del plano; la IA analizará cada página que asignes.',
        CHECKING_TITLE: 'Revisando subidas existentes',
        CHECKING_MESSAGE: 'Buscando planos ya adjuntos a este proyecto.',
        UPLOADED_DOCUMENTS: 'Documentos subidos',
        FILE_COUNT_ONE: '{{count}} archivo',
        FILE_COUNT_MANY: '{{count}} archivos',
        UPLOADED_AT: 'Subido {{date}}',
        DELETE_DOCUMENT: 'Eliminar documento',
        UPLOAD_NEW_VERSION: 'Subir una nueva versión',
        DROPZONE_TITLE: 'Arrastra y suelta tu plano aquí',
        BROWSE_HINT: 'o haz clic para buscar archivos',
        BROWSE_FILES: 'Buscar Archivos',
        PDF_ONLY: 'Solo PDF',
        UPLOADING: 'Subiendo...',
        UPLOADED_SUCCESS: 'Subido correctamente; puedes continuar',
        CHANGE_FILE: 'Cambiar archivo',
        ERRORS: {
          UNSUPPORTED_TYPE: 'Tipo de archivo no compatible. Sube un PDF.',
          INVALID_PDF: 'Este archivo no parece ser un PDF válido. Verifica el archivo e inténtalo de nuevo.',
          FILE_TOO_LARGE: 'El archivo es demasiado grande. Sube un PDF de menos de {{maxSizeMb}} MB.',
          UPLOAD_FAILED: 'Error al subir. Inténtalo de nuevo.',
        },
        TOAST: {
          DELETE_SUCCESS_TITLE: 'Documento eliminado',
          DELETE_SUCCESS_MESSAGE: 'El plano fue eliminado.',
          DELETE_FAILED_TITLE: 'Error al eliminar',
          DELETE_FAILED_MESSAGE: 'No se pudo eliminar el documento. Inténtalo de nuevo.',
          UPLOAD_SUCCESS_TITLE: 'Subida exitosa',
          UPLOAD_SUCCESS_MESSAGE: 'Plano subido correctamente.',
          UPLOAD_FAILED_TITLE: 'Error al subir',
          UPLOAD_FAILED_MESSAGE: 'No se pudo subir el archivo. Inténtalo de nuevo.',
        },
      },
      STEP3: {
        EDIT_PAGE_TITLE: 'Haz clic para editar el número de página',
        FIT: 'Ajustar',
        FIT_TITLE: 'Ajustar página a la pantalla',
        CRITICAL_TITLE: 'Instrucción Crítica',
        CRITICAL_MESSAGE_PREFIX: 'Selecciona solo páginas que contengan',
        ONE_FLOOR_PLAN: 'UN plano de piso',
        CRITICAL_MESSAGE_SUFFIX: 'Evita páginas con varios diseños, secciones o dibujos combinados.',
        DISMISS_NOTICE: 'Descartar aviso',
        NO_PDF: 'No hay PDF cargado. Sube un plano en el paso anterior.',
        CAL_FIRST_POINT: 'Haz clic en el primer punto de una dimensión conocida.',
        CAL_SECOND_POINT: 'Haz clic en el segundo punto para definir la escala.',
        SCALE_UPDATED: 'Escala actualizada correctamente.',
        CALIBRATE_SCALE: 'Calibrar Escala',
        PAGE_LABEL: 'Página {{page}}',
        DRAWN_LINE: 'Línea Dibujada',
        PDF_PT: 'pt PDF',
        EQUALS_REAL_WORLD: 'equivale en el mundo real',
        FEET_LABEL: "Pies (ft') *",
        INCHES_LABEL: 'Pulgadas (in")',
        SCALE: 'Escala',
        RECALIBRATE: 'Recalibrar',
        APPLY_SCALE: 'Aplicar Escala',
        ENTER_REAL_LENGTH: 'Ingresa longitud real',
        NOT_ASSIGNED: 'No Asignado',
        ASSIGNMENT_PANEL: 'PANEL DE ASIGNACIÓN',
        FLOOR_ASSIGNMENT: 'Asignación de Piso',
        ASSIGN_EACH_PAGE: 'Asigna cada página a un nivel',
        ASSIGNED: 'Asignado',
        UNASSIGNED: 'Sin asignar',
        ASSIGN_FLOOR: 'Asignar Piso',
        STANDARD: 'Estándar',
        CALIBRATE: 'Calibrar',
        LOADING_SCALES: 'Cargando escalas...',
        SELECT_SCALE: 'Seleccionar escala...',
        NO_SCALES: 'No hay escalas disponibles',
        REDO_CALIBRATION: 'Rehacer calibración',
        CLEAR_CALIBRATED_SCALE: 'Limpiar escala calibrada',
        SCALE_REQUIRED: 'Selecciona o calibra una escala antes de asignar.',
        FLOOR: 'Piso',
        SELECT_FLOOR: 'Seleccionar piso...',
        ALL_FLOORS_ASSIGNED: 'Todos los pisos ya están asignados',
        ASSIGN: 'Asignar',
        ASSIGN_NEXT: 'Asignar y Siguiente',
        ASSIGNED_FLOORS: 'PISOS ASIGNADOS',
        NO_FLOORS_ASSIGNED: 'Aún no hay pisos asignados.',
        SELECT_AND_ASSIGN: 'Selecciona un piso y haz clic en Asignar.',
        PAGE_SHORT: 'pág',
        CHANGE_FLOOR: 'Cambiar piso...',
        VIEWING_NOW: 'Viendo ahora',
        EDIT_FLOOR: 'Editar piso',
        REMOVE: 'Quitar',
        TOAST: {
          INVALID_SCALE_TITLE: 'Escala inválida',
          INVALID_SCALE_MESSAGE: 'Ingresa la longitud real de la línea medida.',
          SUBMISSION_FAILED_TITLE: 'Error de envío',
          SUBMISSION_FAILED_MESSAGE: 'No se pudo enviar esta página. Inténtalo de nuevo.',
          DELETE_FAILED_TITLE: 'Error al eliminar',
          DELETE_FAILED_MESSAGE: 'No se pudo quitar esta asignación. Inténtalo de nuevo.',
          UPDATE_FAILED_TITLE: 'Error al actualizar',
          UPDATE_FAILED_MESSAGE: 'No se pudo actualizar esta asignación. Inténtalo de nuevo.',
        },
      },
      STEP4: {
        FIT: 'Ajustar',
        UPDATING_ZOOM: 'Actualizando zoom',
        LOADING_FLOOR_PLAN: 'Cargando plano',
        RENDERING_VIEW: 'Renderizando vista {{zoom}}',
        PLEASE_WAIT: 'Espera por favor',
        REGENERATING_COORDINATES: 'Regenerando coordenadas',
        REGENERATE_DETECTION: 'Regenerar detección',
        REGENERATING: 'Regenerando...',
        REGENERATE: 'Regenerar',
        MATERIALS_PRICING: 'Materiales y Precios',
        PRICING: 'Precios',
        VIEW_HTML_REPORT: 'Ver reporte HTML',
        VIEW: 'Ver',
        AI_DETECTION_RUNNING: 'DETECCIÓN IA EN CURSO',
        ANALYZING_TITLE: 'Analizando Planos',
        ANALYZING_MESSAGE: 'Los resultados aparecerán a medida que complete cada categoría. Puedes monitorear el progreso abajo.',
        DONE: 'Listo',
        FAILED: 'Falló',
        DETECTING: 'Detectando {{category}}...',
        PERCENT_COMPLETE: '{{percent}}% completo',
        DETECTION_FAILED: 'Falló la detección',
        NO_ELEMENTS: 'No se detectaron elementos',
        NO_ELEMENTS_MESSAGE: 'La IA no encontró elementos de {{category}}.',
        NO_FLOOR_PLAN: 'No hay plano disponible.',
        DOOR: 'puerta',
        WINDOW: 'ventana',
        DRAG_DRAW_DW: 'Arrastra para dibujar un rectángulo de {{type}}',
        ZOOM_OUT: 'Alejar',
        ZOOM_IN: 'Acercar',
        FIT_TO_SCREEN: 'Ajustar a pantalla',
        DRAG_RECTANGLE: 'Arrastra para dibujar un rectángulo',
        DRAG_ELLIPSE: 'Arrastra para dibujar una elipse',
        CLICK_FIRST_VERTEX: 'Haz clic para colocar el primer vértice',
        POINTS_CLOSE_HINT: 'puntos - doble clic o clic en el primer punto para cerrar',
        SELECTED: 'Seleccionado: {{name}}',
        EDIT_POLYGON_POINTS: 'Editar puntos del polígono',
        EDIT: 'Editar',
        DELETE_THIS_ITEM: 'Eliminar este elemento',
        DELETE: 'Eliminar',
        DESELECT: 'Deseleccionar',
        WIDTH: 'Ancho',
        THICKNESS: 'Espesor',
        NO_SPEC_DATA: 'No hay datos de especificación disponibles.',
        SELECT_ITEM: 'Selecciona un elemento',
        SELECT_ITEM_MESSAGE: 'Haz clic en una puerta o ventana del plano para ver sus detalles.',
        BEAM: 'Viga',
        COLUMN: 'Columna',
        GROUPING: 'Agrupar',
        ERRORS: {
          REGENERATE_FAILED: 'No se pudieron regenerar las coordenadas. Inténtalo de nuevo.',
          COMING_SOON: '{{category}} estará disponible pronto.',
          PROCESS_FAILED: 'No se pudieron procesar los planos. Inténtalo de nuevo.',
          PROCESSING_ERROR: 'Ocurrió un error durante el procesamiento con IA.',
        },
        POINTS_NEED_MORE: '{{count}} {{plural}} — faltan {{more}} más',
        ARIA: {
          OVERLAY_LABEL: 'Plano interactivo de {{category}} con {{count}} elementos detectados. Presiona Tab para moverte entre los elementos, Enter o Espacio para seleccionar.',
        },
        DW_PANEL: {
          COMPLETED: 'Completado',
          ITEMS_DETECTED: '{{count}} elementos detectados',
          SUMMARY: 'Resumen',
          DOORS: 'Puertas',
          WINDOWS: 'Ventanas',
          FLOORS: 'Pisos',
          EDITING: 'Editando:',
          DRAWING_NEW_DOOR: 'Dibujando Nueva Puerta',
          DRAWING_NEW_WINDOW: 'Dibujando Nueva Ventana',
          NAME_YOUR_DOOR: 'Nombra tu Puerta',
          NAME_YOUR_WINDOW: 'Nombra tu Ventana',
          DRAG_DRAW_RECT: 'Arrastra en el plano para dibujar un rectángulo.',
          DOOR_NAME: 'Nombre de la Puerta',
          WINDOW_NAME: 'Nombre de la Ventana',
          CANCEL: 'Cancelar',
          SAVE_DOOR: 'Guardar Puerta',
          SAVE_WINDOW: 'Guardar Ventana',
          ADD_DOOR: 'Agregar Puerta',
          ADD_WINDOW: 'Agregar Ventana',
          DETECTION: 'Detección',
          CHANGE_COLOR_HINT: 'Haz clic en la muestra para cambiar el color de superposición de detección',
          COLOR_TIP: 'Consejo: haz clic aquí para elegir un color',
        },
        EDIT_TOOLBAR: {
          TITLE: 'Editar Puntos',
          UNSAVED: 'Cambios sin guardar',
          UNDO: 'Deshacer',
          UNDO_TITLE: 'Deshacer (Ctrl+Z)',
          REDO: 'Rehacer',
          REDO_TITLE: 'Rehacer (Ctrl+Y)',
          CANCEL: 'Cancelar',
          CANCEL_TITLE: 'Cancelar (Esc)',
          DELETE: 'Eliminar',
          DELETE_ROOM: 'Eliminar esta habitación',
          SAVE: 'Guardar',
          SAVING: 'Guardando…',
          SQ_FT: 'ft²',
          PTS: 'pts',
          SELF_CROSSING: 'Auto-cruzado',
          SHORTCUTS_TITLE: 'Atajos de teclado',
          EDIT_SHORTCUTS: 'Atajos de Edición',
          DRAG_VERTEX: 'Arrastrar vértice',
          MOVE_CORNER: 'Mover esquina',
          CLICK_ON_EDGE: 'Clic ○ en borde',
          ADD_VERTEX: 'Agregar vértice',
          RIGHT_CLICK_VERTEX: 'Clic derecho en vértice',
          DELETE_VERTEX: 'Eliminar vértice',
          UNDO_KEY: 'Deshacer',
          REDO_KEY: 'Rehacer',
          CANCEL_KEY: 'Cancelar (confirma)',
          DRAG_HINT: 'Arrastra · Clic ○ para agregar · Clic derecho para eliminar',
        },
        ROOM_DETAIL: {
          FLOORING_DETECTION: 'Detección de Pisos',
          DRYWALL_DETECTION: 'Detección de Drywall',
          PAINT_DETECTION: 'Detección de Pintura',
          PLUMBING_DETECTION: 'Detección de Plomería',
          HVAC_DETECTION: 'Detección HVAC',
          COLUMN_DETECTION: 'Detección de Columnas',
          BEAM_DETECTION: 'Detección de Vigas',
          DETECTION_DETAILS: 'Detalles de Detección',
          COMPLETED: 'Completado',
          PROCESSING: 'Procesando…',
          FAILED: 'Fallido',
          SUMMARY: 'Resumen',
          ROOMS: 'Habitaciones',
          FIXTURES: 'Accesorios',
          DUCTS: 'Conductos',
          COLUMNS: 'Columnas',
          BEAMS: 'Vigas',
          FLOORS: 'Pisos',
          TOTAL_LENGTH: 'Longitud Total',
          TOTAL_FLOOR_AREA: 'Área Total de Piso',
          TOTAL_CEILING_AREA: 'Área Total de Techo',
          TOTAL_FIXTURES: 'Total de Accesorios',
          TOTAL_AREA: 'Área Total',
          TOTAL_PERIMETER: 'Perímetro Total',
          TOTAL_WALL_PERIMETER: 'Perímetro Total de Pared',
          TOTAL_WALL_LENGTH: 'Longitud Total de Pared',
          FIXTURE_COUNT: 'Cantidad de Accesorios',
          TOTAL_WALL_AREA: 'Área Total de Paredes Interiores',
          TOTAL_CORNERS: 'Total de Esquinas',
          ADD_ROOM: 'Agregar Nueva Habitación',
          ADD_FIXTURE: 'Agregar Nuevo Accesorio',
          ADD_DUCT: 'Agregar Nuevo Conducto',
          ADD_COLUMN: 'Agregar Nueva Columna',
          ADD_BEAM: 'Agregar Nueva Viga',
          DRAWING_NEW_FIXTURE: 'Dibujando Nuevo Accesorio',
          NAME_YOUR_FIXTURE: 'Nombra tu Accesorio',
          PIPE_READY: 'Tubería Lista — Guardar para confirmar',
          ADD_NEW_PIPE: 'Agregar Nueva Tubería',
          NAME_YOUR_ROOM: 'Nombra tu Habitación',
          DRAWING_NEW_ROOM: 'Dibujando Nueva Habitación',
          CANCEL_ESC: 'Cancelar (Esc)',
          PIPE_THICKNESS: 'Grosor de Tubería',
          THIN: 'Delgado',
          MEDIUM: 'Mediano',
          THICK: 'Grueso',
          WIDE: 'Ancho',
          PIPE_START: 'Haz clic en el inicio de la tubería en el plano.',
          PIPE_END: 'Haz clic en el final de la tubería para terminar.',
          POLYGON: 'Polígono',
          RECT: 'Rect',
          ELLIPSE: 'Elipse',
          DRAW_POLYGON_TITLE: 'Dibujar polígono — clic para colocar puntos',
          DRAW_RECT_TITLE: 'Dibujar rectángulo — arrastra para dibujar',
          DRAW_ELLIPSE_TITLE: 'Dibujar elipse — arrastra para dibujar',
          POLYGON_HINT1: 'Haz clic en el plano para colocar vértices.',
          POLYGON_HINT2: 'Doble clic o clic en el primer punto ◎ para cerrar.',
          VERTICES_PLACED: 'Vértices colocados',
          DRAG_RECT: 'Arrastra en el plano para dibujar un rectángulo.',
          DRAG_ELLIPSE: 'Arrastra en el plano para dibujar una elipse.',
          ASSOCIATED_ROOM: 'Habitación Asociada',
          NOT_DETECTED: 'No detectado aún',
          PIPE_NAME: 'Nombre de Tubería',
          FIXTURE_TYPE_LABEL: 'Tipo de Accesorio',
          ROOM_NAME_LABEL: 'Nombre de Habitación',
          NO_FIXTURES: 'No hay accesorios configurados',
          SELECT_FIXTURE: 'Seleccionar accesorio',
          ROOM_NAME_PH: 'ej. Sala de Estar',
          OUTSIDE_ROOMS: 'Fuera de habitaciones detectadas',
          VERTICES: 'Vértices',
          CANCEL: 'Cancelar',
          SAVE_DUCT: 'Guardar Conducto',
          SAVE_FIXTURE: 'Guardar Accesorio',
          SAVE_ROOM: 'Guardar Habitación',
          EDITING: 'Editando:',
          LIVE: 'En vivo',
          SELECTED: 'Seleccionado: {{name}}',
          EDIT_POINTS_TITLE: 'Editar puntos del polígono',
          EDIT: 'Editar',
          DELETE_FIXTURE: 'Eliminar este accesorio',
          DELETE_ROOM_BTN: 'Eliminar esta habitación',
          DELETE: 'Eliminar',
          DESELECT: 'Deseleccionar',
          DRAG_FIXTURE_HINT: 'Arrastra el polígono del accesorio para actualizar la habitación.',
          FIXTURE_TYPE: 'Tipo de Accesorio',
          ASSOC_ROOM: 'Habitación Asociada',
          UNASSIGNED: 'Sin asignar',
          FLOOR_AREA: 'Área del Piso',
          PERIMETER: 'Perímetro',
          FLOORING_TYPE: 'Tipo de Piso',
          CEILING_AREA: 'Área del Techo',
          WALL_PERIMETER: 'Perímetro de Pared',
          ROOM_HEIGHT: 'Altura de Habitación',
          CORNER_POINTS: 'Puntos de Esquina',
          WALL_LENGTH: 'Longitud de Pared',
          SAVE_HEIGHT_TITLE: 'Guardar altura de habitación',
          EDIT_HEIGHT_TITLE: 'Editar altura de habitación',
          SAVE: 'Guardar',
          CANCEL_HEIGHT: 'Cancelar',
          LENGTH: 'Longitud',
          WIDTH: 'Ancho',
          TYPE: 'Tipo',
          BEAM_TYPE: 'Tipo de Viga',
          THICKNESS: 'Grosor',
          AREA: 'Área',
          CORNERS: 'Esquinas',
          COL_GROUP: 'Grupo de Columnas',
          BEAM_GROUP: 'Grupo de Vigas',
          TOTAL_LEN: 'Longitud Total',
          COLS_IN_GROUP: 'Columnas en el grupo',
          BEAMS_IN_GROUP: 'Vigas en el grupo',
          NO_ROOM_SELECTED: 'Ninguna habitación seleccionada',
          NO_FIXTURE_SELECTED: 'Ningún accesorio seleccionado',
          NO_COLUMN_SELECTED: 'Ninguna columna seleccionada',
          NO_BEAM_SELECTED: 'Ninguna viga seleccionada',
          CLICK_ROOM_HINT: 'Haz clic en una habitación del plano para ver los detalles.',
          CLICK_FIXTURE_HINT: 'Haz clic en un accesorio del plano para ver los detalles.',
          CLICK_COLUMN_HINT: 'Clic izquierdo en una columna para ver los detalles.',
          CLICK_COLUMN_RIGHT: 'Clic derecho para seleccionar todo el grupo.',
          CLICK_BEAM_HINT: 'Clic izquierdo en una viga para ver los detalles.',
          CLICK_BEAM_RIGHT: 'Clic derecho para seleccionar todo el grupo.',
          INVALID_HEIGHT_TITLE: 'Altura inválida',
          INVALID_HEIGHT_MESSAGE: 'La altura de habitación debe estar entre 4 y 15 ft.',
        },
        SUMMARY: {
          FIXTURE_S: 'accesorio detectado',
          FIXTURE_P: 'accesorios detectados',
          AREA_S: 'área detectada',
          AREA_P: 'áreas detectadas',
          NO_FIXTURES: 'No se detectaron accesorios para este piso.',
          NO_AREAS: 'No se detectaron áreas para este piso.',
          FIXTURE_TYPE: 'Tipo de Accesorio',
          COUNT: 'Cantidad',
          AREA_NAME: 'Nombre del Área',
          AREA_SQ_FT: 'Área (ft²)',
          PERIMETER_FT: 'Perímetro (ft)',
          CORNERS: 'Esquinas',
        },
      },
    },
    AUTH: {
      ADMIN_LOGIN: {
        CONSOLE: 'Consola Admin',
        HERO_LINE_1: 'Opera el',
        HERO_LINE_2: 'Workspace.',
        HERO_DESCRIPTION: 'Una consola para administrar builders, suscripciones, proyectos, configuraciones de alcance y soporte.',
        FEATURE_BUILDERS: 'Builders',
        FEATURE_ACCOUNTS: 'Cuentas',
        FEATURE_PLANS: 'Planes',
        FEATURE_SUBSCRIPTIONS: 'Suscripciones',
        FEATURE_SCOPES: 'Alcances',
        FEATURE_SETTINGS: 'Configuración',
        BULLET_BUILDERS: 'Revisa y administra todas las cuentas builder',
        BULLET_SUBSCRIPTIONS: 'Mantén suscripciones y registros de proyectos',
        BULLET_SETTINGS: 'Configura materiales de alcance, precios y recursos de soporte',
        MONITORED: 'El acceso admin se monitorea y registra · TALOS Takeoffs',
        ADMIN: 'Admin',
        PORTAL: 'Portal Admin',
        TITLE: 'Iniciar sesión.',
        SUBTITLE: 'Solo personal autorizado',
        EMAIL: 'Correo electrónico',
        EMAIL_ERROR: 'Ingresa un correo electrónico válido.',
        PASSWORD: 'Contraseña',
        PASSWORD_PLACEHOLDER: 'Ingresa tu contraseña',
        PASSWORD_ERROR: 'La contraseña es obligatoria.',
        SIGN_IN_BUTTON: 'Entrar al Portal Admin',
        SIGNING_IN: 'Iniciando sesión...',
        SECURE_NOTE: 'Cifrado de 256 bits · Acceso seguro a TALOS',
      },
      BUILDER: {
        COMMON: {
          TAKEOFFS: 'TAKEOFFS',
          EMAIL: 'Correo Electrónico',
          EMAIL_ERROR: 'Ingresa un correo electrónico válido.',
          PASSWORD: 'Contraseña',
          PASSWORD_PLACEHOLDER: 'Ingresa tu contraseña',
          PASSWORD_REQUIRED: 'La contraseña es obligatoria.',
          SECURE_ACCESS: 'Cifrado de 256 bits · Acceso seguro a TALOS',
          BUILT_AROUND: 'Construido alrededor del flujo de takeoff de TALOS.',
          BACK_TO_LOGIN: 'Volver al Login',
          NEED_HELP: '¿Necesitas ayuda?',
          CONTACT_SUPPORT: 'Contactar Soporte',
          OTP_PLACEHOLDER: 'Ingresa el código de 6 dígitos',
        },
        LOGIN: {
          PANEL_EYEBROW: 'Workspace Builder',
          PANEL_TITLE_1: 'De Planos',
          PANEL_TITLE_2: 'a Reportes',
          PANEL_TITLE_3: 'de Precios.',
          PANEL_DESC: 'Sube PDFs de planos, elige alcances por oficio, revisa cantidades detectadas y exporta reportes de precios desde un solo workspace.',
          FEATURE_PLANS: 'Planos',
          FEATURE_UPLOADS: 'Subidas',
          FEATURE_SCOPES: 'Alcances',
          FEATURE_QUANTITIES: 'Cantidades',
          FEATURE_REPORTS: 'Reportes',
          FEATURE_EXPORTS: 'Exportaciones',
          BULLET_PROJECTS: 'Crea proyectos y sube hojas de planos',
          BULLET_REVIEW: 'Revisa habitaciones, alcances y cantidades detectadas por IA',
          BULLET_PRICING: 'Ajusta precios antes de exportar reportes',
          BADGE: 'Portal Builder',
          TITLE: 'Bienvenido de nuevo.',
          SUBTITLE: 'Inicia sesión en tu workspace de proyectos',
          REMEMBER_ME: 'Recordarme',
          FORGOT_PASSWORD: '¿Olvidaste tu contraseña?',
          SIGN_IN_BUTTON: 'Iniciar sesión en tu cuenta',
          SIGNING_IN: 'Iniciando sesión...',
          NO_ACCOUNT: '¿No tienes una cuenta?',
          SIGN_UP_FREE: 'Regístrate gratis',
          SIGN_IN_SHORT: 'Iniciar sesión',
        },
        SIGNUP: {
          LOGO_TAG: 'BUILDER_SIGNUP',
          PANEL_EYEBROW: 'INICIALIZAR WORKSPACE',
          PANEL_TITLE_1: 'Estimaciones más rápidas',
          PANEL_TITLE_2: 'empiezan aquí.',
          PANEL_DESC: 'Cuéntanos sobre tu oficio y flujo de trabajo. TALOS configura tu workspace de estimación según tus respuestas.',
          FEATURE_PROFILE: 'Perfil Builder',
          FEATURE_PROFILE_HINT: 'Oficio, empresa y datos de contacto',
          FEATURE_WORKFLOW: 'Configuración del Flujo',
          FEATURE_WORKFLOW_HINT: 'Tipos de proyecto y volumen de estimaciones',
          FEATURE_SECURE: 'Acceso Seguro',
          FEATURE_SECURE_HINT: 'Credenciales cifradas de 256 bits',
          FOOT_LABEL: 'SYSTEM_READY - TALOS IA CONSTRUCCIÓN',
          BADGE: 'Registro Builder',
          TITLE: 'Crea tu workspace.',
          SUBTITLE: 'Completa las secciones para activar tu cuenta builder de TALOS.',
          SECTIONS: {
            ACCOUNT_TITLE: 'Detalles de Cuenta',
            ACCOUNT_HINT: 'Nombre, contacto e información de empresa',
            SECURITY_TITLE: 'Seguridad',
            SECURITY_HINT: 'Crea una contraseña fuerte para tu cuenta',
            WORK_TITLE: 'Tu Trabajo',
            WORK_HINT: 'Cuéntanos sobre tus proyectos y volumen',
            CHALLENGES_TITLE: 'Tus Desafíos',
            CHALLENGES_HINT: 'Ayúdanos a optimizar tu flujo',
          },
          FIELDS: {
            FULL_NAME: 'Nombre Completo *',
            FULL_NAME_PLACEHOLDER: 'Ingresa tu nombre completo',
            WORK_EMAIL: 'Correo Laboral *',
            PHONE: 'Número de Teléfono *',
            COMPANY: 'Nombre de Empresa *',
            COMPANY_PLACEHOLDER: 'Ingresa el nombre de tu empresa',
            ADDRESS: 'Dirección *',
            ADDRESS_PLACEHOLDER: 'Ingresa tu dirección',
            TRADE: 'Oficio / Especialidad *',
            TRADE_PLACEHOLDER: 'Selecciona tu oficio',
            PASSWORD: 'Contraseña *',
            PASSWORD_PLACEHOLDER: 'Ingresa una contraseña',
            CONFIRM_PASSWORD: 'Confirmar Contraseña *',
            CONFIRM_PLACEHOLDER: 'Vuelve a ingresar tu contraseña',
            WORK_TYPE: '¿Qué tipo de trabajo haces?',
            PROJECT_TYPE: '¿Qué tipo de proyectos sueles realizar? *',
            ESTIMATES: '¿Cuántos proyectos estimas cada mes?',
            CHALLENGES: '¿Qué te retrasa más?',
            OTHER: '¿Algo más que quieras compartir?',
            OTHER_PLACEHOLDER: 'Cuéntanos sobre otros desafíos...',
            OPTIONAL: 'Opcional',
          },
          ERRORS: {
            FULL_NAME: 'El nombre completo es obligatorio.',
            PHONE: 'Ingresa un número telefónico válido de 10 dígitos de EE. UU.',
            COMPANY: 'El nombre de empresa es obligatorio.',
            ADDRESS: 'La dirección es obligatoria.',
            TRADE: 'El oficio es obligatorio.',
            PASSWORD: 'La contraseña no cumple todos los requisitos.',
            CONFIRM: 'Las contraseñas no coinciden.',
            WORK_TYPE: 'Selecciona al menos un tipo de trabajo.',
            PROJECT_TYPE: 'Selecciona un tipo de proyecto.',
            ESTIMATES: 'Selecciona un rango de estimaciones.',
            CHALLENGES: 'Selecciona al menos un desafío.',
            GENERIC: 'Algo salió mal. Inténtalo de nuevo.',
          },
          PASSWORD_RULES: {
            MIN: 'Al menos 8 caracteres',
            UPPER: 'Una letra mayúscula',
            LOWER: 'Una letra minúscula',
            NUMBER: 'Un número',
            SPECIAL: 'Un carácter especial',
          },
          OPTIONS: {
            TRADES: {
              GENERAL_CONTRACTOR: 'Contratista General',
              SUBCONTRACTOR: 'Subcontratista',
              ESTIMATOR: 'Estimador',
              ARCHITECT: 'Arquitecto',
              ENGINEER: 'Ingeniero',
              DEVELOPER: 'Desarrollador',
              HOME_OWNER: 'Propietario',
            },
            WORK_TYPES: {
              FLOORING: 'Pisos',
              DRYWALL: 'Drywall',
              FRAMING: 'Estructura',
              PAINTING: 'Pintura',
              ELECTRICAL: 'Eléctrico',
              PLUMBING: 'Plomería',
              GENERAL_CONTRACTOR: 'Contratista General',
              OTHER: 'Otro',
            },
            PROJECT_TYPES: {
              RESIDENTIAL: 'Residencial',
              COMMERCIAL: 'Comercial',
              BOTH: 'Ambos',
            },
            CHALLENGES: {
              TAKEOFFS_LONG: 'Los takeoffs tardan demasiado',
              MANUAL_WORK: 'Demasiado trabajo manual',
              MISSING_BIDS: 'Pierdo licitaciones por falta de tiempo',
              ERRORS: 'Errores en estimaciones',
              SCALE: 'Difícil escalar estimaciones',
            },
          },
          CREATE_ACCOUNT: 'Crear mi cuenta',
          HAVE_ACCOUNT: '¿Ya tienes una cuenta?',
        },
        FORGOT: {
          PANEL_EYEBROW: 'Recuperación de Cuenta',
          PANEL_TITLE_1: 'Verifica tu',
          PANEL_TITLE_2: 'correo antes de',
          PANEL_TITLE_3: 'restablecer.',
          PANEL_DESC: 'Enviamos un código de un solo uso a tu correo registrado para mantener la solicitud ligada a tu cuenta.',
          BULLET_EMAIL: 'Solicita un código con tu correo laboral',
          BULLET_OTP: 'Usa el OTP de tu bandeja para continuar',
          BULLET_RESET: 'Restablece el acceso sin exponer tu contraseña anterior',
          PANEL_FOOTER: 'Recuperación segura de cuenta TALOS.',
          BADGE: 'Recuperación',
          TITLE: '¿Olvidaste tu contraseña?',
          SUBTITLE: 'Ingresa tu correo y enviaremos un código de un solo uso.',
          EMAIL_PLACEHOLDER: 'Ingresa tu correo laboral',
          SEND_OTP: 'Enviar OTP',
          SENDING: 'Enviando...',
          CHECK_INBOX: 'Revisa tu bandeja.',
          SENT_PREFIX: 'Enviamos un OTP de 6 dígitos a',
          ENTER_OTP: 'Ingresar OTP y Restablecer Contraseña',
          ERROR_SEND: 'No se pudo enviar el OTP. Inténtalo de nuevo.',
        },
        RESET: {
          PANEL_EYEBROW: 'Restablecer Credenciales',
          PANEL_TITLE_1: 'Confirma el OTP,',
          PANEL_TITLE_2: 'define una',
          PANEL_TITLE_3: 'contraseña segura.',
          PANEL_DESC: 'El flujo verifica tu código, revisa la nueva credencial y actualiza el acceso en un paso seguro.',
          BULLET_VALIDATE: 'Valida el OTP enviado a tu correo',
          BULLET_STRONGER: 'Crea una contraseña más fuerte antes de enviar',
          BULLET_RETURN: 'Vuelve al login después de actualizar la credencial',
          PANEL_FOOTER: 'Actualización segura de credenciales TALOS.',
          BADGE: 'Restablecer Contraseña',
          TITLE: 'Restablecer contraseña.',
          SUBTITLE: 'Ingresa el OTP enviado a tu correo y elige una nueva contraseña.',
          OTP_SENT_TO: 'OTP enviado a',
          OTP_LABEL: 'Contraseña de Un Solo Uso (OTP) *',
          OTP_ERROR: 'Ingresa el OTP de 6 dígitos de tu correo.',
          NEW_PASSWORD: 'Nueva Contraseña *',
          NEW_PASSWORD_PLACEHOLDER: 'Ingresa nueva contraseña',
          PASSWORD_ERROR: 'La contraseña debe cumplir todos los requisitos.',
          REQUIREMENTS: 'Requisitos de contraseña',
          RULE_UPPER: 'Al menos 1 letra mayúscula',
          RULE_NUMBER: 'Al menos 1 número',
          RULE_SPECIAL: 'Al menos 1 carácter especial',
          CONFIRM_PASSWORD: 'Confirmar Nueva Contraseña *',
          CONFIRM_PLACEHOLDER: 'Repite la nueva contraseña',
          RESET_BUTTON: 'Restablecer Contraseña',
          RESETTING: 'Restableciendo...',
          ERROR_INVALID: 'OTP inválido o expirado. Inténtalo de nuevo.',
        },
        VERIFY: {
          TITLE: 'Verifica tu correo',
          SUBTITLE: 'Enviamos un código de 6 dígitos a tu correo. Ingrésalo abajo para confirmar tu cuenta.',
          CODE_LABEL: 'Código de Verificación *',
          CODE_ERROR: 'Ingresa el código de 6 dígitos.',
          VERIFY_BUTTON: 'Verificar Correo',
          SENDING: 'Enviando...',
          RESEND_IN: 'Reenviar código en {{seconds}}s',
          RESEND: '¿No recibiste el código? Reenviar',
          SECURE_NOTE: 'Tus datos están protegidos con cifrado de 256 bits',
          SUCCESS_TITLE: '¡Correo verificado!',
          SUCCESS_MESSAGE: 'Tu cuenta ha sido confirmada. Ya puedes iniciar sesión.',
          GO_LOGIN: 'Ir al Login',
          TOAST_RESENT_TITLE: 'Código reenviado',
          TOAST_RESENT_MESSAGE: 'Se envió un nuevo código de verificación a tu correo.',
          TOAST_FAILED_TITLE: 'Error al reenviar',
          TOAST_FAILED_MESSAGE: 'No se pudo reenviar el código. Inténtalo de nuevo.',
          ERROR_INVALID: 'Código inválido o expirado. Inténtalo de nuevo.',
        },
      },
    },
    LANDING: {
      LOGO_SUB: 'ia de construcción',
      HERO: {
        EYEBROW: 'Plataforma de Takeoff con IA',
        HEADLINE_LINE_1: 'Planos dentro.',
        HEADLINE_LINE_2: 'Reportes fuera.',
        HEADLINE_MUTED: 'Sin escalado manual.',
        DESCRIPTION: 'Sube PDFs de planos, elige alcances por oficio, revisa cantidades detectadas por IA y exporta reportes de precios estructurados - todo en una sola estación de trabajo.',
        FEATURES: {
          PLANS_TITLE: 'Planos',
          PLANS_SUB: 'Subidas',
          SCOPES_TITLE: 'Alcances',
          SCOPES_SUB: 'Detectados',
          REPORTS_TITLE: 'Reportes',
          REPORTS_SUB: 'Exportados',
        },
        BULLETS: {
          DETECTS: 'La IA detecta habitaciones, alcances y cantidades desde PDFs de planos',
          RATES: 'Aplica tarifas de materiales y mano de obra en 5 categorías de oficio',
          EXPORT: 'Exporta reportes de precios detallados para cada proyecto',
        },
        CAPTION: 'Construido alrededor del flujo de takeoff de TALOS.',
      },
      CARD: {
        LOGO_SUB: 'TAKEOFFS + PRECIOS',
        BADGE: 'Plataforma de Construcción con IA',
        TITLE_LINE_1: 'La estación de',
        TITLE_LINE_2: 'takeoff con IA.',
        SUBTITLE: 'Desde la subida del plano hasta un reporte con precio y exportable - en minutos, no horas.',
        START_FIRST: 'Iniciar tu primer takeoff ->',
        SIGN_IN: 'Entrar al workspace',
        SECURE_ACCESS: 'Cifrado de 256 bits · Acceso seguro a TALOS',
        STATS: {
          ANALYSIS_TIME: 'TIEMPO DE ANÁLISIS',
          TRADE_CATEGORIES: 'CATEGORÍAS DE OFICIO',
          DETECTION_ACCURACY: 'PRECISIÓN DE DETECCIÓN',
          FULL_WORKFLOW: 'FLUJO COMPLETO',
        },
      },
      HOW: {
        EYEBROW: 'Cómo Funciona',
        TITLE_LINE_1: 'Cuatro pasos. PDF a',
        TITLE_MUTED: 'reporte con precio.',
        DESCRIPTION: 'Un flujo de IA estructurado que reemplaza por completo el proceso manual de estimación - desde PDF sin procesar hasta salida de precios estructurada.',
        STEPS: {
          UPLOAD: {
            TITLE: 'Sube los PDFs de tus planos',
            DESC: 'Crea un proyecto y adjunta tus hojas de plano. TALOS acepta PDFs de varias páginas y procesa cada hoja de forma independiente.',
          },
          DETECT: {
            TITLE: 'La IA detecta habitaciones y cantidades',
            DESC: 'El motor TALOS escanea cada hoja, identifica límites de habitaciones y calcula cantidades de alcance en las categorías de oficio seleccionadas.',
          },
          REVIEW: {
            TITLE: 'Revisa y aplica precios',
            DESC: 'Inspecciona cantidades detectadas por habitación. Aplica tarifas de material y mano de obra a cada alcance desde el panel de configuración de precios.',
          },
          EXPORT: {
            TITLE: 'Exporta reportes de precios estructurados',
            DESC: 'Genera un PDF formateado con totales de alcance detallados, desglose de mano de obra y resumen del proyecto - listo para entregar al cliente.',
          },
        },
      },
      MODULES: {
        EYEBROW: 'Módulos de Plataforma',
        TITLE_LINE_1: 'Dos módulos.',
        TITLE_MUTED: 'Una plataforma.',
        DESCRIPTION: 'Takeoffs y precios viven juntos. Sin cambiar de contexto entre herramientas.',
        TAKEOFF: {
          MOCK_TITLE: 'Revisión de Takeoff con IA',
          DESCRIPTION: 'Estimación de materiales con IA para divisiones de contratación empresarial. Sube planos, detecta alcances por oficio, revisa cantidades y bloquea la estimación - en minutos.',
          CTA: 'Acceder a la estación de takeoff ->',
        },
        PRICING: {
          MOCK_TITLE: 'Reporte de Precios',
          EXPORT_READY: 'LISTO PARA EXPORTAR',
          DESCRIPTION: 'Convierte cantidades detectadas por IA en reportes de precios configurables. Aplica tarifas por alcance, revisa totales y exporta PDFs estructurados para entregar al cliente.',
          CTA: 'Acceder al módulo de precios ->',
        },
      },
      CTA: {
        EYEBROW: 'Comenzar',
        TITLE_LINE_1: 'Creado para estimadores',
        TITLE_LINE_2: 'que valoran la',
        TITLE_MUTED: 'precisión.',
        DESCRIPTION: 'Sin escalado manual. Sin caos de hojas de cálculo. Infraestructura completa de takeoff y precios con IA - disponible ahora.',
        CREATE_ACCOUNT: 'Crear tu cuenta ->',
      },
      FOOTER: {
        BRAND_SUB: 'IA DE CONSTRUCCIÓN',
        COPY: '© 2026 TALOS - Todos los derechos reservados',
        SECURITY: 'Seguridad',
        PRIVACY: 'Privacidad',
        TERMS: 'Términos',
      },
    },
    FOOTER: {
      BRAND_DESC: 'Análisis de planos y reportes de precios con IA para equipos de estimación en construcción.',
      PRODUCT: {
        TITLE: 'Producto',
        TAKEOFFS: 'Desgloses',
        PRICING: 'Precios',
        PROCESS: 'Proceso',
        SCOPE_SETTINGS: 'Configuración de Alcance',
      },
      COMPANY: {
        TITLE: 'Empresa',
        MISSION: 'Misión',
        WORKFLOWS: 'Flujos de Trabajo',
        BUILDERS: 'Constructores',
        SUPPORT: 'Soporte',
      },
      ACCOUNT: {
        TITLE: 'Cuenta',
        LOGIN: 'Iniciar Sesión',
        START_TAKEOFF: 'Iniciar Análisis',
        DOCUMENTATION: 'Documentación',
        PRIVACY: 'Privacidad',
      },
      COPYRIGHT: '© 2026 TALOS / AI-CONSTRUCTION — Todos los derechos reservados',
      LEGAL: {
        SECURITY: 'Seguridad',
        PRIVACY: 'Privacidad',
        TERMS: 'Términos',
      },
    },
    BUILDER: {
      DASHBOARD: {
        HERO: {
          KICKER: 'CENTRO DE OPERACIONES',
          WELCOME: 'Bienvenido,',
          LINE_1: 'Carga tus planos y deja que TALOS',
          LINE_2: 'ejecute tu análisis con IA.',
          START: 'Iniciar Nuevo Proyecto',
        },
        STATS: {
          TOTAL_KICKER: 'TOTAL',
          PROJECTS: 'Proyectos',
          DONE_KICKER: 'HECHOS',
          COMPLETED: 'Completados',
          ACTIVE_KICKER: 'ACTIVO',
          IN_PROGRESS: 'En Progreso',
        },
        TABLE: {
          KICKER: 'COLA RECIENTE',
          HEADING: 'Mis Proyectos',
          SUBTITLE: 'Tus últimos paquetes de análisis',
          NEW_PROJECT: 'Nuevo Proyecto',
          COL_PROJECT: 'Proyecto',
          COL_STATUS: 'Estado',
          COL_PLANS: 'Planos',
          COL_UPDATED: 'Actualizado',
          UPLOADED: 'cargados',
        },
        STATUS: {
          COMPLETED: 'Completado',
          IN_PROGRESS: 'En Progreso',
          ACTIVE: 'Activo',
        },
      },
      PROFILE: {
        EDIT_BTN: 'Editar Perfil',
        MEMBER_SINCE: 'Miembro desde',
        CONTACT: {
          TITLE: 'Información de Contacto',
          EMAIL: 'Correo',
          PHONE: 'Teléfono',
          COMPANY: 'Empresa',
          ADDRESS: 'Dirección',
        },
        WORK: {
          TITLE: 'Detalles Laborales',
          WORK_TYPE: 'Tipo de Trabajo',
          PROJECT_TYPE: 'Tipo de Proyecto',
          PPM: 'Proyectos / Mes',
          CHALLENGES: 'Desafíos',
          OTHER_CHALLENGES: 'Otros Desafíos',
        },
        SECURITY: {
          TITLE: 'Seguridad',
          PASSWORD: 'Contraseña',
          LAST_UPDATED: 'Última actualización: desconocida',
          CHANGE_BTN: 'Cambiar Contraseña',
        },
        DANGER: {
          TITLE: 'Zona de Peligro',
          DESC: 'Eliminar permanentemente tu cuenta y todos los datos asociados. Esto no se puede deshacer.',
          DELETE_BTN: 'Eliminar Cuenta',
        },
        EDIT: {
          TITLE: 'Editar Perfil',
          SUBTITLE: 'Los cambios se guardan inmediatamente al hacer clic en Guardar.',
          CANCEL: 'Cancelar',
          SAVE: 'Guardar Cambios',
          BASIC_INFO: 'Información Básica',
          FULL_NAME: 'Nombre Completo *',
          FULL_NAME_PH: 'Juan Pérez',
          FULL_NAME_ERR: 'El nombre completo es obligatorio.',
          PHONE: 'Número de Teléfono *',
          PHONE_PH: 'Teléfono de 10 dígitos',
          PHONE_ERR: 'Ingresa un número de teléfono válido de 10 dígitos.',
          COMPANY: 'Nombre de Empresa *',
          COMPANY_PH: 'Construcciones Acme',
          COMPANY_ERR: 'El nombre de la empresa es obligatorio.',
          ADDRESS: 'Dirección *',
          ADDRESS_PH: 'Calle Principal 123',
          ADDRESS_ERR: 'La dirección es obligatoria.',
          TRADE: 'Oficio *',
          TRADE_PH: 'Selecciona tu oficio',
          TRADE_ERR: 'El oficio es obligatorio.',
          PROJ_TYPE: 'Tipo de Proyecto *',
          PROJ_TYPE_PH: 'Selecciona el tipo de proyecto',
          PROJ_TYPE_ERR: 'El tipo de proyecto es obligatorio.',
          PPM: 'Proyectos / Mes *',
          PPM_PH: 'Selecciona un rango',
          PPM_ERR: 'Este campo es obligatorio.',
          OTHER: 'Otros Desafíos',
          OTHER_PH: 'Cualquier otra cosa que te gustaría mejorar...',
        },
        DELETE_MODAL: {
          TITLE: '¿Eliminar tu cuenta?',
          DESC: 'Esto eliminará permanentemente tu cuenta, proyectos y todos los datos.',
          CANNOT_UNDO: 'Esto no se puede deshacer.',
          CONFIRM: 'Sí, eliminar permanentemente',
          CANCEL: 'Cancelar',
        },
        CP_MODAL: {
          TITLE: 'Cambiar Contraseña',
          STEP: 'Paso {{step}} de 2',
          STEP1_DESC: 'Te enviaremos un OTP de 6 dígitos a tu correo electrónico registrado:',
          SEND_OTP: 'Enviar OTP',
          OTP_SENT: '¡OTP enviado! Revisa tu correo e ingrésalo abajo.',
          OTP_CODE: 'Código OTP *',
          OTP_PH: 'Ingresa el código de 6 dígitos',
          OTP_ERR: 'Por favor ingresa el OTP de 6 dígitos.',
          NEW_PWD: 'Nueva Contraseña *',
          NEW_PWD_PH: 'Al menos 8 caracteres',
          NEW_PWD_ERR: 'La contraseña debe tener al menos 8 caracteres.',
          CONFIRM_PWD: 'Confirmar Contraseña *',
          CONFIRM_PH: 'Repite la nueva contraseña',
          CONFIRM_ERR: 'Las contraseñas no coinciden.',
          UPDATE: 'Actualizar Contraseña',
          SENDING: 'Enviando...',
          RESEND_WAIT: 'Reenviar OTP en {{seconds}}s',
          RESEND: 'Reenviar OTP',
        },
        TOAST: {
          PROFILE_UPDATED: 'Perfil actualizado',
          PROFILE_UPDATED_MSG: 'Tus cambios han sido guardados.',
          UPDATE_FAILED: 'Actualización fallida',
          UPDATE_FAILED_MSG: 'No se pudo actualizar el perfil. Inténtalo de nuevo.',
          PWD_CHANGED: 'Contraseña cambiada',
          PWD_CHANGED_MSG: 'Tu contraseña ha sido actualizada exitosamente.',
          OTP_RESENT: 'OTP reenviado',
          OTP_RESENT_MSG: 'Un nuevo código ha sido enviado a tu correo.',
        },
      },
    },
  },
  fr: {
    COMMON: {
      CANCEL: 'Annuler',
      CLOSE: 'Fermer',
      UNEXPECTED_ERROR_TITLE: "Une erreur s'est produite",
      UNEXPECTED_ERROR_MESSAGE: "Une erreur inattendue s'est produite. Veuillez réessayer.",
    },
    ADMIN: {
      SHELL: {
        HEADER: {
          TOGGLE_MENU: 'Basculer le menu',
          ADMIN_CONSOLE: 'Console Admin',
          ONLINE: 'En ligne',
          ADMINISTRATOR: 'Administrateur',
          TALOS_ADMIN: 'Admin TALOS',
          ADMIN_USER: 'Utilisateur Admin',
          LANGUAGE: 'Langue',
          LOG_OUT: 'Déconnexion',
        },
        SIDEBAR: {
          TALOS_HOME: 'Accueil TALOS',
          TAKEOFFS: 'métrés',
          CLOSE_MENU: 'Fermer le menu',
          OPERATIONS: 'Opérations',
          SYSTEM: 'Système',
          DASHBOARD: 'Tableau de bord',
          PROJECTS: 'Projets',
          BUILDERS: 'Builders',
          SUBSCRIPTIONS: 'Abonnements',
          SUPPORT: 'Support',
          SETTINGS: 'Paramètres',
          COPYRIGHT: '© 2026 TALOS · Tous droits réservés',
          EXPAND: 'Développer la barre latérale',
          COLLAPSE: 'Réduire la barre latérale',
        },
      },
      DASHBOARD: {
        HERO: {
          KICKER: 'Centre des Opérations',
          TITLE_LINE_1: 'Bon retour,',
          TITLE_LINE_2: 'Administrateur.',
          DESCRIPTION: "Analysez les plans, vérifiez les quantités extraites et gardez les estimations en mouvement.",
          UPLOAD: 'Créer un Projet',
          ALL_PROJECTS: 'Tous les Projets →',
        },
        PULSE: {
          KICKER: 'Pouls du Système',
          PLANS_PROCESSED: 'Plans Traités',
          REPORTS_GENERATED: 'Devis Générés',
          ACTIVE_PROJECTS: 'Projets Actifs',
        },
        STATS: {
          TOTAL_PLANS: 'Total des Plans Traités',
          REPORTS_GENERATED: 'Devis Générés',
          LAST_ACTIVITY: 'Dernière Activité',
          LAST_ACTIVITY_VALUE: 'il y a 2 h',
          SAVED_PROJECTS: 'Projets Enregistrés',
          BADGE_PLUS_12: '+12%',
          BADGE_PLUS_8: '+8%',
          BADGE_TODAY: "Aujourd'hui",
          BADGE_ACTIVE: 'Actif',
        },
        RECENT: {
          KICKER: 'File Récente',
          TITLE: 'Projets Récents',
          DESCRIPTION: 'Dernières entrées sur la plateforme',
          VIEW_ALL: 'Tout Voir →',
        },
      },
      PROJECTS: {
        HERO: {
          KICKER: 'Index des Projets',
          TITLE: 'Projets',
          LINE_1: 'Recherchez, gérez et ouvrez',
          LINE_2: 'chaque dossier de métré.',
        },
        ACTIONS: {
          NEW_PROJECT: 'Nouveau Projet',
          REFRESH: 'Actualiser',
          TRY_AGAIN: 'Réessayer',
        },
        SEARCH: {
          PLACEHOLDER: 'Rechercher par nom de projet ou propriétaire...',
        },
        META: {
          PROJECTS: '{{count}} projets',
          PAGE: 'Page {{page}}',
        },
        ERROR: {
          LOAD_TITLE: 'Impossible de charger les projets',
          LOAD_MESSAGE: 'Impossible de charger les projets. Veuillez réessayer.',
        },
        TABLE: {
          PROJECT_NAME: 'Nom du Projet',
          OWNER: 'Propriétaire',
          STATUS: 'Statut',
          LAST_MODIFIED: 'Dernière Modification',
          TYPE: 'Type',
          ACTIONS: 'Actions',
          EMPTY_TITLE: 'Aucun projet trouvé',
          EMPTY_MESSAGE: 'Ajustez votre recherche ou ajoutez un nouveau projet',
          VIEW: 'Voir',
          VIEW_PROJECT: 'Voir le projet',
          EDIT: 'Modifier',
          EDIT_PROJECT: 'Modifier le projet',
          DELETE: 'Supprimer',
          DELETE_PROJECT: 'Supprimer le projet',
        },
        STATUS: {
          ACTIVE: 'Actif',
          IN_PROGRESS: 'En Cours',
          COMPLETED: 'Terminé',
        },
        TYPE: {
          RESIDENTIAL: 'Résidentiel',
          COMMERCIAL: 'Commercial',
          INDUSTRIAL: 'Industriel',
          OTHER: 'Autre',
        },
        CONFIRM_DELETE: {
          TITLE: 'Supprimer le Projet',
          MESSAGE: 'Voulez-vous vraiment supprimer ce projet ?',
          CONFIRM: 'Supprimer',
        },
        MODAL: {
          VIEW_TITLE: 'Voir le Projet',
          EDIT_TITLE: 'Modifier le Projet',
          CREATE_TITLE: 'Créer un Nouveau Projet',
          PROJECT_NAME: 'Nom du Projet *',
          PROJECT_NAME_PLACEHOLDER: 'Saisir le nom du projet',
          PROJECT_NAME_ERROR: 'Le nom du projet est requis.',
          PROJECT_NAME_HINT: 'Exemple : Palm Heights Tour A',
          PROJECT_TYPE: 'Type de Projet',
          PROJECT_TYPE_PLACEHOLDER: 'Sélectionner le type de projet',
          PROJECT_TYPE_ERROR: 'Veuillez sélectionner un type de projet.',
          PROJECT_TYPE_NAME: 'Nom de Type Personnalisé',
          PROJECT_TYPE_NAME_PLACEHOLDER: 'Ex. Usage Mixte, Commerce',
          SAVE: 'Enregistrer',
          CREATE: 'Créer le Projet',
        },
        TOAST: {
          UPDATE_SUCCESS_TITLE: 'Projet mis à jour',
          CREATE_SUCCESS_TITLE: 'Projet créé',
          UPDATE_SUCCESS_MESSAGE: 'Projet mis à jour avec succès.',
          CREATE_SUCCESS_MESSAGE: 'Projet créé avec succès.',
          UPDATE_FAILED_TITLE: 'Échec de la mise à jour',
          CREATE_FAILED_TITLE: 'Échec de la création',
          UPDATE_FAILED_MESSAGE: "Nous n'avons pas pu mettre à jour le projet. Veuillez réessayer.",
          CREATE_FAILED_MESSAGE: "Nous n'avons pas pu créer le projet. Veuillez réessayer.",
        },
      },
      SETTINGS: {
        UNKNOWN_SCOPE: 'Paramètres',
        HERO: {
          KICKER: 'Config. Système',
          TITLE: 'Paramètres',
          LINE_1: 'Configurez les prix des matériaux, les paramètres de portée',
          LINE_2: 'et les valeurs par défaut pour toutes les catégories.',
        },
        ACTIVE_SCOPE: {
          KICKER: 'Portée Active',
          DESCRIPTION: 'Les modifications s’appliquent uniquement à cette catégorie.',
        },
        ACTIONS: {
          SAVE_CHANGES: 'Enregistrer',
        },
        PARAMETERS: {
          KICKER: 'Paramètres de Portée',
          TITLE: 'Valeurs par Défaut',
          DESCRIPTION: 'Utilisé lorsqu’aucune valeur de projet ne remplace le défaut.',
          WALL_HEIGHT_FT: 'Hauteur de Mur par Défaut',
        },
        PANELS: {
          FIXTURE_TYPES: 'Types d’Appareils',
          FIXTURES: 'Appareils',
          FIXTURE: 'Appareil',
          FIXTURE_COUNT_ONE: '{{count}} appareil configuré',
          FIXTURE_COUNT_MANY: '{{count}} appareils configurés',
          MATERIAL_TYPES: 'Types de Matériau',
          MATERIAL_TYPES_TITLE: 'Types de Matériau',
          MATERIALS: 'Matériaux',
          MATERIAL: 'Matériau',
          MATERIAL_COUNT_ONE: '{{count}} matériau configuré',
          MATERIAL_COUNT_MANY: '{{count}} matériaux configurés',
          UNIT: 'Unité',
          PRICE_PER_UNIT: 'Prix / Unité',
          ACTIVE: 'Actif',
          ADD_MATERIAL: 'Ajouter un Matériau',
          EDIT: 'Modifier',
          EDIT_PRICE: 'Modifier le prix',
          EDIT_MATERIAL: 'Modifier le matériau',
          DELETE: 'Supprimer',
          DELETE_MATERIAL: 'Supprimer le matériau',
          SAVE: 'Enregistrer',
          NO_FIXTURES: 'Aucun appareil configuré',
          FIXTURES_SYSTEM_MANAGED: 'Les types d’appareils sont gérés par le système.',
          NO_MATERIALS: 'Aucun matériau configuré',
          MATERIALS_SYSTEM_MANAGED: 'Les types de matériau sont gérés par le système.',
          ADD_FIRST_MATERIAL: 'Ajoutez votre premier matériau pour commencer.',
          DRYWALL_PRICE_DESCRIPTION: 'Prix backend par défaut utilisés pour les estimations de cloisons sèches',
          NO_DRYWALL_PRICES: 'Aucun prix de cloison sèche trouvé.',
        },
        MODAL: {
          KICKER: 'Config. Matériau',
          ADD_MATERIAL: 'Ajouter un Matériau',
          EDIT_MATERIAL: 'Modifier le Matériau',
          SAVE_MATERIAL: 'Enregistrer le Matériau',
          SAVE_PRICE: 'Enregistrer le Prix',
          SAVE_FIXTURE: 'Enregistrer l’Appareil',
          EDIT_DRYWALL_PRICE: 'Modifier le Prix Cloison Sèche',
          EDIT_FIXTURE_PRICE: 'Modifier le Prix de l’Appareil',
          EDIT_PLUMBING_MATERIAL_PRICE: 'Modifier le Prix du Matériau de Plomberie',
          EDIT_FIXTURE: 'Modifier l’Appareil',
          ADD_FIXTURE: 'Ajouter un Appareil',
          MATERIAL_NAME: 'Nom du Matériau *',
          MATERIAL_NAME_PLACEHOLDER: 'ex. Bois, Carrelage',
          MATERIAL_NAME_ERROR: 'Le nom du matériau est requis.',
          UNIT: 'Unité *',
          UNIT_ERROR: 'Veuillez sélectionner une unité.',
          PRICE_PER_UNIT: 'Prix par Unité *',
          PRICE_ERROR: 'Veuillez saisir un prix valide (0 ou plus).',
        },
        DOORS_WINDOWS: {
          MATERIALS_DESCRIPTION: 'Variétés de matériaux partagées pour les portes et fenêtres.',
          MATERIAL_NAME: 'Nom du Matériau',
          DOORS: 'Portes',
          DOORS_EMPTY: 'portes',
          DOORS_DESCRIPTION: 'Types de portes et tarifs d’installation par défaut.',
          WINDOWS: 'Fenêtres',
          WINDOWS_EMPTY: 'fenêtres',
          WINDOWS_DESCRIPTION: 'Types de fenêtres et tarifs d’installation par défaut.',
          ADD_TYPE: 'Ajouter un Type',
          NEW_TYPE: 'Nouveau type',
          UNIT_AT_PRICE: '{{unit}} à ${{price}}',
          DELETE_TYPE: 'Supprimer le type',
          TYPE_NAME: 'Nom du Type',
          EDIT_TYPE: 'Modifier le type',
          OPERATION: 'Fonctionnement',
          EDIT_OPERATION: 'Modifier le fonctionnement',
          BEST_USE: 'Meilleur Usage',
          EDIT_BEST_USE: 'Modifier le meilleur usage',
          NOT_SET: 'Non défini',
          HANDING_OPTIONS: 'Options de Main',
          NO_ACTIVE_MATERIALS: 'Aucun matériau actif disponible.',
          NO_TYPES: 'Aucun type {{type}} configuré',
          ADD_FIRST_TYPE: 'Ajoutez le premier type pour commencer.',
        },
        PAINT: {
          BRAND_LIBRARY: 'Bibliothèque de Marques de Peinture',
          BRAND_LIBRARY_DESCRIPTION: 'Définissez les marques, couleurs disponibles, fini et prix',
          REMOVE_BRAND: 'Supprimer la marque',
          REMOVE_COLOR: 'Supprimer la couleur',
          ADD_COLOR_PLACEHOLDER: 'Ajouter une couleur...',
          ADD: 'Ajouter',
          ADD_BRAND: 'Ajouter une Marque',
          BRAND_NAME: 'Nom de marque',
          DEFAULT_SHEEN: 'Fini par défaut',
          PRICE_5_GAL: 'Prix / 5 gal',
          MIN_PRICE: 'Le prix minimum est de 5,00 $',
          SUPPLIES: 'Fournitures de Peinture',
          SUPPLIES_DESCRIPTION: 'Prix unitaires utilisés dans la section Fournitures de Peinture du rapport',
          PAINTERS_TAPE: 'Ruban de peintre (par rouleau)',
          DROP_CLOTH: 'Bâche plastique (chacune)',
          ROLLER_KIT: 'Kit rouleau (par kit)',
          SURFACE_ASSIGNMENTS: 'Attributions de Surface',
          SURFACE_ASSIGNMENTS_DESCRIPTION: 'Attribuez des marques à chaque surface, puis choisissez la couleur et le fini',
          NO_BRANDS_ASSIGNED: 'Aucune marque attribuée pour le moment.',
          BRAND: 'Marque',
          COLOR: 'Couleur',
          SHEEN: 'Fini',
          REMOVE_ASSIGNMENT: 'Supprimer l’attribution',
          CHOOSE_BRAND: 'Choisir une marque...',
          NO_COLORS: 'Aucune couleur configurée',
          NO_SHEEN: 'Aucun fini configuré',
        },
        TOAST: {
          LOAD_FAILED_TITLE: 'Impossible de charger les paramètres',
          LOAD_DRYWALL_FAILED_TITLE: 'Impossible de charger les prix de cloison sèche',
          LOAD_DOORS_WINDOWS_FAILED_TITLE: 'Impossible de charger les paramètres portes et fenêtres',
          LOAD_PLUMBING_FAILED_TITLE: 'Impossible de charger les paramètres de plomberie',
          LOAD_PAINT_FAILED_TITLE: 'Impossible de charger les paramètres de peinture intérieure',
          TRY_AGAIN_MESSAGE: 'Veuillez réessayer dans un instant.',
          SAVED_TITLE: 'Paramètres enregistrés',
          SAVE_FAILED_TITLE: 'Impossible d’enregistrer les paramètres',
          SAVE_FAILED_MESSAGE: 'Vérifiez vos modifications et réessayez.',
          WALL_HEIGHT_MESSAGE: 'La hauteur du mur doit être d’au moins 6 ft.',
          PRICING_SAVED_MESSAGE: 'La configuration des prix a été mise à jour.',
          PLUMBING_SAVED_MESSAGE: 'Les paramètres de plomberie ont été mis à jour.',
          PAINT_SAVED_MESSAGE: 'Les paramètres de peinture intérieure ont été mis à jour.',
          DRYWALL_SAVED_MESSAGE: 'Les paramètres de cloison sèche ont été mis à jour.',
          DOORS_WINDOWS_SAVED_MESSAGE: 'Les paramètres Portes et Fenêtres ont été mis à jour.',
        },
      },
      SUBSCRIPTIONS: {
        HERO: {
          KICKER: 'Facturation',
          TITLE: "Plans d'Abonnement",
          LINE_1: "Consultez et gérez tous les niveaux d'abonnement",
          LINE_2: 'disponibles sur la plateforme.',
        },
        ACTIONS: {
          ADD_PLAN: 'Ajouter un Plan',
          EDIT_PLAN: 'Modifier le plan',
        },
        STATS: {
          TOTAL: 'Total',
          PLANS: 'Plans',
          ACTIVE: 'Actifs',
          RUNNING: 'En Cours',
          INACTIVE: 'Inactifs',
          PAUSED: 'En Pause',
        },
        BILLING: {
          MONTHLY: 'Mensuel',
          YEARLY: 'Annuel',
        },
        STATUS: {
          ACTIVE: 'Actif',
          INACTIVE: 'Inactif',
        },
        PLANS: {
          MONTHLY_LIMITED: 'Plan Mensuel Limité',
          MONTHLY_PREMIUM: 'Plan Mensuel Premium',
          YEARLY_LIMITED: 'Plan Annuel Limité',
          YEARLY_PREMIUM: 'Plan Annuel Premium',
        },
        PRICE: {
          PER_YEAR: '/an',
          PER_MONTH: '≈ {{amount}} par mois',
        },
        FEATURES: {
          ALL_CATEGORIES: 'Toutes les catégories incluses',
          ONE_CATEGORY: '{{count}} catégorie incluse',
          MANY_CATEGORIES: '{{count}} catégories incluses',
          SUPPORT: 'Assistance {{support}}',
        },
        SUPPORT: {
          PRIORITY: 'prioritaire',
        },
        MODAL: {
          KICKER: 'Config. du Plan',
          TITLE: "Modifier le Plan d'Abonnement",
          CLOSE: 'Fermer',
          PLAN_NAME: 'Nom du Plan *',
          PLAN_NAME_PLACEHOLDER: 'Saisir le nom du plan',
          PLAN_NAME_ERROR: 'Le nom du plan est requis.',
          DESCRIPTION: 'Description *',
          DESCRIPTION_PLACEHOLDER: 'Description courte et claire pour ce plan.',
          DESCRIPTION_ERROR: 'La description est requise.',
          DESCRIPTION_HINT: 'Description courte et claire pour ce plan.',
          ANNUAL_FEE: 'Frais Annuels *',
          ANNUAL_FEE_ERROR: 'Saisissez des frais annuels valides.',
          CANCEL: 'Annuler',
          SAVE: 'Enregistrer',
        },
      },
      BUILDERS: {
        HERO: {
          KICKER: 'PLATEFORME',
          TITLE: 'Tous les Constructeurs',
          TOTAL_BADGE: '{{count}} au total',
          LINE_1: 'Gérez tous les constructeurs enregistrés',
          LINE_2: 'et leur accès aux abonnements.',
        },
        STATS: {
          TOTAL_KICKER: 'TOTAL',
          REGISTERED: 'Enregistrés',
          PAID_KICKER: 'PAYANT',
          PRO_ENTERPRISE: 'Pro & Entreprise',
          FREE_KICKER: 'GRATUIT',
          TRIAL: 'Accès d\'essai',
        },
        TABLE: {
          KICKER: 'REGISTRE DES CONSTRUCTEURS',
          HEADING: 'Tous les Constructeurs',
          SHOWING: 'Affichage de {{shown}} sur {{total}} constructeurs',
          PAGE: '· Page {{page}}',
          SEARCH_PH: 'Rechercher par nom ou email...',
        },
        EMPTY: {
          TITLE: 'Aucun constructeur trouvé',
          SUBTITLE: 'Essayez d\'ajuster votre recherche.',
        },
        COL: {
          BUILDER: 'Constructeur',
          EMAIL: 'E-mail',
          SUBSCRIPTION: 'Abonnement',
          JOINED: 'Inscrit',
        },
        ACTION: {
          VIEW: 'Voir',
          DELETE: 'Supprimer',
        },
        MODAL: {
          TITLE: 'Supprimer le Constructeur',
          MESSAGE: 'Êtes-vous sûr de vouloir supprimer ce constructeur de la plateforme ?',
          CONFIRM: 'Supprimer',
          CANCEL: 'Annuler',
        },
      },
      SUPPORT: {
        HERO: {
          KICKER: 'CENTRE D\'AIDE',
          TITLE: 'Comment pouvons-nous vous aider ?',
          LINE_1: 'Consultez notre FAQ, lisez nos politiques ou contactez-nous directement.',
          LINE_2: 'Notre équipe est là pour rendre votre expérience TALOS fluide.',
        },
        CONTACT: {
          EMAIL_TITLE: 'Support par E-mail',
          EMAIL_SUBTITLE: 'support@talos.ai',
          EMAIL_ACTION: 'Envoyer un message',
          PHONE_TITLE: 'Support Téléphonique',
          PHONE_SUBTITLE: '+1 (800) 555-0199',
          PHONE_ACTION: 'Nous appeler',
          TERMS_TITLE: 'Conditions Générales',
          TERMS_SUBTITLE: "Politiques d'utilisation de la plateforme",
          TERMS_ACTION: 'Lire les conditions',
          PRIVACY_TITLE: 'Politique de Confidentialité',
          PRIVACY_SUBTITLE: 'Traitement des données et droits',
          PRIVACY_ACTION: 'Lire la politique',
        },
        FAQ: {
          KICKER: 'FAQ',
          TITLE: 'Questions Fréquemment Posées',
          COUNT: '{{count}} questions',
          Q1: 'Comment ajouter un nouveau constructeur à la plateforme ?',
          A1: "Accédez à la Gestion des Constructeurs depuis la barre latérale. Utilisez l'option d'invitation pour enregistrer un nouveau compte constructeur. Il recevra un e-mail avec ses identifiants.",
          Q2: 'Comment les plans d\'abonnement sont-ils attribués aux constructeurs ?',
          A2: "Les plans d'abonnement sont sélectionnés par les constructeurs lors de leur inscription. En tant qu'administrateur, vous pouvez gérer les plans disponibles depuis la page de Gestion des Abonnements.",
          Q3: 'Que se passe-t-il quand je supprime un projet ?',
          A3: 'La suppression d\'un projet est définitive. Tous les plans, rapports et documents générés par l\'IA seront supprimés. Confirmez toujours avant de procéder.',
          Q4: 'Puis-je modifier le prix d\'un plan d\'abonnement après sa publication ?',
          A4: "Oui — utilisez le bouton d'édition (crayon) sur n'importe quelle carte de plan dans la page de Gestion des Abonnements. Les modifications s'appliquent uniquement aux nouveaux abonnements.",
          Q5: 'Comment contacter le support technique ?',
          A5: "Vous pouvez joindre l'équipe technique par e-mail à support@talos.ai ou appeler notre ligne d'assistance pendant les heures ouvrées. Les délais de réponse sont généralement inférieurs à 24 heures.",
        },
        FOOTER: "Heures de support : Lun – Ven, 9h – 18h IST · Délai de réponse moyen : < 24 heures",
      },
    },
    REPORTS: {
      NAV: {
        REPORTS: 'Rapports',
        AI_ESTIMATE: 'Estimation IA',
        EXPORT_WORD: 'Exporter Word',
        EXPORT_PDF: 'Exporter PDF',
        EXPORT_EXCEL: 'Exporter Excel',
      },
      CATEGORIES: {
        FLOORING: 'Revêtement de sol',
        DRYWALL: 'Cloison sèche',
        INTERIOR_PAINT: 'Peinture intérieure',
        FIXTURES: 'Appareils',
        FIXTURES_PLUMBING: 'Appareils (Plomberie)',
        DOORS_WINDOWS: 'Portes et Fenêtres',
      },
      TITLES: {
        FLOORING_REPORT: 'Rapport Revêtement de Sol',
        DRYWALL_REPORT: 'Rapport Cloison Sèche',
        INTERIOR_PAINT_REPORT: 'Rapport Peinture Intérieure',
        FIXTURES_REPORT: 'Rapport Appareils (Plomberie)',
        DOORS_WINDOWS_REPORT: 'Rapport Portes et Fenêtres',
      },
      LABELS: {
        AI_GENERATED: 'Estimation générée par IA',
        TOTAL_COST: 'Coût Total',
        GRAND_TOTAL: 'Total Général',
        PROJECT_ESTIMATE: 'Estimation du Projet',
        GENERATED: 'Généré',
        ESTIMATED_MATERIALS: 'Matériaux estimés',
        FLOORS: 'Étages',
        FLOOR: 'Étage',
        LEVELS_ANALYZED: 'Niveau(x) analysés',
        TOTAL_AREA: 'Surface Totale',
        COMBINED_FLOOR_AREA: 'Surface combinée des étages',
        ROOMS: 'Pièces',
        ACROSS_ALL_FLOORS: 'Sur tous les étages',
        COST_BY_FLOOR: 'Coût par Étage',
        MATERIALS_DISTRIBUTION: 'Distribution des matériaux par niveau',
        FLOOR_DISTRIBUTION: 'Distribution des Étages',
        COST_SHARE_PER_FLOOR: 'Part du coût par étage',
        ROOM_MATERIALS: 'Matériaux par Pièce',
        MATERIALS_BREAKDOWN: 'Détail des Matériaux',
        ITEM: 'Article',
        NAME: 'Nom',
        LABEL: 'Libellé',
        ROOM: 'Pièce',
        QTY: 'Qté',
        QUANTITY: 'Quantité',
        UNIT: 'Unité',
        UNIT_PRICE: 'Prix Unitaire',
        TOTAL: 'Total',
        COST: 'Coût',
        VALUE: 'Valeur',
        DIMENSIONS: 'Dimensions',
        TYPE: 'Type',
        HANDING: 'Sens',
        MATERIAL: 'Matériau',
        FLOOR_SUBTOTAL: 'Sous-total Étage',
        PROJECT_TOTAL: 'Total du projet',
        WALL_AREA: 'Surface Murale',
        CEILING_AREA: 'Surface Plafond',
        CORNERS: 'Angles',
        INTERIOR_WALL_SURFACE: 'Surface intérieure des murs',
        TOTAL_CEILING_SURFACE: 'Surface totale du plafond',
        TOTAL_CORNER_POINTS: 'Nombre total d’angles',
        MATERIAL_COST_BREAKDOWN: 'Détail du Coût des Matériaux',
        SURFACE_AREA_SPLIT: 'Répartition des Surfaces',
        BOARD_TYPE: 'Type de Plaque',
        ALL_MATERIALS: 'Tous les Matériaux',
        COMPLETE_MATERIALS_SCHEDULE: 'Liste complète des matériaux',
        TOTAL_MATERIALS: 'Total Matériaux',
        COST_SPLIT: 'Répartition des Coûts',
        PAINT_AREAS: 'Zones de Peinture',
        PRIMER_APPLICATION: 'Application du Primer',
        PAINT_APPLICATION: 'Application de Peinture',
        PAINT_SUPPLIES: 'Fournitures de Peinture',
        PRIMER: 'Primer',
        PAINT: 'Peinture',
        SUPPLIES: 'Fournitures',
        SINGLE_COAT: 'Une couche',
        SINGLE_COAT_APPLICATION: 'Application en une couche',
        TWO_COATS: 'Deux couches',
        TWO_COAT_APPLICATION: 'Application en deux couches',
        PRIMER_PAINT_SUPPLIES: 'Primer + Peinture + Fournitures',
        BRUSHES_TAPE_TOOLS: 'Pinceaux, ruban et outils',
        APPLICATION: 'Application',
        SQ_FT: 'pi²',
        GALLONS: 'Gallons',
        GAL: 'Gal',
        BRAND: 'Marque',
        COLOR: 'Couleur',
        SHEEN: 'Finition',
        TOTAL_PRIMER_COST: 'Coût Total du Primer',
        OVERALL_PAINT_COST: 'Coût Global de Peinture',
        TOTAL_PAINT_SUPPLIES: 'Total Fournitures de Peinture',
        PEX_PIPING: 'PEX et Tuyauterie',
        PEX_TUBING: 'Tuyau PEX',
        PEX_CONNECTORS: 'Connecteurs PEX',
        DRAINAGE: 'Évacuation',
        OTHER: 'Autre',
        ALL_PLUMBING_COMBINED: 'Toute la plomberie combinée',
        PER_ROOM_FIXTURE_COST: 'Coût des appareils par pièce',
        TUBING_CONNECTORS: 'Tuyaux et connecteurs',
        DRAINAGE_EXAMPLES: 'Siphons P, brides, wyes',
        COST_BREAKDOWN: 'Détail des Coûts',
        SHARE_BY_PLUMBING_CATEGORY: 'Part par catégorie de plomberie',
        SHARE_BY_CATEGORY: 'Part par catégorie',
        TOP_FIXTURES_BY_COST: 'Appareils principaux par coût',
        PER_FIXTURE_COST_DISTRIBUTION: 'Distribution du coût par appareil',
        FIXTURES_SUBTOTAL: 'Sous-total Appareils',
        DOORS: 'Portes',
        WINDOWS: 'Fenêtres',
        INSTALLATION: 'Installation',
        MATERIALS_INSTALLATION: 'Matériaux + Installation',
        LABOR_MATERIALS: 'Main-d’œuvre et matériaux',
        DOOR_ITEMS_BY_COST: 'Portes par Coût',
        COST_PER_DETECTED_DOOR: 'Coût par porte détectée',
        DOORS_SUBTOTAL: 'Sous-total Portes',
        WINDOWS_SUBTOTAL: 'Sous-total Fenêtres',
        INSTALLATION_MATERIALS: 'Matériaux d’Installation',
        DOOR_INSTALLATION: 'Installation des Portes',
        WINDOW_INSTALLATION: 'Installation des Fenêtres',
        INSTALLATION_SUBTOTAL: 'Sous-total Installation',
        TOTAL_ESTIMATED_PROJECT_COST: 'Coût Total Estimé du Projet',
        POWERED_BY_TALOS: 'Propulsé par TALOS',
        ALL_RIGHTS_RESERVED: 'Tous droits réservés.',
        ITEM_COUNT: '{{count}} article(s)',
        ITEMS_DETECTED: '{{count}} article(s) détecté(s)',
        FLOOR_COUNT: '{{count}} étage(s)',
        ROOM_COUNT: '{{count}} pièce(s)',
        AREAS_COUNT: '{{count}} zones',
        GAL_TOTAL: '{{count}} gal au total',
        OF_TOTAL: '{{percent}}% du total',
        OF_TOTAL_SURFACE: '{{percent}}% de la surface totale',
      },
      EMPTY: {
        NO_ROOMS: 'Aucune pièce assignée',
        NO_MATERIALS: 'Aucun matériau assigné',
        NO_FIXTURE_ROWS: 'Aucune ligne d’appareils',
        NO_FIXTURE_ROWS_AVAILABLE: 'Aucune ligne d’appareils disponible',
        NO_DOOR_ROWS_AVAILABLE: 'Aucune ligne de portes disponible',
        NO_REPORT_DATA: 'Aucune donnée de rapport disponible.',
        GO_BACK: 'Retour',
      },
      DISCLAIMER: 'Cette estimation est générée par IA à partir des données du plan et des prix régionaux des matériaux. Les coûts réels peuvent varier selon le marché local, les tarifs des entrepreneurs et les spécifications finales du projet. Les chiffres servent uniquement de guide de planification et doivent être vérifiés par un entrepreneur agréé.',
      SHORT_DISCLAIMER: 'Toutes les mesures et estimations de coûts sont approximatives, générées avec des outils d’IA. Veuillez vérifier tous les chiffres avant exécution.',
    },
    PROJECT_STEPPER: {
      WORKFLOW: 'Flux du Projet',
      STEPS: {
        DETECTION_SCOPE: 'Portée de Détection',
        UPLOAD_PLAN: 'Téléverser le Plan',
        ASSIGN_FLOORS: 'Attribuer les Étages',
        RESULTS: 'Résultats',
      },
      HEADER: {
        STEP: 'Étape',
        STEP_OF: 'ÉTAPE {{step}} SUR {{total}}',
      },
      ARIA: {
        DIALOG: 'Étape {{step}} sur {{total}} : {{label}} - {{project}}',
        STEP_LIVE: 'Étape {{step}} sur {{total}} : {{label}}',
        WORKFLOW_STEPS: 'Étapes du flux',
        COMPLETE: 'terminée',
        CURRENT: 'étape actuelle',
        UPCOMING: 'à venir',
      },
      LOADING: {
        PROJECT_TITLE: 'Chargement du projet',
        PROJECT_MESSAGE: 'Recherche des résultats existants...',
        FLOOR_VIEWER_TITLE: 'Chargement du visualiseur de plan',
        FLOOR_VIEWER_MESSAGE: "Préparation de l'espace d'attribution du PDF.",
        RESULTS_TITLE: 'Chargement des résultats',
        RESULTS_MESSAGE: 'Préparation de la vue des résultats de détection pour ce projet.',
      },
      NAV: {
        PREVIOUS: 'Étape précédente',
        RESTART_TOUR: 'Revoir le tour',
        CONTINUE: 'Continuer',
        CONTINUE_UPLOAD: 'Continuer vers le Téléversement',
        CONTINUE_ASSIGN: 'Continuer vers les Étages',
        CONTINUE_RESULTS: 'Continuer vers les Résultats',
        FINISH: 'Terminer',
        SAVING_PRICING: 'Enregistrement des prix...',
        SELECT_CATEGORIES_TOOLTIP: 'Sélectionnez 1 à {{max}} catégories pour continuer',
        UPLOAD_TOOLTIP: 'Téléversez un plan pour continuer',
        ASSIGN_AND_SCALE_TOOLTIP: 'Attribuez au moins une page et sélectionnez une échelle pour continuer',
        ASSIGN_TOOLTIP: 'Attribuez au moins une page pour continuer',
        SCALE_TOOLTIP: 'Sélectionnez une échelle pour continuer',
      },
      CONFIRM: {
        CLOSE_DIRTY: 'Vous avez des modifications de plan non enregistrées. Fermer et les ignorer ?',
        BACK_DIRTY: 'Vous avez des modifications non enregistrées. Revenir et les ignorer ?',
        SCALE_TITLE: 'Échelle non attribuée',
        SCALE_MESSAGE: "Vous avez changé l'échelle sans cliquer sur Attribuer. La nouvelle échelle ne sera pas enregistrée.",
        CONTINUE_ANYWAY: 'Continuer quand même',
        GO_BACK: 'Retour',
      },
      TOAST: {
        SAVE_FAILED_TITLE: "Échec de l'enregistrement",
        SAVE_SCOPE_FAILED_MESSAGE: "Impossible d'enregistrer la portée de détection.",
        PRICING_SAVE_FAILED_TITLE: "Échec de l'enregistrement des prix",
        PRICING_SAVE_FAILED_MESSAGE: "Impossible d'enregistrer les prix des matériaux. Veuillez réessayer.",
      },
      ERRORS: {
        MAX_CATEGORIES: "Sélectionnez jusqu'à {{max}} catégories de détection.",
        LOAD_SCOPE: 'Impossible de charger la portée de détection.',
        SAVE_SCOPE: "Impossible d'enregistrer la portée de détection.",
        NO_PROJECT: 'Aucun projet sélectionné.',
        NO_PROJECT_RESTART: 'Aucun projet sélectionné. Veuillez redémarrer le flux.',
        TOO_MANY_CATEGORIES: 'Trop de catégories de détection sélectionnées.',
      },
      CATEGORIES: {
        FLOORING: { LABEL: 'Revêtement de Sol', DESCRIPTION: 'Détecte les surfaces de sol et les polygones de pièces pour le métré.' },
        DRYWALL: { LABEL: 'Cloisons Sèches', DESCRIPTION: 'Mesure les murs et périmètres de pièces pour les estimations.' },
        FIXTURES: { LABEL: 'Appareils de Plomberie', DESCRIPTION: 'Identifie lavabos, toilettes, baignoires et appareils associés.' },
        DOORS_WINDOWS: { LABEL: 'Portes et Fenêtres', DESCRIPTION: 'Détecte les ouvertures de portes et fenêtres sur les plans attribués.' },
        INTERIOR_PAINT: { LABEL: 'Peinture Intérieure', DESCRIPTION: 'Estime les surfaces de peinture intérieure par pièce et surface murale.' },
        HVAC: { LABEL: 'CVC', DESCRIPTION: 'Mesure les conduits et éléments CVC depuis les plans.' },
        COLUMNS: { LABEL: 'Colonnes', DESCRIPTION: 'Détecte les colonnes structurelles et leurs dimensions mesurées.' },
        STEEL_BEAMS: { LABEL: 'Poutres en Acier', DESCRIPTION: 'Détecte les poutres en acier et les mesures groupées.' },
      },
      STEP1: {
        TITLE: 'Sélectionner la Portée de Détection.',
        DESCRIPTION: "Choisissez jusqu'à deux éléments architecturaux que l'IA doit détecter dans vos plans.",
        LOADING_TITLE: 'Chargement de la portée de détection',
        LOADING_MESSAGE: 'Récupération des catégories disponibles pour ce projet.',
        SELECT_UP_TO: "Sélectionnez jusqu'à {{max}} catégories.",
      },
      STEP2: {
        TITLE: 'Téléverser le Plan.',
        DESCRIPTION: "Téléversez le PDF du plan; l'IA analysera chaque page que vous attribuez.",
        CHECKING_TITLE: 'Vérification des téléversements existants',
        CHECKING_MESSAGE: 'Recherche des plans déjà joints à ce projet.',
        UPLOADED_DOCUMENTS: 'Documents téléversés',
        FILE_COUNT_ONE: '{{count}} fichier',
        FILE_COUNT_MANY: '{{count}} fichiers',
        UPLOADED_AT: 'Téléversé {{date}}',
        DELETE_DOCUMENT: 'Supprimer le document',
        UPLOAD_NEW_VERSION: 'Téléverser une nouvelle version',
        DROPZONE_TITLE: 'Glissez et déposez votre plan ici',
        BROWSE_HINT: 'ou cliquez pour parcourir les fichiers',
        BROWSE_FILES: 'Parcourir',
        PDF_ONLY: 'PDF uniquement',
        UPLOADING: 'Téléversement...',
        UPLOADED_SUCCESS: 'Téléversé avec succès; vous pouvez continuer',
        CHANGE_FILE: 'Changer de fichier',
        ERRORS: {
          UNSUPPORTED_TYPE: 'Type de fichier non pris en charge. Veuillez téléverser un PDF.',
          INVALID_PDF: 'Ce fichier ne semble pas être un PDF valide. Vérifiez le fichier et réessayez.',
          FILE_TOO_LARGE: 'Le fichier est trop volumineux. Téléversez un PDF de moins de {{maxSizeMb}} Mo.',
          UPLOAD_FAILED: 'Échec du téléversement. Veuillez réessayer.',
        },
        TOAST: {
          DELETE_SUCCESS_TITLE: 'Document supprimé',
          DELETE_SUCCESS_MESSAGE: 'Le plan a été supprimé.',
          DELETE_FAILED_TITLE: 'Échec de la suppression',
          DELETE_FAILED_MESSAGE: 'Impossible de supprimer le document. Veuillez réessayer.',
          UPLOAD_SUCCESS_TITLE: 'Téléversement réussi',
          UPLOAD_SUCCESS_MESSAGE: 'Plan téléversé avec succès.',
          UPLOAD_FAILED_TITLE: 'Échec du téléversement',
          UPLOAD_FAILED_MESSAGE: 'Impossible de téléverser le fichier. Veuillez réessayer.',
        },
      },
      STEP3: {
        EDIT_PAGE_TITLE: 'Cliquer pour modifier le numéro de page',
        FIT: 'Ajuster',
        FIT_TITLE: "Ajuster la page à l'écran",
        CRITICAL_TITLE: 'Instruction Critique',
        CRITICAL_MESSAGE_PREFIX: 'Sélectionnez uniquement les pages contenant',
        ONE_FLOOR_PLAN: 'UN seul plan d’étage',
        CRITICAL_MESSAGE_SUFFIX: 'Évitez les pages avec plusieurs plans, sections ou dessins combinés.',
        DISMISS_NOTICE: "Masquer l'avis",
        NO_PDF: "Aucun PDF chargé. Téléversez un plan à l'étape précédente.",
        CAL_FIRST_POINT: 'Cliquez sur le premier point d’une dimension connue.',
        CAL_SECOND_POINT: "Cliquez sur le second point pour définir l'échelle.",
        SCALE_UPDATED: 'Échelle mise à jour avec succès.',
        CALIBRATE_SCALE: "Calibrer l'Échelle",
        PAGE_LABEL: 'Page {{page}}',
        DRAWN_LINE: 'Ligne Dessinée',
        PDF_PT: 'pt PDF',
        EQUALS_REAL_WORLD: 'équivaut dans le monde réel',
        FEET_LABEL: "Pieds (ft') *",
        INCHES_LABEL: 'Pouces (in")',
        SCALE: 'Échelle',
        RECALIBRATE: 'Recalibrer',
        APPLY_SCALE: "Appliquer l'Échelle",
        ENTER_REAL_LENGTH: 'Saisir la longueur réelle',
        NOT_ASSIGNED: 'Non Attribué',
        ASSIGNMENT_PANEL: "PANNEAU D'ATTRIBUTION",
        FLOOR_ASSIGNMENT: "Attribution d'Étage",
        ASSIGN_EACH_PAGE: 'Attribuez chaque page à un niveau',
        ASSIGNED: 'Attribué',
        UNASSIGNED: 'Non attribué',
        ASSIGN_FLOOR: "Attribuer l'Étage",
        STANDARD: 'Standard',
        CALIBRATE: 'Calibrer',
        LOADING_SCALES: 'Chargement des échelles...',
        SELECT_SCALE: 'Sélectionner une échelle...',
        NO_SCALES: 'Aucune échelle disponible',
        REDO_CALIBRATION: 'Refaire la calibration',
        CLEAR_CALIBRATED_SCALE: "Effacer l'échelle calibrée",
        SCALE_REQUIRED: "Sélectionnez ou calibrez une échelle avant d'attribuer.",
        FLOOR: 'Étage',
        SELECT_FLOOR: 'Sélectionner un étage...',
        ALL_FLOORS_ASSIGNED: 'Tous les étages sont déjà attribués',
        ASSIGN: 'Attribuer',
        ASSIGN_NEXT: 'Attribuer et Suivant',
        ASSIGNED_FLOORS: 'ÉTAGES ATTRIBUÉS',
        NO_FLOORS_ASSIGNED: 'Aucun étage attribué pour le moment.',
        SELECT_AND_ASSIGN: 'Sélectionnez un étage et cliquez sur Attribuer.',
        PAGE_SHORT: 'pg',
        CHANGE_FLOOR: "Changer d'étage...",
        VIEWING_NOW: 'Affiché maintenant',
        EDIT_FLOOR: "Modifier l'étage",
        REMOVE: 'Retirer',
        TOAST: {
          INVALID_SCALE_TITLE: 'Échelle invalide',
          INVALID_SCALE_MESSAGE: 'Saisissez la longueur réelle de la ligne mesurée.',
          SUBMISSION_FAILED_TITLE: "Échec de l'envoi",
          SUBMISSION_FAILED_MESSAGE: 'Impossible de soumettre cette page. Veuillez réessayer.',
          DELETE_FAILED_TITLE: 'Échec de la suppression',
          DELETE_FAILED_MESSAGE: 'Impossible de retirer cette attribution. Veuillez réessayer.',
          UPDATE_FAILED_TITLE: 'Échec de la mise à jour',
          UPDATE_FAILED_MESSAGE: 'Impossible de mettre à jour cette attribution. Veuillez réessayer.',
        },
      },
      STEP4: {
        FIT: 'Ajuster',
        UPDATING_ZOOM: 'Mise à jour du zoom',
        LOADING_FLOOR_PLAN: 'Chargement du plan',
        RENDERING_VIEW: 'Rendu de la vue {{zoom}}',
        PLEASE_WAIT: 'Veuillez patienter',
        REGENERATING_COORDINATES: 'Régénération des coordonnées',
        REGENERATE_DETECTION: 'Régénérer la détection',
        REGENERATING: 'Régénération...',
        REGENERATE: 'Régénérer',
        MATERIALS_PRICING: 'Matériaux et Prix',
        PRICING: 'Prix',
        VIEW_HTML_REPORT: 'Voir le rapport HTML',
        VIEW: 'Voir',
        AI_DETECTION_RUNNING: 'DÉTECTION IA EN COURS',
        ANALYZING_TITLE: 'Analyse des Plans',
        ANALYZING_MESSAGE: 'Les résultats apparaîtront à mesure que chaque catégorie de détection se termine. Vous pouvez suivre la progression ci-dessous.',
        DONE: 'Terminé',
        FAILED: 'Échec',
        DETECTING: 'Détection de {{category}}...',
        PERCENT_COMPLETE: '{{percent}}% terminé',
        DETECTION_FAILED: 'Échec de la détection',
        NO_ELEMENTS: 'Aucun élément détecté',
        NO_ELEMENTS_MESSAGE: "L'IA n'a trouvé aucun élément {{category}}.",
        NO_FLOOR_PLAN: 'Aucun plan disponible.',
        DOOR: 'porte',
        WINDOW: 'fenêtre',
        DRAG_DRAW_DW: 'Glissez pour dessiner un rectangle de {{type}}',
        ZOOM_OUT: 'Dézoomer',
        ZOOM_IN: 'Zoomer',
        FIT_TO_SCREEN: "Ajuster à l'écran",
        DRAG_RECTANGLE: 'Glissez pour dessiner un rectangle',
        DRAG_ELLIPSE: 'Glissez pour dessiner une ellipse',
        CLICK_FIRST_VERTEX: 'Cliquez pour placer le premier sommet',
        POINTS_CLOSE_HINT: 'points - double-cliquez ou cliquez sur le premier point pour fermer',
        SELECTED: 'Sélectionné : {{name}}',
        EDIT_POLYGON_POINTS: 'Modifier les points du polygone',
        EDIT: 'Modifier',
        DELETE_THIS_ITEM: 'Supprimer cet élément',
        DELETE: 'Supprimer',
        DESELECT: 'Désélectionner',
        WIDTH: 'Largeur',
        THICKNESS: 'Épaisseur',
        NO_SPEC_DATA: 'Aucune donnée de spécification disponible.',
        SELECT_ITEM: 'Sélectionner un élément',
        SELECT_ITEM_MESSAGE: 'Cliquez sur une porte ou une fenêtre du plan pour voir ses détails.',
        BEAM: 'Poutre',
        COLUMN: 'Colonne',
        GROUPING: 'Regroupement',
        ERRORS: {
          REGENERATE_FAILED: 'Impossible de régénérer les coordonnées. Veuillez réessayer.',
          COMING_SOON: '{{category}} sera bientôt disponible.',
          PROCESS_FAILED: 'Impossible de traiter les plans. Veuillez réessayer.',
          PROCESSING_ERROR: "Une erreur s'est produite pendant le traitement IA.",
        },
        POINTS_NEED_MORE: '{{count}} {{plural}} — encore {{more}} à placer',
        ARIA: {
          OVERLAY_LABEL: 'Plan interactif de {{category}} avec {{count}} éléments détectés. Appuyez sur Tab pour naviguer entre les éléments, Entrée ou Espace pour sélectionner.',
        },
        DW_PANEL: {
          COMPLETED: 'Terminé',
          ITEMS_DETECTED: '{{count}} éléments détectés',
          SUMMARY: 'Résumé',
          DOORS: 'Portes',
          WINDOWS: 'Fenêtres',
          FLOORS: 'Étages',
          EDITING: 'Modification :',
          DRAWING_NEW_DOOR: 'Nouvelle Porte en cours',
          DRAWING_NEW_WINDOW: 'Nouvelle Fenêtre en cours',
          NAME_YOUR_DOOR: 'Nommez votre Porte',
          NAME_YOUR_WINDOW: 'Nommez votre Fenêtre',
          DRAG_DRAW_RECT: 'Faites glisser sur le plan pour dessiner un rectangle.',
          DOOR_NAME: 'Nom de la Porte',
          WINDOW_NAME: 'Nom de la Fenêtre',
          CANCEL: 'Annuler',
          SAVE_DOOR: 'Enregistrer la Porte',
          SAVE_WINDOW: 'Enregistrer la Fenêtre',
          ADD_DOOR: 'Ajouter une Porte',
          ADD_WINDOW: 'Ajouter une Fenêtre',
          DETECTION: 'Détection',
          CHANGE_COLOR_HINT: "Cliquez sur la pastille pour changer la couleur de la superposition de détection",
          COLOR_TIP: 'Astuce : cliquez ici pour choisir une couleur',
        },
        EDIT_TOOLBAR: {
          TITLE: 'Modifier les Points',
          UNSAVED: 'Modifications non enregistrées',
          UNDO: 'Annuler',
          UNDO_TITLE: 'Annuler (Ctrl+Z)',
          REDO: 'Rétablir',
          REDO_TITLE: 'Rétablir (Ctrl+Y)',
          CANCEL: 'Annuler',
          CANCEL_TITLE: 'Annuler (Échap)',
          DELETE: 'Supprimer',
          DELETE_ROOM: 'Supprimer cette pièce',
          SAVE: 'Enregistrer',
          SAVING: 'Enregistrement…',
          SQ_FT: 'pi²',
          PTS: 'pts',
          SELF_CROSSING: 'Auto-croisement',
          SHORTCUTS_TITLE: 'Raccourcis clavier',
          EDIT_SHORTCUTS: 'Raccourcis d\'édition',
          DRAG_VERTEX: 'Faire glisser un sommet',
          MOVE_CORNER: 'Déplacer un coin',
          CLICK_ON_EDGE: 'Clic ○ sur le bord',
          ADD_VERTEX: 'Ajouter un sommet',
          RIGHT_CLICK_VERTEX: 'Clic droit sur un sommet',
          DELETE_VERTEX: 'Supprimer un sommet',
          UNDO_KEY: 'Annuler',
          REDO_KEY: 'Rétablir',
          CANCEL_KEY: 'Annuler (confirme)',
          DRAG_HINT: 'Glisser · Clic ○ pour ajouter · Clic droit pour supprimer',
        },
        ROOM_DETAIL: {
          FLOORING_DETECTION: 'Détection du Revêtement',
          DRYWALL_DETECTION: 'Détection des Cloisons Sèches',
          PAINT_DETECTION: 'Détection de Peinture',
          PLUMBING_DETECTION: 'Détection de Plomberie',
          HVAC_DETECTION: 'Détection CVC',
          COLUMN_DETECTION: 'Détection de Colonnes',
          BEAM_DETECTION: 'Détection de Poutres',
          DETECTION_DETAILS: 'Détails de Détection',
          COMPLETED: 'Terminé',
          PROCESSING: 'Traitement…',
          FAILED: 'Échec',
          SUMMARY: 'Résumé',
          ROOMS: 'Pièces',
          FIXTURES: 'Équipements',
          DUCTS: 'Conduits',
          COLUMNS: 'Colonnes',
          BEAMS: 'Poutres',
          FLOORS: 'Étages',
          TOTAL_LENGTH: 'Longueur Totale',
          TOTAL_FLOOR_AREA: 'Surface Totale au Sol',
          TOTAL_CEILING_AREA: 'Surface Totale du Plafond',
          TOTAL_FIXTURES: 'Total des Équipements',
          TOTAL_AREA: 'Surface Totale',
          TOTAL_PERIMETER: 'Périmètre Total',
          TOTAL_WALL_PERIMETER: 'Périmètre Total des Murs',
          TOTAL_WALL_LENGTH: 'Longueur Totale des Murs',
          FIXTURE_COUNT: 'Nombre d’Équipements',
          TOTAL_WALL_AREA: 'Surface Totale des Murs Intérieurs',
          TOTAL_CORNERS: 'Nombre Total de Coins',
          ADD_ROOM: 'Ajouter une Pièce',
          ADD_FIXTURE: 'Ajouter un Équipement',
          ADD_DUCT: 'Ajouter un Conduit',
          ADD_COLUMN: 'Ajouter une Colonne',
          ADD_BEAM: 'Ajouter une Poutre',
          DRAWING_NEW_FIXTURE: 'Nouvel Équipement en cours',
          NAME_YOUR_FIXTURE: 'Nommez votre Équipement',
          PIPE_READY: 'Tuyau Prêt — Enregistrer pour confirmer',
          ADD_NEW_PIPE: 'Ajouter un Tuyau',
          NAME_YOUR_ROOM: 'Nommez votre Pièce',
          DRAWING_NEW_ROOM: 'Nouvelle Pièce en cours',
          CANCEL_ESC: 'Annuler (Échap)',
          PIPE_THICKNESS: 'Épaisseur du Tuyau',
          THIN: 'Fin',
          MEDIUM: 'Moyen',
          THICK: 'Épais',
          WIDE: 'Large',
          PIPE_START: 'Cliquez sur le début du tuyau sur le plan.',
          PIPE_END: 'Cliquez sur la fin du tuyau pour terminer.',
          POLYGON: 'Polygone',
          RECT: 'Rect',
          ELLIPSE: 'Ellipse',
          DRAW_POLYGON_TITLE: 'Tracer un polygone — cliquer pour placer des points',
          DRAW_RECT_TITLE: 'Tracer un rectangle — glisser pour dessiner',
          DRAW_ELLIPSE_TITLE: 'Tracer une ellipse — glisser pour dessiner',
          POLYGON_HINT1: 'Cliquez sur le plan pour placer des sommets.',
          POLYGON_HINT2: 'Double-cliquez ou cliquez sur le premier point ◎ pour fermer.',
          VERTICES_PLACED: 'Sommets placés',
          DRAG_RECT: 'Faites glisser sur le plan pour dessiner un rectangle.',
          DRAG_ELLIPSE: 'Faites glisser sur le plan pour dessiner une ellipse.',
          ASSOCIATED_ROOM: 'Pièce Associée',
          NOT_DETECTED: 'Pas encore détecté',
          PIPE_NAME: 'Nom du Tuyau',
          FIXTURE_TYPE_LABEL: 'Type d\'Équipement',
          ROOM_NAME_LABEL: 'Nom de la Pièce',
          NO_FIXTURES: 'Aucun équipement configuré',
          SELECT_FIXTURE: 'Sélectionner un équipement',
          ROOM_NAME_PH: 'ex. Salon',
          OUTSIDE_ROOMS: 'Hors des pièces détectées',
          VERTICES: 'Sommets',
          CANCEL: 'Annuler',
          SAVE_DUCT: 'Enregistrer le Conduit',
          SAVE_FIXTURE: 'Enregistrer l\'Équipement',
          SAVE_ROOM: 'Enregistrer la Pièce',
          EDITING: 'Modification :',
          LIVE: 'En direct',
          SELECTED: 'Sélectionné : {{name}}',
          EDIT_POINTS_TITLE: 'Modifier les points du polygone',
          EDIT: 'Modifier',
          DELETE_FIXTURE: 'Supprimer cet équipement',
          DELETE_ROOM_BTN: 'Supprimer cette pièce',
          DELETE: 'Supprimer',
          DESELECT: 'Désélectionner',
          DRAG_FIXTURE_HINT: 'Faites glisser le polygone sur le plan pour mettre à jour cette pièce.',
          FIXTURE_TYPE: 'Type d\'Équipement',
          ASSOC_ROOM: 'Pièce Associée',
          UNASSIGNED: 'Non assigné',
          FLOOR_AREA: 'Surface au Sol',
          PERIMETER: 'Périmètre',
          FLOORING_TYPE: 'Type de Revêtement',
          CEILING_AREA: 'Surface du Plafond',
          WALL_PERIMETER: 'Périmètre des Murs',
          ROOM_HEIGHT: 'Hauteur de Pièce',
          CORNER_POINTS: 'Points d\'Angle',
          WALL_LENGTH: 'Longueur de Mur',
          SAVE_HEIGHT_TITLE: 'Enregistrer la hauteur de pièce',
          EDIT_HEIGHT_TITLE: 'Modifier la hauteur de pièce',
          SAVE: 'Enregistrer',
          CANCEL_HEIGHT: 'Annuler',
          LENGTH: 'Longueur',
          WIDTH: 'Largeur',
          TYPE: 'Type',
          BEAM_TYPE: 'Type de Poutre',
          THICKNESS: 'Épaisseur',
          AREA: 'Surface',
          CORNERS: 'Coins',
          COL_GROUP: 'Groupe de Colonnes',
          BEAM_GROUP: 'Groupe de Poutres',
          TOTAL_LEN: 'Longueur Totale',
          COLS_IN_GROUP: 'Colonnes dans le groupe',
          BEAMS_IN_GROUP: 'Poutres dans le groupe',
          NO_ROOM_SELECTED: 'Aucune pièce sélectionnée',
          NO_FIXTURE_SELECTED: 'Aucun équipement sélectionné',
          NO_COLUMN_SELECTED: 'Aucune colonne sélectionnée',
          NO_BEAM_SELECTED: 'Aucune poutre sélectionnée',
          CLICK_ROOM_HINT: 'Cliquez sur une pièce du plan pour voir les détails.',
          CLICK_FIXTURE_HINT: 'Cliquez sur un équipement du plan pour voir les détails.',
          CLICK_COLUMN_HINT: 'Clic gauche sur une colonne pour voir les détails.',
          CLICK_COLUMN_RIGHT: 'Clic droit pour sélectionner tout le groupe.',
          CLICK_BEAM_HINT: 'Clic gauche sur une poutre pour voir les détails.',
          CLICK_BEAM_RIGHT: 'Clic droit pour sélectionner tout le groupe.',
          INVALID_HEIGHT_TITLE: 'Hauteur invalide',
          INVALID_HEIGHT_MESSAGE: 'La hauteur de pièce doit être comprise entre 4 et 15 pi.',
        },
        SUMMARY: {
          FIXTURE_S: 'équipement détecté',
          FIXTURE_P: 'équipements détectés',
          AREA_S: 'zone détectée',
          AREA_P: 'zones détectées',
          NO_FIXTURES: 'Aucun équipement détecté pour cet étage.',
          NO_AREAS: 'Aucune zone détectée pour cet étage.',
          FIXTURE_TYPE: 'Type d\'Équipement',
          COUNT: 'Nombre',
          AREA_NAME: 'Nom de la Zone',
          AREA_SQ_FT: 'Surface (pi²)',
          PERIMETER_FT: 'Périmètre (pi)',
          CORNERS: 'Coins',
        },
      },
    },
    AUTH: {
      ADMIN_LOGIN: {
        CONSOLE: 'Console Admin',
        HERO_LINE_1: 'Pilotez le',
        HERO_LINE_2: 'Workspace.',
        HERO_DESCRIPTION: 'Une console pour gérer les builders, abonnements, projets, paramètres de portée et support.',
        FEATURE_BUILDERS: 'Builders',
        FEATURE_ACCOUNTS: 'Comptes',
        FEATURE_PLANS: 'Plans',
        FEATURE_SUBSCRIPTIONS: 'Abonnements',
        FEATURE_SCOPES: 'Portées',
        FEATURE_SETTINGS: 'Paramètres',
        BULLET_BUILDERS: 'Consultez et gérez tous les comptes builder',
        BULLET_SUBSCRIPTIONS: 'Gérez les abonnements et les dossiers de projets',
        BULLET_SETTINGS: 'Configurez les matériaux, prix et ressources de support',
        MONITORED: 'L’accès admin est surveillé et journalisé · TALOS Takeoffs',
        ADMIN: 'Admin',
        PORTAL: 'Portail Admin',
        TITLE: 'Connexion.',
        SUBTITLE: 'Personnel autorisé uniquement',
        EMAIL: 'Adresse e-mail',
        EMAIL_ERROR: 'Veuillez saisir une adresse e-mail valide.',
        PASSWORD: 'Mot de passe',
        PASSWORD_PLACEHOLDER: 'Saisir votre mot de passe',
        PASSWORD_ERROR: 'Le mot de passe est requis.',
        SIGN_IN_BUTTON: 'Se connecter au Portail Admin',
        SIGNING_IN: 'Connexion...',
        SECURE_NOTE: 'Chiffrement 256 bits · Accès TALOS sécurisé',
      },
      BUILDER: {
        COMMON: {
          TAKEOFFS: 'MÉTRÉS',
          EMAIL: 'Adresse e-mail',
          EMAIL_ERROR: 'Veuillez saisir une adresse e-mail valide.',
          PASSWORD: 'Mot de passe',
          PASSWORD_PLACEHOLDER: 'Saisir votre mot de passe',
          PASSWORD_REQUIRED: 'Le mot de passe est requis.',
          SECURE_ACCESS: 'Chiffrement 256 bits · Accès TALOS sécurisé',
          BUILT_AROUND: 'Conçu autour du flux de métré TALOS.',
          BACK_TO_LOGIN: 'Retour à la connexion',
          NEED_HELP: 'Besoin d’aide ?',
          CONTACT_SUPPORT: 'Contacter le support',
          OTP_PLACEHOLDER: 'Saisir le code à 6 chiffres',
        },
        LOGIN: {
          PANEL_EYEBROW: 'Workspace Builder',
          PANEL_TITLE_1: 'Des plans',
          PANEL_TITLE_2: 'aux rapports',
          PANEL_TITLE_3: 'de prix.',
          PANEL_DESC: 'Importez des PDF de plans, choisissez les portées métier, vérifiez les quantités détectées et exportez des rapports de prix depuis un seul workspace.',
          FEATURE_PLANS: 'Plans',
          FEATURE_UPLOADS: 'Imports',
          FEATURE_SCOPES: 'Portées',
          FEATURE_QUANTITIES: 'Quantités',
          FEATURE_REPORTS: 'Rapports',
          FEATURE_EXPORTS: 'Exports',
          BULLET_PROJECTS: 'Créez des projets et importez les feuilles de plans',
          BULLET_REVIEW: 'Vérifiez les pièces, portées et quantités détectées par IA',
          BULLET_PRICING: 'Ajustez les prix avant d’exporter les rapports',
          BADGE: 'Portail Builder',
          TITLE: 'Bon retour.',
          SUBTITLE: 'Connectez-vous à votre workspace projet',
          REMEMBER_ME: 'Se souvenir de moi',
          FORGOT_PASSWORD: 'Mot de passe oublié ?',
          SIGN_IN_BUTTON: 'Se connecter au compte',
          SIGNING_IN: 'Connexion...',
          NO_ACCOUNT: 'Vous n’avez pas de compte ?',
          SIGN_UP_FREE: 'Inscription gratuite',
          SIGN_IN_SHORT: 'Se connecter',
        },
        SIGNUP: {
          LOGO_TAG: 'BUILDER_SIGNUP',
          PANEL_EYEBROW: 'INITIALISER LE WORKSPACE',
          PANEL_TITLE_1: 'Des estimations plus rapides',
          PANEL_TITLE_2: 'commencent ici.',
          PANEL_DESC: 'Parlez-nous de votre métier et de votre flux. TALOS configure votre workspace d’estimation selon vos réponses.',
          FEATURE_PROFILE: 'Profil Builder',
          FEATURE_PROFILE_HINT: 'Métier, entreprise et coordonnées',
          FEATURE_WORKFLOW: 'Configuration du Flux',
          FEATURE_WORKFLOW_HINT: 'Types de projets et volume d’estimation',
          FEATURE_SECURE: 'Accès Sécurisé',
          FEATURE_SECURE_HINT: 'Identifiants chiffrés 256 bits',
          FOOT_LABEL: 'SYSTEM_READY - TALOS IA CONSTRUCTION',
          BADGE: 'Inscription Builder',
          TITLE: 'Créez votre workspace.',
          SUBTITLE: 'Complétez les sections ci-dessous pour activer votre compte builder TALOS.',
          SECTIONS: {
            ACCOUNT_TITLE: 'Détails du Compte',
            ACCOUNT_HINT: 'Nom, contact et informations entreprise',
            SECURITY_TITLE: 'Sécurité',
            SECURITY_HINT: 'Créez un mot de passe robuste pour votre compte',
            WORK_TITLE: 'Votre Travail',
            WORK_HINT: 'Parlez-nous de vos projets et volumes',
            CHALLENGES_TITLE: 'Vos Défis',
            CHALLENGES_HINT: 'Aidez-nous à optimiser votre flux',
          },
          FIELDS: {
            FULL_NAME: 'Nom Complet *',
            FULL_NAME_PLACEHOLDER: 'Saisir votre nom complet',
            WORK_EMAIL: 'E-mail Professionnel *',
            PHONE: 'Numéro de Téléphone *',
            COMPANY: 'Nom de l’Entreprise *',
            COMPANY_PLACEHOLDER: 'Saisir le nom de votre entreprise',
            ADDRESS: 'Adresse *',
            ADDRESS_PLACEHOLDER: 'Saisir votre adresse',
            TRADE: 'Métier / Spécialité *',
            TRADE_PLACEHOLDER: 'Sélectionner votre métier',
            PASSWORD: 'Mot de passe *',
            PASSWORD_PLACEHOLDER: 'Saisir un mot de passe',
            CONFIRM_PASSWORD: 'Confirmer le Mot de passe *',
            CONFIRM_PLACEHOLDER: 'Ressaisir votre mot de passe',
            WORK_TYPE: 'Quel type de travail faites-vous ?',
            PROJECT_TYPE: 'Sur quels types de projets travaillez-vous habituellement ? *',
            ESTIMATES: 'Combien de projets estimez-vous chaque mois ?',
            CHALLENGES: 'Qu’est-ce qui vous ralentit le plus ?',
            OTHER: 'Autre chose à partager ?',
            OTHER_PLACEHOLDER: 'Parlez-nous des autres défis rencontrés...',
            OPTIONAL: 'Facultatif',
          },
          ERRORS: {
            FULL_NAME: 'Le nom complet est requis.',
            PHONE: 'Veuillez saisir un numéro US valide à 10 chiffres.',
            COMPANY: 'Le nom de l’entreprise est requis.',
            ADDRESS: 'L’adresse est requise.',
            TRADE: 'Le métier est requis.',
            PASSWORD: 'Le mot de passe ne respecte pas tous les critères.',
            CONFIRM: 'Les mots de passe ne correspondent pas.',
            WORK_TYPE: 'Sélectionnez au moins un type de travail.',
            PROJECT_TYPE: 'Veuillez sélectionner un type de projet.',
            ESTIMATES: 'Veuillez sélectionner une plage d’estimation.',
            CHALLENGES: 'Sélectionnez au moins un défi.',
            GENERIC: 'Une erreur est survenue. Veuillez réessayer.',
          },
          PASSWORD_RULES: {
            MIN: 'Au moins 8 caractères',
            UPPER: 'Une lettre majuscule',
            LOWER: 'Une lettre minuscule',
            NUMBER: 'Un chiffre',
            SPECIAL: 'Un caractère spécial',
          },
          OPTIONS: {
            TRADES: {
              GENERAL_CONTRACTOR: 'Contractant général',
              SUBCONTRACTOR: 'Sous-traitant',
              ESTIMATOR: 'Estimateur',
              ARCHITECT: 'Architecte',
              ENGINEER: 'Ingénieur',
              DEVELOPER: 'Développeur',
              HOME_OWNER: 'Propriétaire',
            },
            WORK_TYPES: {
              FLOORING: 'Revêtement de sol',
              DRYWALL: 'Cloison sèche',
              FRAMING: 'Ossature',
              PAINTING: 'Peinture',
              ELECTRICAL: 'Électricité',
              PLUMBING: 'Plomberie',
              GENERAL_CONTRACTOR: 'Contractant général',
              OTHER: 'Autre',
            },
            PROJECT_TYPES: {
              RESIDENTIAL: 'Résidentiel',
              COMMERCIAL: 'Commercial',
              BOTH: 'Les deux',
            },
            CHALLENGES: {
              TAKEOFFS_LONG: 'Les métrés prennent trop de temps',
              MANUAL_WORK: 'Trop de travail manuel',
              MISSING_BIDS: 'Des offres manquées par manque de temps',
              ERRORS: 'Erreurs dans les estimations',
              SCALE: 'Difficile de faire évoluer l’estimation',
            },
          },
          CREATE_ACCOUNT: 'Créer mon compte',
          HAVE_ACCOUNT: 'Vous avez déjà un compte ?',
        },
        FORGOT: {
          PANEL_EYEBROW: 'Récupération de Compte',
          PANEL_TITLE_1: 'Vérifiez votre',
          PANEL_TITLE_2: 'e-mail avant la',
          PANEL_TITLE_3: 'réinitialisation.',
          PANEL_DESC: 'Nous envoyons un code à usage unique à votre e-mail enregistré pour lier la demande à votre compte.',
          BULLET_EMAIL: 'Demandez un code avec votre e-mail professionnel',
          BULLET_OTP: 'Utilisez l’OTP reçu pour continuer',
          BULLET_RESET: 'Réinitialisez l’accès sans exposer votre ancien mot de passe',
          PANEL_FOOTER: 'Récupération sécurisée du compte TALOS.',
          BADGE: 'Récupération',
          TITLE: 'Mot de passe oublié ?',
          SUBTITLE: 'Saisissez votre e-mail et nous enverrons un code à usage unique.',
          EMAIL_PLACEHOLDER: 'Saisir votre e-mail professionnel',
          SEND_OTP: 'Envoyer l’OTP',
          SENDING: 'Envoi...',
          CHECK_INBOX: 'Consultez votre boîte.',
          SENT_PREFIX: 'Nous avons envoyé un OTP à 6 chiffres à',
          ENTER_OTP: 'Saisir l’OTP et Réinitialiser',
          ERROR_SEND: 'Impossible d’envoyer l’OTP. Veuillez réessayer.',
        },
        RESET: {
          PANEL_EYEBROW: 'Réinitialisation des Identifiants',
          PANEL_TITLE_1: 'Confirmez l’OTP,',
          PANEL_TITLE_2: 'définissez un',
          PANEL_TITLE_3: 'mot de passe sûr.',
          PANEL_DESC: 'Le flux vérifie votre code, contrôle le nouveau mot de passe et met à jour l’accès en une étape sécurisée.',
          BULLET_VALIDATE: 'Validez l’OTP envoyé à votre e-mail',
          BULLET_STRONGER: 'Créez un mot de passe plus robuste avant l’envoi',
          BULLET_RETURN: 'Revenez à la connexion après la mise à jour',
          PANEL_FOOTER: 'Mise à jour sécurisée des identifiants TALOS.',
          BADGE: 'Réinitialisation du Mot de Passe',
          TITLE: 'Réinitialiser le mot de passe.',
          SUBTITLE: 'Saisissez l’OTP envoyé à votre e-mail et choisissez un nouveau mot de passe.',
          OTP_SENT_TO: 'OTP envoyé à',
          OTP_LABEL: 'Mot de passe à usage unique (OTP) *',
          OTP_ERROR: 'Veuillez saisir l’OTP à 6 chiffres reçu par e-mail.',
          NEW_PASSWORD: 'Nouveau Mot de Passe *',
          NEW_PASSWORD_PLACEHOLDER: 'Saisir le nouveau mot de passe',
          PASSWORD_ERROR: 'Le mot de passe doit respecter tous les critères.',
          REQUIREMENTS: 'Critères du mot de passe',
          RULE_UPPER: 'Au moins 1 lettre majuscule',
          RULE_NUMBER: 'Au moins 1 chiffre',
          RULE_SPECIAL: 'Au moins 1 caractère spécial',
          CONFIRM_PASSWORD: 'Confirmer le Nouveau Mot de Passe *',
          CONFIRM_PLACEHOLDER: 'Répéter le nouveau mot de passe',
          RESET_BUTTON: 'Réinitialiser le Mot de Passe',
          RESETTING: 'Réinitialisation...',
          ERROR_INVALID: 'OTP invalide ou expiré. Veuillez réessayer.',
        },
        VERIFY: {
          TITLE: 'Vérifiez votre e-mail',
          SUBTITLE: 'Nous avons envoyé un code à 6 chiffres à votre adresse e-mail. Saisissez-le ci-dessous pour confirmer votre compte.',
          CODE_LABEL: 'Code de Vérification *',
          CODE_ERROR: 'Veuillez saisir le code à 6 chiffres.',
          VERIFY_BUTTON: 'Vérifier l’E-mail',
          SENDING: 'Envoi...',
          RESEND_IN: 'Renvoyer le code dans {{seconds}}s',
          RESEND: 'Code non reçu ? Renvoyer',
          SECURE_NOTE: 'Vos données sont protégées par un chiffrement 256 bits',
          SUCCESS_TITLE: 'E-mail vérifié !',
          SUCCESS_MESSAGE: 'Votre compte a été confirmé. Vous pouvez maintenant vous connecter.',
          GO_LOGIN: 'Aller à la Connexion',
          TOAST_RESENT_TITLE: 'Code renvoyé',
          TOAST_RESENT_MESSAGE: 'Un nouveau code de vérification a été envoyé à votre e-mail.',
          TOAST_FAILED_TITLE: 'Échec du renvoi',
          TOAST_FAILED_MESSAGE: 'Impossible de renvoyer le code. Veuillez réessayer.',
          ERROR_INVALID: 'Code invalide ou expiré. Veuillez réessayer.',
        },
      },
    },
    LANDING: {
      LOGO_SUB: 'ia construction',
      HERO: {
        EYEBROW: 'Plateforme de métré IA',
        HEADLINE_LINE_1: 'Plans entrants.',
        HEADLINE_LINE_2: 'Rapports sortants.',
        HEADLINE_MUTED: 'Aucune mise à l’échelle manuelle.',
        DESCRIPTION: 'Importez des PDF de plans, choisissez les portées métier, vérifiez les quantités détectées par IA et exportez des rapports de prix structurés - le tout dans un seul poste de travail.',
        FEATURES: {
          PLANS_TITLE: 'Plans',
          PLANS_SUB: 'Importés',
          SCOPES_TITLE: 'Portées',
          SCOPES_SUB: 'Détectées',
          REPORTS_TITLE: 'Rapports',
          REPORTS_SUB: 'Exportés',
        },
        BULLETS: {
          DETECTS: 'L’IA détecte les pièces, portées et quantités depuis les PDF de plans',
          RATES: 'Appliquez les tarifs matériaux et main-d’œuvre sur 5 catégories métier',
          EXPORT: 'Exportez des rapports de prix détaillés pour chaque projet',
        },
        CAPTION: 'Conçu autour du flux de métré TALOS.',
      },
      CARD: {
        LOGO_SUB: 'MÉTRÉS + PRIX',
        BADGE: 'Plateforme Construction IA',
        TITLE_LINE_1: 'Le poste de',
        TITLE_LINE_2: 'métré IA.',
        SUBTITLE: 'De l’import du plan au rapport chiffré et exportable - en minutes, pas en heures.',
        START_FIRST: 'Démarrer votre premier métré ->',
        SIGN_IN: 'Connexion au workspace',
        SECURE_ACCESS: 'Chiffrement 256 bits · Accès TALOS sécurisé',
        STATS: {
          ANALYSIS_TIME: 'TEMPS D’ANALYSE',
          TRADE_CATEGORIES: 'CATÉGORIES MÉTIER',
          DETECTION_ACCURACY: 'PRÉCISION DE DÉTECTION',
          FULL_WORKFLOW: 'FLUX COMPLET',
        },
      },
      HOW: {
        EYEBROW: 'Fonctionnement',
        TITLE_LINE_1: 'Quatre étapes. PDF vers',
        TITLE_MUTED: 'rapport chiffré.',
        DESCRIPTION: 'Un flux IA structuré qui remplace entièrement le processus manuel d’estimation - du PDF brut à une sortie de prix structurée.',
        STEPS: {
          UPLOAD: {
            TITLE: 'Importez vos PDF de plans',
            DESC: 'Créez un projet et joignez vos feuilles de plan. TALOS accepte les PDF multipages et traite chaque feuille indépendamment.',
          },
          DETECT: {
            TITLE: 'L’IA détecte les pièces et les quantités',
            DESC: 'Le moteur TALOS analyse chaque feuille, identifie les limites des pièces et calcule les quantités de portée dans les catégories métier sélectionnées.',
          },
          REVIEW: {
            TITLE: 'Vérifiez et appliquez les prix',
            DESC: 'Inspectez les quantités détectées pièce par pièce. Appliquez les tarifs matériaux et main-d’œuvre à chaque portée depuis le panneau de configuration des prix.',
          },
          EXPORT: {
            TITLE: 'Exportez des rapports de prix structurés',
            DESC: 'Générez un PDF formaté avec les totaux détaillés par portée, les ventilations de main-d’œuvre et un résumé de projet - prêt pour le client.',
          },
        },
      },
      MODULES: {
        EYEBROW: 'Modules de Plateforme',
        TITLE_LINE_1: 'Deux modules.',
        TITLE_MUTED: 'Une plateforme.',
        DESCRIPTION: 'Les métrés et la tarification restent ensemble. Aucun changement de contexte entre outils.',
        TAKEOFF: {
          MOCK_TITLE: 'Revue de Métré IA',
          DESCRIPTION: 'Estimation de matériaux propulsée par IA pour les divisions de construction. Importez les plans, détectez les portées métier, vérifiez les quantités et verrouillez l’estimation - en minutes.',
          CTA: 'Accéder au poste de métré ->',
        },
        PRICING: {
          MOCK_TITLE: 'Rapport de Prix',
          EXPORT_READY: 'PRÊT À EXPORTER',
          DESCRIPTION: 'Convertissez les quantités détectées par IA en rapports de prix configurables. Appliquez les tarifs par portée, vérifiez les totaux et exportez des PDF structurés pour livraison client.',
          CTA: 'Accéder au module de prix ->',
        },
      },
      CTA: {
        EYEBROW: 'Commencer',
        TITLE_LINE_1: 'Conçu pour les estimateurs',
        TITLE_LINE_2: 'qui valorisent la',
        TITLE_MUTED: 'précision.',
        DESCRIPTION: 'Aucune mise à l’échelle manuelle. Aucun chaos de tableur. Infrastructure complète de métré et tarification IA - disponible maintenant.',
        CREATE_ACCOUNT: 'Créer votre compte ->',
      },
      FOOTER: {
        BRAND_SUB: 'IA CONSTRUCTION',
        COPY: '© 2026 TALOS - Tous droits réservés',
        SECURITY: 'Sécurité',
        PRIVACY: 'Confidentialité',
        TERMS: 'Conditions',
      },
    },
    FOOTER: {
      BRAND_DESC: "Métrés de plans et rapports de prix propulsés par l'IA pour les équipes d'estimation en construction.",
      PRODUCT: {
        TITLE: 'Produit',
        TAKEOFFS: 'Métrés',
        PRICING: 'Tarification',
        PROCESS: 'Processus',
        SCOPE_SETTINGS: 'Paramètres de Portée',
      },
      COMPANY: {
        TITLE: 'Entreprise',
        MISSION: 'Mission',
        WORKFLOWS: 'Flux de Travail',
        BUILDERS: 'Constructeurs',
        SUPPORT: 'Assistance',
      },
      ACCOUNT: {
        TITLE: 'Compte',
        LOGIN: 'Connexion',
        START_TAKEOFF: 'Démarrer Métré',
        DOCUMENTATION: 'Documentation',
        PRIVACY: 'Confidentialité',
      },
      COPYRIGHT: '© 2026 TALOS / AI-CONSTRUCTION — Tous droits réservés',
      LEGAL: {
        SECURITY: 'Sécurité',
        PRIVACY: 'Confidentialité',
        TERMS: 'Conditions',
      },
    },
    BUILDER: {
      DASHBOARD: {
        HERO: {
          KICKER: 'CENTRE DES OPÉRATIONS',
          WELCOME: 'Bon retour,',
          LINE_1: 'Importez vos plans et laissez TALOS',
          LINE_2: "exécuter votre analyse alimentée par l'IA.",
          START: 'Démarrer un Nouveau Projet',
        },
        STATS: {
          TOTAL_KICKER: 'TOTAL',
          PROJECTS: 'Projets',
          DONE_KICKER: 'TERMINÉS',
          COMPLETED: 'Terminés',
          ACTIVE_KICKER: 'ACTIF',
          IN_PROGRESS: 'En Cours',
        },
        TABLE: {
          KICKER: 'FILE RÉCENTE',
          HEADING: 'Mes Projets',
          SUBTITLE: "Vos derniers lots d'analyse",
          NEW_PROJECT: 'Nouveau Projet',
          COL_PROJECT: 'Projet',
          COL_STATUS: 'Statut',
          COL_PLANS: 'Plans',
          COL_UPDATED: 'Mis à jour',
          UPLOADED: 'importés',
        },
        STATUS: {
          COMPLETED: 'Terminé',
          IN_PROGRESS: 'En Cours',
          ACTIVE: 'Actif',
        },
      },
      PROFILE: {
        EDIT_BTN: 'Modifier le Profil',
        MEMBER_SINCE: 'Membre depuis',
        CONTACT: {
          TITLE: 'Informations de Contact',
          EMAIL: 'E-mail',
          PHONE: 'Téléphone',
          COMPANY: 'Entreprise',
          ADDRESS: 'Adresse',
        },
        WORK: {
          TITLE: 'Détails Professionnels',
          WORK_TYPE: 'Type de Travail',
          PROJECT_TYPE: 'Type de Projet',
          PPM: 'Projets / Mois',
          CHALLENGES: 'Défis',
          OTHER_CHALLENGES: 'Autres Défis',
        },
        SECURITY: {
          TITLE: 'Sécurité',
          PASSWORD: 'Mot de passe',
          LAST_UPDATED: 'Dernière mise à jour : inconnue',
          CHANGE_BTN: 'Changer le Mot de Passe',
        },
        DANGER: {
          TITLE: 'Zone Dangereuse',
          DESC: 'Supprimer définitivement votre compte et toutes les données associées. Cette action est irréversible.',
          DELETE_BTN: 'Supprimer le Compte',
        },
        EDIT: {
          TITLE: 'Modifier le Profil',
          SUBTITLE: 'Les modifications sont enregistrées immédiatement après avoir cliqué sur Enregistrer.',
          CANCEL: 'Annuler',
          SAVE: 'Enregistrer les Modifications',
          BASIC_INFO: 'Informations de Base',
          FULL_NAME: 'Nom Complet *',
          FULL_NAME_PH: 'Jean Dupont',
          FULL_NAME_ERR: 'Le nom complet est obligatoire.',
          PHONE: 'Numéro de Téléphone *',
          PHONE_PH: 'Téléphone à 10 chiffres',
          PHONE_ERR: 'Entrez un numéro de téléphone valide à 10 chiffres.',
          COMPANY: "Nom de l'Entreprise *",
          COMPANY_PH: 'Acme Construction',
          COMPANY_ERR: "Le nom de l'entreprise est obligatoire.",
          ADDRESS: 'Adresse *',
          ADDRESS_PH: '123 Rue Principale',
          ADDRESS_ERR: "L'adresse est obligatoire.",
          TRADE: 'Métier *',
          TRADE_PH: 'Sélectionnez votre métier',
          TRADE_ERR: 'Le métier est obligatoire.',
          PROJ_TYPE: 'Type de Projet *',
          PROJ_TYPE_PH: 'Sélectionnez le type de projet',
          PROJ_TYPE_ERR: 'Le type de projet est obligatoire.',
          PPM: 'Projets / Mois *',
          PPM_PH: 'Sélectionnez une plage',
          PPM_ERR: 'Ce champ est obligatoire.',
          OTHER: 'Autres Défis',
          OTHER_PH: 'Autre chose que vous souhaitez améliorer...',
        },
        DELETE_MODAL: {
          TITLE: 'Supprimer votre compte ?',
          DESC: 'Cela supprimera définitivement votre compte, vos projets et toutes les données.',
          CANNOT_UNDO: 'Cette action est irréversible.',
          CONFIRM: 'Oui, supprimer définitivement',
          CANCEL: 'Annuler',
        },
        CP_MODAL: {
          TITLE: 'Changer le Mot de Passe',
          STEP: 'Étape {{step}} sur 2',
          STEP1_DESC: 'Nous enverrons un OTP à 6 chiffres à votre adresse e-mail enregistrée :',
          SEND_OTP: "Envoyer l'OTP",
          OTP_SENT: 'OTP envoyé ! Vérifiez votre e-mail et entrez-le ci-dessous.',
          OTP_CODE: 'Code OTP *',
          OTP_PH: 'Entrez le code à 6 chiffres',
          OTP_ERR: "Veuillez entrer l'OTP à 6 chiffres.",
          NEW_PWD: 'Nouveau Mot de Passe *',
          NEW_PWD_PH: 'Au moins 8 caractères',
          NEW_PWD_ERR: 'Le mot de passe doit contenir au moins 8 caractères.',
          CONFIRM_PWD: 'Confirmer le Mot de Passe *',
          CONFIRM_PH: 'Répétez le nouveau mot de passe',
          CONFIRM_ERR: 'Les mots de passe ne correspondent pas.',
          UPDATE: 'Mettre à Jour le Mot de Passe',
          SENDING: 'Envoi en cours...',
          RESEND_WAIT: "Renvoyer l'OTP dans {{seconds}}s",
          RESEND: "Renvoyer l'OTP",
        },
        TOAST: {
          PROFILE_UPDATED: 'Profil mis à jour',
          PROFILE_UPDATED_MSG: 'Vos modifications ont été enregistrées.',
          UPDATE_FAILED: 'Mise à jour échouée',
          UPDATE_FAILED_MSG: 'Impossible de mettre à jour le profil. Veuillez réessayer.',
          PWD_CHANGED: 'Mot de passe changé',
          PWD_CHANGED_MSG: 'Votre mot de passe a été mis à jour avec succès.',
          OTP_RESENT: 'OTP renvoyé',
          OTP_RESENT_MSG: 'Un nouveau code a été envoyé à votre e-mail.',
        },
      },
    },
  },
};
